import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FeatureFlag } from "../types/chat";
import { WhatsAppEntity } from "../types/entity";
import { orchestratorApi, authFetch, API_BASE } from '../api/orchestrator'
import { useTenantId } from "../auth/useTenantId";
import { AddEntityModal } from "./AddEntityModal";
import RAGManagement from "./RAGManagement";
import TokenUsage from "./TokenUsage";
import { AgentLLMConfig } from "./AgentLLMConfig";
import { SystemPrompt } from "./SystemPrompt";
import { MonitoringDashboard } from "./MonitoringDashboard";
import ModelSettings from "./ModelSettings";
import { SystemSettings } from "./SystemSettings";
import { GoogleCalendarAuth } from "./GoogleCalendarAuth";
import { CRMSettings } from "./CRMSettings";
import { ZohoResponseControl } from "./ZohoResponseControl";
import MemoryManagement from "./MemoryManagement";
import ToolConfiguration from "./ToolConfiguration";
import HumanHandoffDashboard from "./HumanHandoffDashboard";
import ManagementMessagesDashboard from "./ManagementMessagesDashboard";
import CuratedQA from "./CuratedQA";
import Testing from "./Testing";
import { AgentFactory } from "./AgentFactory";
import { EntityAssignments } from "./EntityAssignments";
import ModelPreferences from "./ModelPreferences";
import { DeadLetterQueue } from "./DeadLetterQueue";
import { EntityManagement } from "./EntityManagement";
import UnansweredArchive from "./UnansweredArchive";
import jsQR from "jsqr";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface AgentSettings {
  id: number;
  agent_name: string;
  send_client_messages: boolean;
  notification_chats: string;
  typeform_message_template?: string;
  webform_ai_prompt?: string;
  primary_ai_provider?: string;
  primary_ai_model?: string;
  backup_ai_provider?: string;
  backup_ai_model?: string;
  calendar_check_time?: string;
  calendar_timezone?: string;
  calendar_days_ahead?: number;
  calendar_last_check?: string;
  calendar_next_check?: string;
  unanswered_check_times?: string;
  unanswered_timezone?: string;
  unanswered_max_chats?: number;
  unanswered_minutes_threshold?: number;
  unanswered_last_check?: string;
  unanswered_next_check?: string;
  qualifier_message_template?: string;
  qualifier_ai_prompt?: string;
  created_at: string;
  updated_at: string;
}

interface CalendarSchedule {
  checkTime: string;
  timezone: string;
  daysAhead: number;
  lastCheck?: string;
  nextCheck?: string;
}

interface ScrapedUrl {
  id: number;
  url: string;
  title?: string;
  description?: string;
  last_successful_scrape?: string;
  last_attempt?: string;
  scrape_count: number;
  failure_count: number;
  status: string;
  content_fingerprint?: string;
  priority: number;
  category: string;
  source: string;
  created_at: string;
  updated_at: string;
}

interface ScrapingStats {
  totalUrls: number;
  activeUrls: number;
  failedUrls: number;
  lastRunTime?: string;
  changesDetected: number;
}

interface UnansweredMessagesSchedule {
  checkTimes: string[];
  timezone: string;
  maxChats: number;
  minutesThreshold: number;
  llmTimeoutSeconds: number;
  lastCheck?: string;
  nextCheck?: string;
  enabled?: boolean;
  notificationChats?: string;
}

// Legacy interface - replaced by user-friendly schedule settings
/*
interface ScrapingSchedule {
  scheduleName: string;
  timerName: string;
  enabled: boolean;
  lastRunAt?: string;
  nextRunAt?: string;
  notificationEmail: string;
  includeDiffInEmail: boolean;
  readableSchedule?: string;
  timerStatus?: string;
  timerUnit?: string;
}
*/

interface QualifierSchedule {
  [dayOfWeek: number]: {
    [hourOfDay: number]: boolean;
  };
}

// Agent Setting Card Component
const AgentSettingCard: React.FC<{
  setting: AgentSettings;
  updating: boolean;
  onUpdate: (agentName: string, updates: Partial<AgentSettings>) => void;
}> = ({ setting, updating, onUpdate }) => {
  const [localSendClient, setLocalSendClient] = useState(setting.send_client_messages);
  const [localNotificationChats, setLocalNotificationChats] = useState(setting.notification_chats);
  const [localTypeformTemplate, setLocalTypeformTemplate] = useState(setting.typeform_message_template || '');
  const [localWebformPrompt, setLocalWebformPrompt] = useState(setting.webform_ai_prompt || '');
  const [localQualifierTemplate, setLocalQualifierTemplate] = useState(setting.qualifier_message_template || '');
  const [localQualifierPrompt, setLocalQualifierPrompt] = useState(setting.qualifier_ai_prompt || '');

  // Update local state when setting changes
  React.useEffect(() => {
    setLocalSendClient(setting.send_client_messages);
    setLocalNotificationChats(setting.notification_chats);
    setLocalTypeformTemplate(setting.typeform_message_template || '');
    setLocalWebformPrompt(setting.webform_ai_prompt || '');
    setLocalQualifierTemplate(setting.qualifier_message_template || '');
    setLocalQualifierPrompt(setting.qualifier_ai_prompt || '');
  }, [setting]);

  const handleSave = () => {
    const updates: Partial<AgentSettings> = {
      send_client_messages: localSendClient,
      notification_chats: localNotificationChats,
    };

    if (setting.agent_name === 'typeform_email_agent') {
      updates.typeform_message_template = localTypeformTemplate;
    } else if (setting.agent_name === 'webform_email_agent') {
      updates.webform_ai_prompt = localWebformPrompt;
    } else if (setting.agent_name === 'qualifier_agent') {
      updates.qualifier_message_template = localQualifierTemplate;
      updates.qualifier_ai_prompt = localQualifierPrompt;
    }

    onUpdate(setting.agent_name, updates);
  };

  const hasChanges =
    localSendClient !== setting.send_client_messages ||
    localNotificationChats !== setting.notification_chats ||
    (setting.agent_name === 'typeform_email_agent' && localTypeformTemplate !== (setting.typeform_message_template || '')) ||
    (setting.agent_name === 'webform_email_agent' && localWebformPrompt !== (setting.webform_ai_prompt || '')) ||
    (setting.agent_name === 'qualifier_agent' && (
      localQualifierTemplate !== (setting.qualifier_message_template || '') ||
      localQualifierPrompt !== (setting.qualifier_ai_prompt || '')
    ));

  const getAgentDisplayName = (agentName: string) => {
    switch (agentName) {
      case 'typeform_email_agent':
        return 'Typeform Email Agent';
      case 'webform_email_agent':
        return 'Webform Email Agent';
      case 'qualifier_agent':
        return 'Lead Qualifier Agent';
      default:
        return agentName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const renderAiModelInfo = (setting: AgentSettings) => {
    const primaryModel = setting.primary_ai_model || 'gemini-2.5-flash-lite-preview-09-2025';
    const primaryProvider = setting.primary_ai_provider || 'gemini';
    const backupModel = setting.backup_ai_model || 'z-ai/glm-4.5';
    const backupProvider = setting.backup_ai_provider || 'openrouter';

    return (
      <div className="ai-model-info" style={{ margin: '12px 0', padding: '12px', background: '#f8f9fa', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
        <h4 style={{ margin: '0 0 8px 0', color: '#2d3748', fontSize: '14px', fontWeight: 'bold' }}>🤖 AI Model Configuration</h4>
        <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
          <strong>Primary:</strong> {primaryModel} via {primaryProvider.charAt(0).toUpperCase() + primaryProvider.slice(1)}<br />
          <strong>Backup:</strong> {backupModel} via {backupProvider.charAt(0).toUpperCase() + backupProvider.slice(1)}<br />
          <small style={{ color: '#718096' }}>Uses primary model first, falls back to backup if unavailable</small>
        </div>
      </div>
    );
  };

  return (
    <div className="agent-setting-card">
      <div className="setting-header">
        <h3 className="agent-name">{getAgentDisplayName(setting.agent_name)}</h3>
        <div className="setting-status">
          <span className={`status-badge ${localSendClient ? "enabled" : "disabled"}`}>
            Client Messages: {localSendClient ? "Enabled" : "Disabled"}
          </span>
        </div>
      </div>

      <div className="setting-form">
        <div className="setting-field">
          <label className="setting-label">
            <input
              type="checkbox"
              checked={localSendClient}
              onChange={(e) => setLocalSendClient(e.target.checked)}
              disabled={updating}
            />
            📱 Send WhatsApp messages to prospective clients
          </label>
          <small className="field-help">
            <strong>What this controls:</strong> When enabled, automatically sends personalized welcome messages with business cards to phone numbers extracted from form submissions (Typeform/Webform). This is the primary client outreach feature.
          </small>
        </div>

        <div className="setting-field">
          <label className="setting-label">🔔 Internal Notification Recipients:</label>
          <textarea
            value={localNotificationChats}
            onChange={(e) => setLocalNotificationChats(e.target.value)}
            disabled={updating}
            placeholder="E&H Immigration Internal, 6596734436, Other Group Name"
            className="notification-chats-input"
            rows={3}
          />
          <small className="field-help">
            <strong>📋 Always notified:</strong> These chats receive notifications for ALL form submissions regardless of the client messaging toggle above.<br />
            <strong>💡 Format:</strong> Enter multiple group names and/or phone numbers separated by commas.<br />
            <strong>📝 Examples:</strong> "E&H Immigration Internal" (group), "6596734436" (phone), "Sales Team, 65123456789"<br />
            <strong>🏷️ Display:</strong> Group names shown as-is, phone numbers with contact names when available.
          </small>
        </div>

        {/* Typeform Message Template */}
        {setting.agent_name === 'typeform_email_agent' && (
          <div className="setting-field">
            <label className="setting-label">💬 Typeform Client Message Template:</label>
            <textarea
              value={localTypeformTemplate}
              onChange={(e) => setLocalTypeformTemplate(e.target.value)}
              disabled={updating}
              placeholder="Hi {firstName}, Lynn here from E&H Immigration..."
              className="message-template-input"
              rows={8}
            />
            <small className="field-help">
              <strong>📝 What this is:</strong> The message template sent to clients when they submit a Typeform.<br />
              <strong>🏷️ Available variables:</strong> <code>{`{firstName}`}</code>, <code>{`{applicationType}`}</code><br />
              <strong>📋 Note:</strong> This message is sent along with Lynn's business card when client messaging is enabled.
            </small>
          </div>
        )}

        {/* Webform AI Prompt Template */}
        {setting.agent_name === 'webform_email_agent' && (
          <div className="setting-field">
            <label className="setting-label">🤖 Webform AI Prompt Template:</label>
            <textarea
              value={localWebformPrompt}
              onChange={(e) => setLocalWebformPrompt(e.target.value)}
              disabled={updating}
              placeholder="You are Lynn from E&H Immigration consultancy..."
              className="message-template-input"
              rows={12}
            />
            <small className="field-help">
              <strong>🧠 What this is:</strong> The AI prompt template used to generate personalized intro messages for Squarespace form submissions.<br />
              <strong>🏷️ Available variables:</strong> <code>{`{clientName}`}</code>, <code>{`{emailBody}`}</code><br />
              <strong>📋 Note:</strong> AI generates custom messages based on the actual form submission content.
            </small>
          </div>
        )}

        {/* Qualifier Agent Fields */}
        {setting.agent_name === 'qualifier_agent' && (
          <>
            <div className="setting-field">
              <label className="setting-label">💬 Qualifier Follow-up Message Template:</label>
              <textarea
                value={localQualifierTemplate}
                onChange={(e) => setLocalQualifierTemplate(e.target.value)}
                disabled={updating}
                placeholder="Hi {firstName}, I see from our previous conversation that you're interested in {immigrationTopic}..."
                className="message-template-input"
                rows={6}
              />
              <small className="field-help">
                <strong>📝 What this is:</strong> Message template sent to qualified leads as follow-up.<br />
                <strong>🏷️ Available variables:</strong> <code>{`{firstName}`}</code>, <code>{`{immigrationTopic}`}</code><br />
                <strong>📋 Note:</strong> Only sent when client messaging is enabled and enough time has passed since last contact.
              </small>
            </div>

            <div className="setting-field">
              <label className="setting-label">🤖 Qualifier AI Analysis Prompt:</label>
              <textarea
                value={localQualifierPrompt}
                onChange={(e) => setLocalQualifierPrompt(e.target.value)}
                disabled={updating}
                placeholder="Analyze this WhatsApp conversation and determine if it contains a qualified lead..."
                className="message-template-input"
                rows={12}
              />
              <small className="field-help">
                <strong>🧠 What this is:</strong> AI prompt used to analyze WhatsApp conversations and determine if they contain qualified leads.<br />
                <strong>🏷️ Available variables:</strong> <code>{`{conversationText}`}</code><br />
                <strong>📋 Note:</strong> AI analyzes conversation patterns to identify genuine immigration interest and intent.
              </small>
            </div>

            <div className="setting-field">
            </div>
          </>
        )}

        {/* AI Model Configuration Display */}
        {renderAiModelInfo(setting)}

        <div className="setting-actions">
          <button
            onClick={handleSave}
            disabled={updating || !hasChanges}
            className={`save-button ${hasChanges ? 'has-changes' : ''}`}
          >
            {updating ? "Saving..." : hasChanges ? "Save Changes" : "No Changes"}
          </button>
        </div>
      </div>

      <div className="setting-info">
        <small className="last-updated">
          Last updated: {new Date(setting.updated_at).toLocaleString()}
        </small>
      </div>
    </div>
  );
};

// CSS-in-JS styles for QR code comparison
const qrComparisonStyles = `
  .qr-decoding-status {
    background: #f0f8ff;
    border: 1px solid #b0d4f1;
    border-radius: 6px;
    padding: 8px 12px;
    margin: 10px 0;
    color: #2c5282;
  }

  .qr-content-comparison {
    background: #f8f9fa;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 15px;
    margin: 15px 0;
    font-size: 14px;
  }

  .qr-content-comparison h4 {
    margin: 0 0 12px 0;
    color: #2d3748;
    font-size: 16px;
  }

  .qr-current-content,
  .qr-previous-content {
    margin: 8px 0;
  }

  .qr-current-content strong,
  .qr-previous-content strong {
    color: #4a5568;
    display: block;
    margin-bottom: 4px;
  }

  .qr-code-text {
    background: #ffffff;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 8px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    word-break: break-all;
    display: block;
    color: #1a202c;
    max-height: 100px;
    overflow-y: auto;
  }

  .qr-comparison-result {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e2e8f0;
  }

  .qr-comparison-result strong {
    color: #4a5568;
  }

  .qr-status {
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: bold;
    margin-left: 8px;
  }

  .qr-status.changed {
    background: #c6f6d5;
    color: #22543d;
  }

  .qr-status.unchanged {
    background: #fed7d7;
    color: #742a2a;
  }

  .qr-refresh-hint {
    background: #fffbeb;
    border: 1px solid #f6e05e;
    border-radius: 4px;
    padding: 8px 12px;
    margin-top: 8px;
    color: #744210;
  }

  .qr-validation-results {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 15px;
    margin: 15px 0;
  }

  .qr-validation-results h4 {
    margin: 0 0 12px 0;
    color: #2d3748;
    font-size: 16px;
  }

  .validation-test-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #e2e8f0;
  }

  .validation-test-item:last-child {
    border-bottom: none;
  }

  .test-name {
    font-weight: 500;
    color: #4a5568;
  }

  .test-result {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .test-status {
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: bold;
    font-size: 12px;
  }

  .test-status.pass {
    background: #c6f6d5;
    color: #22543d;
  }

  .test-status.fail {
    background: #fed7d7;
    color: #742a2a;
  }

  .test-status.warning {
    background: #fefcbf;
    color: #744210;
  }

  .freshness-indicator {
    padding: 4px 8px;
    border-radius: 4px;
    font-weight: bold;
    font-size: 12px;
    margin-left: 8px;
  }

  .freshness-indicator.fresh {
    background: #c6f6d5;
    color: #22543d;
  }

  .freshness-indicator.aging {
    background: #fbd38d;
    color: #744210;
  }

  .freshness-indicator.stale {
    background: #fed7d7;
    color: #742a2a;
  }

  .freshness-indicator.expired {
    background: #e53e3e;
    color: white;
  }

  .validation-summary {
    background: #ffffff;
    border: 2px solid;
    border-radius: 8px;
    padding: 12px;
    margin-top: 12px;
    font-weight: bold;
  }

  .validation-summary.pass {
    border-color: #38a169;
    background: #f0fff4;
    color: #22543d;
  }

  .validation-summary.fail {
    border-color: #e53e3e;
    background: #fffafa;
    color: #742a2a;
  }

  .qr-comparison-status {
    background: #f7fafc;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px;
    margin: 12px 0;
  }

  .qr-comparison-header {
    margin: 0 0 8px 0;
    color: #2d3748;
    font-size: 14px;
  }

  .qr-comparison-badge {
    padding: 10px 12px;
    border-radius: 6px;
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 8px;
    border: 2px solid;
  }

  .qr-comparison-badge.changed {
    background: #c6f6d5;
    color: #22543d;
    border-color: #38a169;
  }

  .qr-comparison-badge.unchanged {
    background: #fed7d7;
    color: #742a2a;
    border-color: #e53e3e;
  }

  .qr-content-preview {
    background: #ffffff;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 8px;
    font-family: 'Courier New', monospace;
    color: #4a5568;
    line-height: 1.4;
  }
`;

// CSS-in-JS styles for agent settings
const agentSettingsStyles = `
  /* Agent Settings Styles */
  .agent-settings-section {
    margin: 30px 0;
  }

  .agent-settings-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 20px;
    margin-top: 20px;
  }

  .agent-setting-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
  }

  .agent-setting-card:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .setting-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #edf2f7;
  }

  .agent-name {
    margin: 0;
    color: #2d3748;
    font-size: 18px;
    font-weight: 600;
  }

  .setting-status .status-badge {
    background: #e2e8f0;
    color: #4a5568;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .setting-status .status-badge.enabled {
    background: #c6f6d5;
    color: #22543d;
  }

  .setting-status .status-badge.disabled {
    background: #fed7d7;
    color: #c53030;
  }

  .setting-form {
    /* Vertical spacing provided by .setting-field margin-bottom */
  }

  .setting-field {
    margin-bottom: 20px;
  }

  .setting-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    color: #4a5568;
    margin-bottom: 8px;
    cursor: pointer;
  }

  .setting-label input[type="checkbox"] {
    margin: 0;
    transform: scale(1.2);
  }

  .notification-chats-input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    font-family: inherit;
    font-size: 14px;
    resize: vertical;
    min-height: 50px;
  }

  .notification-chats-input:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  .message-template-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #cbd5e0;
    border-radius: 8px;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.5;
    resize: vertical;
    min-height: 120px;
    background: #fafafa;
  }

  .message-template-input:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
    background: white;
  }

  .threshold-slider {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 0;
  }

  .threshold-slider input[type="range"] {
    flex: 1;
    height: 6px;
    background: #e2e8f0;
    border-radius: 3px;
    outline: none;
    -webkit-appearance: none;
    appearance: none;
  }

  .threshold-slider input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: #4299e1;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .threshold-slider input[type="range"]::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: #4299e1;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .threshold-value {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 80px;
  }

  .threshold-value .percentage {
    font-size: 18px;
    font-weight: 600;
    color: #2d3748;
  }

  .threshold-value .description {
    font-size: 12px;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .field-help code {
    background: #e2e8f0;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 11px;
    color: #2d3748;
  }

  .field-help {
    display: block;
    margin-top: 6px;
    color: #718096;
    font-size: 12px;
    line-height: 1.4;
  }

  .setting-actions {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #edf2f7;
  }

  .save-button {
    background: #e2e8f0;
    color: #4a5568;
    border: 1px solid #cbd5e0;
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 500;
    cursor: not-allowed;
    transition: all 0.2s ease;
  }

  .save-button.has-changes {
    background: #4299e1;
    color: white;
    border-color: #3182ce;
    cursor: pointer;
  }

  .save-button.has-changes:hover {
    background: #3182ce;
  }

  .save-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .setting-info {
    margin-top: 15px;
    padding-top: 10px;
    border-top: 1px solid #f7fafc;
  }

  .last-updated {
    color: #a0aec0;
    font-size: 11px;
  }

  .loading-state, .no-settings {
    text-align: center;
    padding: 40px 20px;
    color: #718096;
    font-style: italic;
  }

  /* Entity Selector Styles */
  .admin-entity-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    margin: 0;
  }

  .entity-selector-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  .entity-select-label {
    font-weight: 600;
    color: #2d3748;
    font-size: 14px;
    white-space: nowrap;
  }

  .entity-select-dropdown {
    flex: 1;
    max-width: 400px;
    padding: 10px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    font-size: 14px;
    background: white;
    cursor: pointer;
  }

  .entity-select-dropdown:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  .entity-selector-center {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .timezone-display {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  }

  .timezone-display strong {
    font-weight: 700;
  }

  .entity-selector-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  @media (max-width: 1024px) {
    .admin-entity-selector {
      flex-wrap: wrap;
    }

    .entity-selector-left {
      flex-basis: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .entity-select-dropdown {
      max-width: none;
    }

    .entity-selector-center {
      order: -1;
      flex-basis: 100%;
    }

    .timezone-display {
      width: 100%;
      justify-content: center;
    }
  }
`;

const broadcastStyles = `
  .broadcast-section {
    background: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
  }

  .broadcast-section h2 {
    margin: 0 0 8px 0;
    color: #2d3748;
    font-size: 24px;
    font-weight: 600;
  }

  .create-campaign-form {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;
    margin: 16px 0;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-group label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #4a5568;
  }

  .form-group input,
  .form-group textarea {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    font-size: 14px;
  }

  .source-options {
    display: flex;
    gap: 16px;
  }

  .source-options label {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font-weight: normal;
  }

  .form-actions {
    display: flex;
    gap: 12px;
    margin-top: 20px;
  }

  /* Scraping Schedule Display */
  .schedule-display {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 24px;
  }

  .schedule-card {
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .schedule-details h4 {
    margin-bottom: 16px;
    color: #2d3748;
    font-size: 18px;
    font-weight: 600;
  }

  .schedule-details p {
    margin-bottom: 12px;
    color: #4b5563;
    line-height: 1.5;
  }

  .schedule-details strong {
    color: #1a1e3e;
  }

  .status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    margin-left: 8px;
  }

  .status-badge.enabled {
    background: #10b981;
    color: white;
  }

  .status-badge.disabled {
    background: #ef4444;
    color: white;
  }

  .schedule-update-form {
    background: #f8f9fa;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;
    margin-top: 16px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #2d3748;
  }

  .form-group small {
    display: block;
    margin-bottom: 16px;
    color: #6b7280;
    line-height: 1.4;
  }

  .cron-input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 14px;
  }

  .email-input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  .campaign-details {
    background: #f0fff4;
    border: 1px solid #9ae6b4;
    border-radius: 8px;
    padding: 20px;
    margin: 16px 0;
  }

  .csv-upload {
    background: #fffbf0;
    border: 1px solid #f6e05e;
    border-radius: 6px;
    padding: 16px;
    margin: 16px 0;
  }

  .csv-upload input[type="file"] {
    margin: 8px 0;
  }

  .csv-upload small {
    display: block;
    color: #718096;
    margin-top: 8px;
  }

  .campaign-actions {
    display: flex;
    gap: 12px;
    margin-top: 16px;
  }

  .campaigns-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px;
    margin-top: 16px;
  }

  .campaign-card {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .campaign-card:hover {
    border-color: #4299e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .campaign-card.selected {
    border-color: #3182ce;
    background: #ebf8ff;
  }

  .campaign-card h4 {
    margin: 0 0 8px 0;
    color: #2d3748;
  }

  .status-created { color: #718096; }
  .status-running { color: #38a169; }
  .status-paused { color: #d69e2e; }
  .status-completed { color: #3182ce; }
  .status-failed { color: #e53e3e; }

  .no-campaigns {
    text-align: center;
    padding: 40px 20px;
    color: #718096;
    font-style: italic;
  }

  .progress-section {
    background: #f0f8ff;
    border: 1px solid #b0d4f1;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
  }

  .progress-section h4 {
    margin: 0 0 12px 0;
    color: #2d3748;
    font-size: 16px;
  }

  .progress-bar-container {
    margin: 12px 0;
  }

  .progress-bar {
    width: 100%;
    height: 24px;
    background: #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #48bb78, #38a169);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 12px;
    transition: width 0.3s ease;
    min-width: 40px;
  }

  .progress-stats {
    display: flex;
    gap: 16px;
    margin-top: 8px;
    flex-wrap: wrap;
  }

  .stat-item {
    font-size: 13px;
    color: #4a5568;
    background: white;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #cbd5e0;
  }

  .campaign-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 8px;
    margin: 16px 0;
  }

  .campaign-stats p {
    margin: 4px 0;
    font-size: 14px;
  }

  .mini-progress {
    margin-top: 8px;
  }

  .mini-progress-bar {
    width: 100%;
    height: 8px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 4px;
  }

  .mini-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #48bb78, #38a169);
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .mini-progress small {
    color: #718096;
    font-size: 11px;
  }
`;

const urlManagementStyles = {
  padding: '20px',
  maxWidth: '1400px',
  margin: '0 auto'
};

const urlManagementCSS = `
  .url-management-section {
    background: #f8fafc;
    min-height: 100vh;
  }

  .url-management-content {
    display: grid;
    gap: 24px;
  }

  .stats-overview,
  .manual-controls,
  .add-url-section,
  .urls-list-section,
  .scraping-schedule-section {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .stats-overview h3,
  .manual-controls h3,
  .add-url-section h3,
  .urls-list-section h3 {
    color: #2d3748;
    font-size: 18px;
    margin-bottom: 16px;
    border-bottom: 2px solid #f7fafc;
    padding-bottom: 8px;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }

  .stat-card {
    background: white;
    border: 2px solid #e2e8f0;
    color: #2d3748;
    padding: 20px;
    border-radius: 8px;
    text-align: center;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
  }

  .stat-card:hover {
    border-color: #4299e1;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }

  .stat-number {
    display: block;
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 4px;
    color: #4299e1;
  }

  .stat-label {
    font-size: 0.875rem;
    color: #718096;
    font-weight: 500;
  }

  .control-buttons {
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }

  .control-help {
    color: #718096;
    font-style: italic;
  }

  .button-explanations {
    margin-top: 20px;
    display: grid;
    gap: 16px;
  }

  .button-explanation {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
  }

  .button-explanation strong {
    color: #2d3748;
    display: block;
    margin-bottom: 8px;
  }

  .button-explanation p {
    margin: 8px 0;
    color: #4a5568;
    font-size: 14px;
  }

  .button-explanation ul {
    margin: 8px 0 12px 16px;
    padding: 0;
  }

  .button-explanation li {
    color: #4a5568;
    font-size: 13px;
    margin-bottom: 4px;
  }

  .button-explanation small {
    color: #718096;
    font-style: italic;
  }

  .stat-item {
    cursor: help;
    display: inline-block;
    margin-right: 8px;
    padding: 2px 4px;
    border-radius: 3px;
    transition: background-color 0.2s;
  }

  .stat-item:hover {
    background-color: #f7fafc;
  }

  .action-buttons {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  .btn-small {
    padding: 4px 8px;
    font-size: 12px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-info {
    background-color: #3182ce;
    color: white;
  }

  .btn-info:hover {
    background-color: #2c5aa0;
  }

  .btn-success {
    background-color: #38a169;
    color: white;
  }

  .btn-success:hover {
    background-color: #2f855a;
  }

  .btn-danger {
    background-color: #e53e3e;
    color: white;
  }

  .btn-danger:hover {
    background-color: #c53030;
  }

  .content-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .content-modal {
    background: white;
    border-radius: 12px;
    width: 90vw;
    max-width: 1200px;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
    background-color: #f7fafc;
  }

  .modal-header h2 {
    margin: 0;
    color: #2d3748;
    font-size: 20px;
    flex: 1;
  }

  .modal-close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #718096;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
  }

  .modal-close-btn:hover {
    background-color: #e2e8f0;
    color: #4a5568;
  }

  .modal-content {
    padding: 24px;
    overflow-y: auto;
    max-height: calc(90vh - 80px);
  }

  .content-metadata {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
    padding: 16px;
    background-color: #f7fafc;
    border-radius: 8px;
  }

  .metadata-item {
    font-size: 14px;
  }

  .metadata-item strong {
    color: #4a5568;
    margin-right: 8px;
  }

  .content-section {
    margin-bottom: 32px;
  }

  .content-section h3 {
    color: #2d3748;
    font-size: 18px;
    margin-bottom: 16px;
    border-bottom: 2px solid #e2e8f0;
    padding-bottom: 8px;
  }

  .content-display {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    max-height: 400px;
    overflow-y: auto;
  }

  .raw-content, .markdown-content {
    padding: 16px;
    margin: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
    background-color: #f8fafc;
  }

  .chunks-container {
    display: grid;
    gap: 16px;
  }

  .chunk-item {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }

  .chunk-header {
    background-color: #f7fafc;
    padding: 12px 16px;
    display: flex;
    gap: 16px;
    align-items: center;
    border-bottom: 1px solid #e2e8f0;
  }

  .chunk-index {
    font-weight: 600;
    color: #2d3748;
  }

  .chunk-type {
    background-color: #e2e8f0;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    color: #4a5568;
  }

  .chunk-tokens {
    color: #718096;
    font-size: 12px;
  }

  .chunk-content {
    padding: 16px;
    line-height: 1.6;
    color: #4a5568;
  }

  .chunk-metadata {
    padding: 12px 16px;
    background-color: #f8fafc;
    border-top: 1px solid #e2e8f0;
    font-family: monospace;
    font-size: 11px;
    color: #718096;
  }

  .no-content {
    padding: 40px 16px;
    text-align: center;
    color: #718096;
    font-style: italic;
  }

  .add-url-form {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 20px;
  }

  .form-row {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    align-items: stretch;
  }

  .form-row:last-child {
    margin-bottom: 0;
  }

  .url-input {
    flex: 2;
    padding: 10px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    font-size: 14px;
  }

  .title-input,
  .description-input {
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    font-size: 14px;
  }

  .category-select,
  .source-select,
  .priority-select {
    padding: 10px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    font-size: 14px;
    background: white;
  }

  .urls-table {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }

  .table-header,
  .table-row {
    display: grid;
    grid-template-columns: 2fr 1fr 120px 180px 120px 140px;
    gap: 12px;
    padding: 12px 16px;
    align-items: center;
  }

  .table-header {
    background: #f7fafc;
    font-weight: 600;
    color: #4a5568;
    border-bottom: 1px solid #e2e8f0;
  }

  .table-row {
    border-bottom: 1px solid #f1f5f9;
  }

  .table-row:hover {
    background: #f8fafc;
  }

  .table-row:last-child {
    border-bottom: none;
  }

  .url-link {
    color: #3182ce;
    text-decoration: none;
    font-weight: 500;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .url-link:hover {
    text-decoration: underline;
  }

  .url-source {
    display: block;
    color: #718096;
    font-size: 11px;
    margin-top: 2px;
  }

  .category-badge {
    background: #edf2f7;
    color: #4a5568;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    text-transform: capitalize;
  }

  .status-badge {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .status-active {
    background: #c6f6d5;
    color: #22543d;
  }

  .status-failed {
    background: #fed7d7;
    color: #742a2a;
  }

  .status-disabled {
    background: #e2e8f0;
    color: #718096;
  }

  .date-info {
    font-size: 12px;
    color: #4a5568;
    margin-bottom: 2px;
  }

  .date-info strong {
    color: #2d3748;
  }

  .stats-mini {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 11px;
  }

  .stats-mini span {
    display: block;
  }

  .btn-small {
    padding: 6px 8px;
    font-size: 12px;
    min-width: auto;
  }

  .no-data {
    text-align: center;
    padding: 40px 20px;
    color: #718096;
    font-style: italic;
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }

  .loading-state {
    text-align: center;
    padding: 40px 20px;
    color: #718096;
    font-style: italic;
  }

  @media (max-width: 1200px) {
    .table-header,
    .table-row {
      grid-template-columns: 2fr 1fr 100px 140px 100px 60px;
      gap: 8px;
      padding: 8px 12px;
    }
  }

  @media (max-width: 768px) {
    .form-row {
      flex-direction: column;
    }
    
    .control-buttons {
      flex-direction: column;
    }
    
    .stats-grid {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    }
    
    .table-header,
    .table-row {
      grid-template-columns: 1fr;
      gap: 4px;
    }
    
    .col-url,
    .col-category,
    .col-status,
    .col-last-updated,
    .col-stats,
    .col-actions {
      border-bottom: 1px solid #f1f5f9;
      padding: 8px 0;
    }
  }
`;

const knowledgeBaseStyles = `
  .knowledge-base-section {
    padding: 20px;
  }

  .section-header {
    margin-bottom: 30px;
    text-align: center;
  }

  .section-header h2 {
    color: #2d3748;
    font-size: 28px;
    margin-bottom: 8px;
  }

  .section-header p {
    color: #718096;
    font-size: 16px;
    margin: 0;
  }

  .knowledge-base-content {
    display: flex;
    flex-direction: column;
    gap: 30px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .upload-section,
  .documents-list-section,
  .upload-form-section,
  .documents-table-section,
  .statistics-section,
  .test-section {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .upload-section h3,
  .documents-list-section h3,
  .upload-form-section h3,
  .documents-table-section h3,
  .statistics-section h3,
  .test-section h3 {
    color: #2d3748;
    font-size: 20px;
    margin-bottom: 20px;
    border-bottom: 2px solid #f7fafc;
    padding-bottom: 10px;
  }

  .upload-area {
    text-align: center;
    margin-bottom: 20px;
  }

  .upload-button {
    display: inline-block;
    background: #4299e1;
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
  }

  .upload-button:hover {
    background: #3182ce;
  }

  .upload-info {
    margin-top: 10px;
  }

  .upload-info p {
    color: #718096;
    font-size: 14px;
    margin: 0;
  }

  .document-metadata {
    margin-bottom: 20px;
  }

  .metadata-row {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    gap: 12px;
  }

  .metadata-row label {
    font-weight: 500;
    min-width: 120px;
    color: #4a5568;
  }

  .metadata-row select {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
  }

  .upload-submit {
    width: 100%;
    background: #48bb78;
    color: white;
    padding: 12px;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .upload-submit:hover {
    background: #38a169;
  }

  .documents-controls {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
  }

  .search-input {
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
  }

  .filter-select {
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
    min-width: 150px;
  }

  .documents-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .document-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f7fafc;
  }

  .document-info h4 {
    margin: 0 0 8px 0;
    color: #2d3748;
    font-size: 16px;
  }

  .document-info p {
    margin: 4px 0;
    color: #718096;
    font-size: 14px;
  }

  .document-actions {
    display: flex;
    gap: 8px;
  }

  .btn {
    padding: 6px 12px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    transition: all 0.2s;
  }

  .btn-sm {
    padding: 4px 8px;
    font-size: 11px;
  }

  .btn-primary {
    background: #4299e1;
    color: white;
  }

  .btn-primary:hover {
    background: #3182ce;
  }

  .btn-danger {
    background: #f56565;
    color: white;
  }

  .btn-danger:hover {
    background: #e53e3e;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }

  .stat-card {
    text-align: center;
    padding: 20px;
    background: #f7fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }

  .stat-card h4 {
    font-size: 24px;
    color: #2d3748;
    margin: 0 0 8px 0;
  }

  .stat-card p {
    color: #718096;
    font-size: 14px;
    margin: 0;
  }

  .test-controls {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
  }

  .test-query-input {
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
  }

  .test-results {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    min-height: 100px;
  }

  .test-results p {
    color: #718096;
    margin: 0;
    font-style: italic;
  }

  @media (max-width: 768px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  /* New styles for functional Knowledge Base */
  .selected-file-info {
    margin-top: 12px;
    padding: 10px;
    background: #f0fdf4;
    border: 1px solid #86efac;
    border-radius: 6px;
  }

  .selected-file-info p {
    color: #166534;
    margin: 0;
    font-size: 14px;
  }

  /* Metadata fields styling */
  .upload-form-grid {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .upload-metadata-fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .metadata-field {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  .metadata-label {
    min-width: 120px;
    width: 120px;
    font-weight: 600;
    color: #2d3748;
    font-size: 15px;
    line-height: 1.5;
    padding-top: 8px;
  }

  .metadata-textarea {
    flex: 1;
    min-height: 90px;
    max-height: 150px;
    padding: 12px 16px;
    border: 1px solid #cbd5e0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    line-height: 1.6;
    resize: vertical;
    overflow-y: auto;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .metadata-textarea:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  .metadata-textarea:disabled {
    background-color: #f7fafc;
    cursor: not-allowed;
    opacity: 0.6;
  }

  .metadata-textarea::placeholder {
    color: #a0aec0;
  }

  .metadata-input {
    flex: 1;
    padding: 10px 16px;
    border: 1px solid #cbd5e0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    line-height: 1.5;
    height: 42px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .metadata-input:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  .metadata-input:disabled {
    background-color: #f7fafc;
    cursor: not-allowed;
    opacity: 0.6;
  }

  .metadata-input::placeholder {
    color: #a0aec0;
  }

  /* Documents table styling */
  .documents-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border-radius: 8px;
    overflow: hidden;
  }

  .documents-table thead {
    background: #f7fafc;
    border-bottom: 2px solid #e2e8f0;
  }

  .documents-table th {
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    color: #4a5568;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .documents-table tbody tr {
    border-bottom: 1px solid #e2e8f0;
    transition: background-color 0.2s;
  }

  .documents-table tbody tr:hover {
    background-color: #f7fafc;
  }

  .documents-table tbody tr:last-child {
    border-bottom: none;
  }

  .documents-table td {
    padding: 12px 16px;
    font-size: 14px;
    color: #2d3748;
  }

  .filename-cell {
    font-weight: 500;
    max-width: 450px;
    word-break: break-word;
  }

  .description-cell {
    max-width: 375px;
    word-break: break-word;
    color: #718096;
  }

  .source-cell {
    white-space: nowrap;
    max-width: 100px;
  }

  .source-badge {
    display: inline-block;
    padding: 4px 8px;
    background: #ebf8ff;
    color: #3182ce;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .size-cell {
    white-space: nowrap;
    font-family: 'Courier New', monospace;
    color: #718096;
  }

  .date-cell {
    white-space: nowrap;
    color: #718096;
    font-size: 13px;
  }

  .actions-cell {
    white-space: nowrap;
  }

  .actions-cell .btn {
    margin-right: 8px;
    padding: 6px 12px;
    font-size: 13px;
  }

  .search-input {
    padding: 8px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 6px;
    font-size: 14px;
    width: 250px;
    transition: border-color 0.2s;
  }

  .search-input:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #718096;
  }

  .empty-state p {
    font-size: 16px;
  }

  .category-breakdown-section {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    grid-column: 1 / 3;
  }

  .category-breakdown-section h3 {
    color: #2d3748;
    font-size: 18px;
    margin-bottom: 16px;
  }

  .category-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .category-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    font-size: 14px;
  }

  .category-name {
    font-weight: 500;
    color: #4a5568;
  }

  .category-count {
    color: #718096;
    font-size: 12px;
  }

  .status-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
  }

  .status-processing {
    background: #fef3c7;
    color: #92400e;
  }

  .status-pending {
    background: #fed7aa;
    color: #7c2d12;
  }

  .status-error {
    background: #fecaca;
    color: #991b1b;
  }

  .upload-submit:disabled {
    background: #a0aec0;
    cursor: not-allowed;
  }

  .upload-submit:disabled:hover {
    background: #a0aec0;
  }
`;

const learningSystemStyles = `
  .learning-system-section {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .learning-status-card,
  .learning-controls-card,
  .learning-qa-pairs-card,
  .learning-runs-card,
  .review-queue-card,
  .learning-config-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .learning-status-card h3,
  .learning-controls-card h3,
  .learning-runs-card h3,
  .review-queue-card h3,
  .learning-config-card h3 {
    margin: 0 0 16px 0;
    color: #2d3748;
    font-size: 18px;
    font-weight: 600;
  }

  .status-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }

  .status-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: #f7fafc;
    border-radius: 8px;
    border-left: 4px solid #4299e1;
  }

  .status-label {
    font-weight: 500;
    color: #4a5568;
  }

  .status-value {
    font-weight: 600;
    font-size: 18px;
    color: #2d3748;
  }

  .status-value.success {
    color: #38a169;
  }

  .status-value.warning {
    color: #d69e2e;
  }

  .status-value.error {
    color: #e53e3e;
  }

  .controls-section {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
  }

  .processing-info {
    background: #f7fafc;
    padding: 16px;
    border-radius: 8px;
    border-left: 4px solid #4299e1;
  }

  .processing-info ul {
    margin: 8px 0 0 0;
    padding-left: 20px;
  }

  .processing-info li {
    margin-bottom: 4px;
    color: #4a5568;
  }

  .runs-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .run-item {
    padding: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f7fafc;
  }

  .run-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    gap: 12px;
  }

  .run-id {
    font-weight: 600;
    color: #2d3748;
  }

  .run-status {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
  }

  .run-status.status-completed {
    background: #c6f6d5;
    color: #2f855a;
  }

  .run-status.status-running {
    background: #bee3f8;
    color: #2b6cb0;
  }

  .run-status.status-failed {
    background: #fed7d7;
    color: #c53030;
  }

  .run-date {
    color: #718096;
    font-size: 14px;
    margin-left: auto;
  }

  .run-stats {
    display: flex;
    gap: 16px;
    font-size: 14px;
    color: #4a5568;
  }

  .no-runs,
  .no-reviews {
    text-align: center;
    padding: 40px 20px;
    color: #718096;
    font-style: italic;
  }

  .reviews-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .review-item {
    padding: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fffaf0;
    border-left: 4px solid #d69e2e;
  }

  .review-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    gap: 12px;
  }

  .review-priority {
    font-weight: 600;
    color: #d69e2e;
  }

  .review-confidence {
    font-weight: 600;
    color: #2d3748;
    margin-left: auto;
  }

  .qa-preview {
    margin: 12px 0;
    padding: 12px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
  }

  .qa-preview .question {
    margin-bottom: 8px;
    color: #2d3748;
  }

  .qa-preview .answer {
    color: #4a5568;
  }

  .review-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .btn-sm {
    padding: 4px 8px;
    font-size: 12px;
  }

  .view-all-reviews {
    text-align: center;
    margin-top: 16px;
  }

  .config-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
  }

  .config-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: #f7fafc;
    border-radius: 8px;
  }

  .config-item label {
    font-weight: 500;
    color: #4a5568;
  }

  .config-item span {
    font-weight: 600;
    color: #2d3748;
  }

  .schedule-status.enabled {
    color: #38a169;
  }

  .schedule-status.disabled {
    color: #e53e3e;
  }

  @media (max-width: 768px) {
    .status-grid {
      grid-template-columns: 1fr;
    }

    .config-grid {
      grid-template-columns: 1fr;
    }

    .controls-section {
      flex-direction: column;
    }

    .run-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .run-stats {
      flex-wrap: wrap;
      gap: 8px;
    }

    .review-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .review-actions {
      justify-content: flex-start;
    }
  }

  /* Q&A Pairs Styles */
  .qa-pairs-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 16px;
  }

  .qa-pairs-filters {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .qa-pairs-filters label {
    font-weight: 500;
    color: #4a5568;
    font-size: 14px;
  }

  .filter-select {
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: white;
    color: #2d3748;
    font-size: 14px;
    cursor: pointer;
    min-width: 180px;
  }

  .filter-select:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  .qa-pairs-list {
    max-height: 600px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }

  .qa-pair-item {
    border-bottom: 1px solid #f7fafc;
    padding: 20px;
    background: white;
  }

  .qa-pair-item:last-child {
    border-bottom: none;
  }

  .qa-pair-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .qa-pair-id {
    font-weight: 600;
    color: #4a5568;
    font-size: 14px;
  }

  .qa-pair-status {
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
  }

  .qa-pair-status.auto_approved {
    background: #c6f6d5;
    color: #22543d;
  }

  .qa-pair-status.needs_review {
    background: #feebc8;
    color: #c05621;
  }

  .qa-pair-status.rejected {
    background: #fed7d7;
    color: #c53030;
  }

  .qa-pair-confidence {
    font-size: 12px;
    color: #718096;
    margin-left: auto;
  }

  .qa-pair-content .question,
  .qa-pair-content .answer {
    margin-bottom: 16px;
  }

  .qa-pair-content .question p,
  .qa-pair-content .answer p {
    margin: 8px 0 0 0;
    padding: 12px;
    background: #f7fafc;
    border-radius: 8px;
    border-left: 3px solid #4299e1;
    line-height: 1.5;
  }

  .qa-pair-content .answer p {
    border-left-color: #48bb78;
  }

  .translation-info small {
    color: #718096;
    font-style: italic;
  }

  .category-tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 8px;
  }

  .category-tag {
    background: #edf2f7;
    color: #4a5568;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
  }

  .qa-pair-meta {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #f7fafc;
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .qa-pair-meta small {
    color: #a0aec0;
    font-size: 11px;
  }

  .no-qa-pairs, .no-data {
    text-align: center;
    padding: 40px 20px;
    color: #718096;
    font-style: italic;
    background: #f7fafc;
    border-radius: 8px;
  }

  /* Status sections */
  .status-section {
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f7fafc;
  }

  .status-section:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  .status-section h4 {
    margin: 0 0 16px 0;
    color: #2d3748;
    font-size: 16px;
    font-weight: 600;
  }

  /* Manual Review Instructions and Input Styles */
  .review-instructions {
    margin: 20px 0;
  }

  .instruction-box {
    background: #f0f8ff;
    border: 1px solid #b0d4f1;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
  }

  .instruction-box h4 {
    margin: 0 0 12px 0;
    color: #2d3748;
    font-size: 16px;
    font-weight: 600;
  }

  .instruction-box ol {
    margin: 0;
    padding-left: 20px;
    color: #4a5568;
  }

  .instruction-box li {
    margin-bottom: 8px;
    line-height: 1.5;
  }

  .reviewer-input-section {
    background: #fffbf0;
    border: 2px solid #f6e05e;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
  }

  .reviewer-label {
    display: block;
    font-size: 14px;
    color: #2d3748;
    margin-bottom: 8px;
  }

  .reviewer-name-input {
    width: 100%;
    padding: 12px;
    border: 2px solid #cbd5e0;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .reviewer-name-input:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  .reviewer-warning {
    color: #d69e2e;
    font-size: 13px;
    font-weight: 500;
    padding: 8px 12px;
    background: #fffbeb;
    border: 1px solid #f6e05e;
    border-radius: 4px;
    margin-top: 8px;
  }

  .reviewer-success {
    color: #38a169;
    font-size: 13px;
    font-weight: 500;
    padding: 8px 12px;
    background: #f0fff4;
    border: 1px solid #9ae6b4;
    border-radius: 4px;
    margin-top: 8px;
  }

  /* AI Configuration Styles */
  .ai-config-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
  }

  .ai-config-controls {
    margin: 16px 0;
  }

  .current-config-display {
    background: #f0f8ff;
    border: 1px solid #b0d4f1;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
  }

  .current-config-display h4 {
    margin: 0 0 12px 0;
    color: #2d3748;
    font-size: 16px;
    font-weight: 600;
  }

  .config-info {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 12px;
  }

  .config-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .config-label {
    font-size: 12px;
    font-weight: 600;
    color: #718096;
    text-transform: uppercase;
  }

  .config-value {
    font-size: 14px;
    color: #2d3748;
    font-weight: 500;
  }

  .ai-config-panel {
    border-top: 2px solid #e2e8f0;
    padding-top: 20px;
    margin-top: 16px;
  }

  .model-section {
    margin-bottom: 32px;
  }

  .model-section h4 {
    margin: 0 0 16px 0;
    color: #2d3748;
    font-size: 16px;
    font-weight: 600;
  }

  .models-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
  }

  .model-item {
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    background: #f7fafc;
    transition: all 0.2s;
  }

  .model-item.active {
    border-color: #38a169;
    background: #f0fff4;
  }

  .model-info {
    margin-bottom: 12px;
  }

  .model-name {
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 4px;
  }

  .model-provider {
    font-size: 12px;
    color: #718096;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .model-description {
    font-size: 14px;
    color: #4a5568;
    line-height: 1.4;
  }

  .model-controls {
    display: flex;
    gap: 8px;
  }

  .add-model-section {
    background: #fffaf0;
    border: 1px solid #f6e05e;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 32px;
  }

  .add-model-section h5 {
    margin: 0 0 16px 0;
    color: #2d3748;
    font-size: 14px;
    font-weight: 600;
  }

  .model-form,
  .new-model-form {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
  }

  .form-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .form-field label {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
  }

  .form-field input,
  .form-field select,
  .form-field textarea {
    padding: 8px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    font-size: 14px;
  }

  .form-field input:focus,
  .form-field select:focus,
  .form-field textarea:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  .prompts-section {
    margin-bottom: 32px;
  }

  .prompts-section h4 {
    margin: 0 0 16px 0;
    color: #2d3748;
    font-size: 16px;
    font-weight: 600;
  }

  .prompts-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 16px;
  }

  .prompt-item {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
    background: white;
  }

  .prompt-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .prompt-type {
    font-size: 12px;
    font-weight: 600;
    color: #4299e1;
    text-transform: uppercase;
  }

  .prompt-version {
    font-size: 12px;
    color: #718096;
    background: #f7fafc;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .prompt-name {
    font-weight: 500;
    color: #2d3748;
    margin-bottom: 8px;
  }

  .prompt-stats {
    display: flex;
    gap: 16px;
    font-size: 12px;
    color: #718096;
    margin-bottom: 12px;
  }

  /* Prompt Editor Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .prompt-editor-modal {
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 900px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 2px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 12px 12px 0 0;
  }

  .modal-header h3 {
    margin: 0;
    color: #2d3748;
    font-size: 20px;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background-color 0.2s;
  }

  .close-btn:hover {
    background: #e2e8f0;
  }

  .prompt-info {
    padding: 20px 24px;
    background: #f0f8ff;
    border-bottom: 1px solid #e2e8f0;
  }

  .prompt-meta-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .meta-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .meta-item label {
    font-size: 12px;
    font-weight: 600;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .meta-item span {
    font-size: 14px;
    color: #2d3748;
    font-weight: 500;
  }

  .prompt-type-badge {
    background: #4299e1;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    width: fit-content;
  }

  .prompt-content-section {
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
  }

  .prompt-label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 12px;
  }

  .prompt-content-display {
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px;
    max-height: 200px;
    overflow-y: auto;
  }

  .prompt-text {
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    line-height: 1.5;
    color: #2d3748;
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .prompt-editor-section {
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
  }

  .prompt-editor-textarea {
    width: 100%;
    padding: 12px;
    border: 2px solid #cbd5e0;
    border-radius: 6px;
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    line-height: 1.5;
    resize: vertical;
    min-height: 200px;
    margin-bottom: 12px;
  }

  .prompt-editor-textarea:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  .prompt-editor-controls {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .version-description {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    font-size: 14px;
  }

  .save-prompt-btn {
    padding: 8px 16px;
    background: #38a169;
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .save-prompt-btn:hover {
    background: #2f855a;
  }

  .prompt-actions {
    padding: 20px 24px;
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  .prompt-actions .btn {
    padding: 10px 16px;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .prompt-actions .btn-secondary {
    background: #e2e8f0;
    color: #4a5568;
  }

  .prompt-actions .btn-secondary:hover {
    background: #cbd5e0;
  }

  .prompt-actions .btn-success {
    background: #38a169;
    color: white;
  }

  .prompt-actions .btn-success:hover {
    background: #2f855a;
  }

  @media (max-width: 768px) {
    .prompt-editor-modal {
      width: 95%;
      margin: 20px;
    }

    .prompt-meta-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .prompt-editor-controls {
      flex-direction: column;
      align-items: stretch;
    }

    .models-list,
    .prompts-list {
      grid-template-columns: 1fr;
    }

    .model-form {
      grid-template-columns: 1fr;
    }
  }

  /* Service Area Selection Styles */
  .service-area-selection {
    margin-top: 16px;
    padding: 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }

  .service-area-selection strong {
    display: block;
    margin-bottom: 12px;
    color: #2d3748;
    font-size: 14px;
    font-weight: 600;
  }

  .checkbox-group {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
  }

  .service-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #4a5568;
    cursor: pointer;
    user-select: none;
  }

  .service-checkbox input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #4299e1;
    cursor: pointer;
  }

  .service-checkbox:hover {
    color: #2d3748;
  }

  .service-checkbox input[type="checkbox"]:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .service-checkbox:has(input[type="checkbox"]:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Calendar Settings Card Component
const CalendarSettingsCard: React.FC<{
  schedule: CalendarSchedule | null;
  updating: boolean;
  onUpdate: (checkTime: string, timezone: string, daysAhead: number) => void;
  onTest: () => void;
}> = ({ schedule, updating, onUpdate, onTest }) => {
  const [localCheckTime, setLocalCheckTime] = useState(schedule?.checkTime || '20:00');
  const [localTimezone, setLocalTimezone] = useState(schedule?.timezone || 'Asia/Singapore');
  const [localDaysAhead, setLocalDaysAhead] = useState(schedule?.daysAhead || 1);

  // Update local state when schedule changes
  React.useEffect(() => {
    if (schedule) {
      setLocalCheckTime(schedule.checkTime);
      setLocalTimezone(schedule.timezone);
      setLocalDaysAhead(schedule.daysAhead);
    }
  }, [schedule]);

  const handleSave = () => {
    onUpdate(localCheckTime, localTimezone, localDaysAhead);
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="agent-setting-card">
      <div className="setting-header">
        <h3 className="agent-name">📅 Calendar Reminder Schedule</h3>
        <div className="setting-status">
          <span className="status-badge">
            {schedule ? 'Configured' : 'Not Set'}
          </span>
        </div>
      </div>

      <div className="setting-field">
        <label className="setting-label">
          ⏰ Daily Check Time (SGT)
          <span className="field-help">Time when calendar agent checks for next day's Sales meetings</span>
        </label>
        <input
          type="time"
          value={localCheckTime}
          onChange={(e) => setLocalCheckTime(e.target.value)}
          className="setting-input"
          disabled={updating}
        />
      </div>

      <div className="setting-field">
        <label className="setting-label">
          🌏 Timezone
          <span className="field-help">Timezone for scheduling calendar checks</span>
        </label>
        <select
          value={localTimezone}
          onChange={(e) => setLocalTimezone(e.target.value)}
          className="setting-input"
          disabled={updating}
        >
          <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
          <option value="America/New_York">America/New_York (EST/EDT)</option>
          <option value="Europe/London">Europe/London (GMT/BST)</option>
          <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
          <option value="UTC">UTC</option>
        </select>
      </div>

      <div className="setting-field">
        <label className="setting-label">
          📅 Days Ahead
          <span className="field-help">How many days ahead to check for meetings (default: 1 for next day)</span>
        </label>
        <select
          value={localDaysAhead}
          onChange={(e) => setLocalDaysAhead(parseInt(e.target.value))}
          className="setting-input"
          disabled={updating}
        >
          <option value={1}>1 day (Tomorrow only)</option>
          <option value={2}>2 days</option>
          <option value={3}>3 days</option>
          <option value={7}>7 days (1 week)</option>
        </select>
      </div>

      {schedule && (
        <div className="schedule-status">
          <div className="status-item">
            <strong>🕐 Last Check:</strong> {formatDateTime(schedule.lastCheck)}
          </div>
          <div className="status-item">
            <strong>⏭️ Next Check:</strong> {formatDateTime(schedule.nextCheck)}
          </div>
          <div className="status-item">
            <strong>🔄 Schedule:</strong> Daily at {localCheckTime} {localTimezone}
          </div>
        </div>
      )}

      <div className="setting-actions">
        <button
          onClick={handleSave}
          disabled={updating}
          className="btn btn-primary"
        >
          {updating ? 'Saving...' : '💾 Save Schedule'}
        </button>

        <button
          onClick={onTest}
          disabled={updating}
          className="btn btn-secondary"
          style={{ marginLeft: '10px' }}
        >
          🧪 Test Calendar Check
        </button>
      </div>

      <div className="setting-help">
        <p>
          <strong>📋 How it works:</strong>
        </p>
        <ul>
          <li>Calendar agent automatically checks for "Sales:" meetings daily at the configured time</li>
          <li>Extracts phone numbers from meeting titles (e.g., "Sales: John Doe 6587855563")</li>
          <li>Sends WhatsApp reminders: "Hi, this is a friendly reminder that we have a meeting on [date] at [time], tks"</li>
          <li>Supports Singapore 8-digit numbers (auto-adds +65) and international numbers</li>
        </ul>
      </div>
    </div>
  );
};

// Unanswered Messages Settings Card Component
const UnansweredMessagesSettingsCard: React.FC<{
  schedule: UnansweredMessagesSchedule | null;
  updating: boolean;
  onUpdate: (checkTimes: string[], timezone: string, maxChats: number, minutesThreshold: number, notificationChats: string, llmTimeoutSeconds: number) => void;
  onTest: () => void;
  onToggleEnabled?: (enabled: boolean) => void;
}> = ({ schedule, updating, onUpdate, onTest, onToggleEnabled }) => {
  const [localCheckTimes, setLocalCheckTimes] = useState<string[]>(schedule?.checkTimes || ['12:00', '17:30']);
  const [localTimezone, setLocalTimezone] = useState(schedule?.timezone || 'Asia/Singapore');
  const [localMaxChats, setLocalMaxChats] = useState(schedule?.maxChats || 100);
  const [localMinutesThreshold, setLocalMinutesThreshold] = useState(schedule?.minutesThreshold || 30);
  const [localLlmTimeoutSeconds, setLocalLlmTimeoutSeconds] = useState(schedule?.llmTimeoutSeconds || 60);
  const [localEnabled, setLocalEnabled] = useState(schedule?.enabled ?? false);
  const [localNotificationChats, setLocalNotificationChats] = useState(schedule?.notificationChats || 'E&H Immigration Internal');

  // Update local state when schedule changes
  React.useEffect(() => {
    if (schedule) {
      setLocalCheckTimes(schedule.checkTimes);
      setLocalTimezone(schedule.timezone);
      setLocalMaxChats(schedule.maxChats);
      setLocalMinutesThreshold(schedule.minutesThreshold);
      setLocalLlmTimeoutSeconds(schedule.llmTimeoutSeconds || 60);
      setLocalEnabled(schedule.enabled ?? false);
      setLocalNotificationChats(schedule.notificationChats || 'E&H Immigration Internal');
    }
  }, [schedule]);

  const handleSave = () => {
    onUpdate(localCheckTimes, localTimezone, localMaxChats, localMinutesThreshold, localNotificationChats, localLlmTimeoutSeconds);
  };

  const handleToggleEnabled = () => {
    const newEnabled = !localEnabled;
    setLocalEnabled(newEnabled);
    if (onToggleEnabled) {
      onToggleEnabled(newEnabled);
    }
  };

  const handleCheckTimesChange = (value: string) => {
    // Split by comma and clean up times
    const times = value.split(',').map(time => time.trim()).filter(time => time.length > 0);
    setLocalCheckTimes(times);
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="agent-setting-card">
      <div className="setting-header">
        <h3 className="agent-name">🚨 Unanswered Messages Monitor</h3>
        <div className="setting-status">
          <span
            className="status-badge"
            style={{
              backgroundColor: localEnabled ? '#c6f6d5' : '#fed7d7',
              color: localEnabled ? '#22543d' : '#742a2a'
            }}
          >
            {localEnabled ? '✅ Enabled' : '❌ Disabled'}
          </span>
        </div>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="setting-field">
        <label className="setting-label">
          {localEnabled ? '🟢 Agent Enabled' : '🔴 Agent Disabled'}
          <span className="field-help">
            {localEnabled
              ? 'The agent will scan for unanswered messages according to the schedule below'
              : 'The agent will not scan for unanswered messages for this entity'}
          </span>
        </label>
        <button
          onClick={handleToggleEnabled}
          className={`btn ${localEnabled ? 'btn-secondary' : 'btn-primary'}`}
          disabled={updating}
          style={{
            padding: '8px 16px',
            fontSize: '14px'
          }}
        >
          {localEnabled ? '🔴 Disable' : '✅ Enable'}
        </button>
      </div>

      <div className="setting-field">
        <label className="setting-label">
          ⏰ Daily Check Times (SGT)
          <span className="field-help">Times when agent scans for unanswered messages (comma-separated HH:MM format)</span>
        </label>
        <input
          type="text"
          value={localCheckTimes.join(', ')}
          onChange={(e) => handleCheckTimesChange(e.target.value)}
          className="setting-input"
          placeholder="12:00, 17:30"
          disabled={updating}
        />
        <small className="field-help">
          <strong>📋 Examples:</strong> "12:00, 17:30" (default: noon and 5:30pm), "09:00, 14:00, 19:00" (3 times daily)
        </small>
      </div>

      <div className="setting-field">
        <label className="setting-label">
          🌏 Timezone
          <span className="field-help">Timezone for scheduling unanswered message checks</span>
        </label>
        <select
          value={localTimezone}
          onChange={(e) => setLocalTimezone(e.target.value)}
          className="setting-input"
          disabled={updating}
        >
          <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
          <option value="America/New_York">America/New_York (EST/EDT)</option>
          <option value="Europe/London">Europe/London (GMT/BST)</option>
          <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
          <option value="UTC">UTC</option>
        </select>
      </div>

      <div className="setting-field">
        <label className="setting-label">
          📊 Maximum Chats to Scan
          <span className="field-help">Number of most recent chats to check for unanswered messages</span>
        </label>
        <select
          value={localMaxChats}
          onChange={(e) => setLocalMaxChats(parseInt(e.target.value))}
          className="setting-input"
          disabled={updating}
        >
          <option value={50}>50 chats</option>
          <option value={100}>100 chats (recommended)</option>
          <option value={200}>200 chats</option>
          <option value={500}>500 chats</option>
        </select>
      </div>

      <div className="setting-field">
        <label className="setting-label">
          ⏳ Minutes Threshold
          <span className="field-help">Consider messages unanswered after this many minutes</span>
        </label>
        <select
          value={localMinutesThreshold}
          onChange={(e) => setLocalMinutesThreshold(parseInt(e.target.value))}
          className="setting-input"
          disabled={updating}
        >
          <option value={5}>5 minutes</option>
          <option value={10}>10 minutes</option>
          <option value={15}>15 minutes</option>
          <option value={30}>30 minutes (recommended)</option>
          <option value={60}>60 minutes (1 hour)</option>
          <option value={120}>120 minutes (2 hours)</option>
        </select>
      </div>

      <div className="setting-field">
        <label className="setting-label">
          ⏱️ LLM Timeout
          <span className="field-help">Time to wait for LLM response (higher = more chats can be processed but slower)</span>
        </label>
        <select
          value={localLlmTimeoutSeconds}
          onChange={(e) => setLocalLlmTimeoutSeconds(parseInt(e.target.value))}
          className="setting-input"
          disabled={updating}
        >
          <option value={15}>15 seconds (fast, fewer chats)</option>
          <option value={30}>30 seconds (default)</option>
          <option value={60}>60 seconds (recommended for batch)</option>
          <option value={90}>90 seconds (large batch)</option>
          <option value={120}>120 seconds (maximum)</option>
        </select>
        <small className="field-help">
          <strong>💡 Tip:</strong> Increase timeout if you see "LLM call failed" errors. 60s allows processing ~50-100 chats in one batch.
        </small>
      </div>

      <div className="setting-field">
        <label className="setting-label">
          📢 Send Alerts To
          <span className="field-help">WhatsApp group or contact to send summary alerts to (comma-separated for multiple destinations)</span>
        </label>
        <input
          type="text"
          value={localNotificationChats}
          onChange={(e) => setLocalNotificationChats(e.target.value)}
          className="setting-input"
          placeholder="E&H Immigration Internal"
          disabled={updating}
        />
        <small className="field-help">
          <strong>📋 Examples:</strong><br />
          • Single group: <code>E&H Immigration Internal</code><br />
          • Multiple groups: <code>E&H Immigration Internal, E&H Sales</code><br />
          • Phone number: <code>+65 1234 5678</code><br />
          • Mix: <code>E&H Sales, +65 1234 5678</code>
        </small>
      </div>

      {schedule && (
        <div className="schedule-status">
          <div className="status-item">
            <strong>🕐 Last Check:</strong> {formatDateTime(schedule.lastCheck)}
          </div>
          <div className="status-item">
            <strong>⏭️ Next Check:</strong> {formatDateTime(schedule.nextCheck)}
          </div>
          <div className="status-item">
            <strong>🔄 Schedule:</strong> Daily at {localCheckTimes.join(', ')} {localTimezone}
          </div>
        </div>
      )}

      <div className="setting-actions">
        <button
          onClick={handleSave}
          disabled={updating}
          className="btn btn-primary"
        >
          {updating ? 'Saving...' : '💾 Save Schedule'}
        </button>

        <button
          onClick={onTest}
          disabled={updating}
          className="btn btn-secondary"
          style={{ marginLeft: '10px' }}
        >
          🧪 Test Unanswered Check
        </button>
      </div>

      <div className="setting-help">
        <p>
          <strong>📋 How it works:</strong>
        </p>
        <ul>
          <li>Agent scans the top {localMaxChats} most recent chats at scheduled times</li>
          <li>Identifies messages from clients that haven't been responded to for {localMinutesThreshold}+ minutes</li>
          <li>Filters for messages requiring responses (questions, inquiries, immigration topics)</li>
          <li>Sends summary alerts to configured destination(s): <strong>{localNotificationChats}</strong></li>
          <li>Supports both individual chats (shows name + phone) and group chats (shows group name)</li>
        </ul>
      </div>
    </div>
  );
};

// Qualifier Schedule Card Component
const QualifierScheduleCard: React.FC<{
  schedule: QualifierSchedule | null;
  updating: boolean;
  onUpdate: (updates: { dayOfWeek: number; hourOfDay: number; isActive: boolean }[]) => void;
}> = ({ schedule, updating, onUpdate }) => {
  const [localSchedule, setLocalSchedule] = useState<QualifierSchedule>(() => {
    // Initialize with empty schedule if none provided
    const emptySchedule: QualifierSchedule = {};
    for (let day = 0; day <= 6; day++) {
      emptySchedule[day] = {};
      for (let hour = 0; hour <= 23; hour++) {
        emptySchedule[day][hour] = false;
      }
    }
    return emptySchedule;
  });

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ day: number, hour: number } | null>(null);

  // Update local state when schedule changes
  React.useEffect(() => {
    if (schedule) {
      setLocalSchedule(JSON.parse(JSON.stringify(schedule))); // Deep copy
    }
  }, [schedule]);

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const hourLabels = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);

  const handleCellClick = (dayOfWeek: number, hourOfDay: number) => {
    const newSchedule = JSON.parse(JSON.stringify(localSchedule));
    newSchedule[dayOfWeek][hourOfDay] = !newSchedule[dayOfWeek][hourOfDay];
    setLocalSchedule(newSchedule);
  };

  const handleCellMouseDown = (dayOfWeek: number, hourOfDay: number) => {
    setIsSelecting(true);
    setSelectionStart({ day: dayOfWeek, hour: hourOfDay });
    handleCellClick(dayOfWeek, hourOfDay);
  };

  const handleMouseEnter = (dayOfWeek: number, hourOfDay: number) => {
    if (isSelecting && selectionStart) {
      const newSchedule = JSON.parse(JSON.stringify(localSchedule));
      const startDay = Math.min(selectionStart.day, dayOfWeek);
      const endDay = Math.max(selectionStart.day, dayOfWeek);
      const startHour = Math.min(selectionStart.hour, hourOfDay);
      const endHour = Math.max(selectionStart.hour, hourOfDay);

      // Get the value of the starting cell to apply to all cells in selection
      const startValue = localSchedule[selectionStart.day][selectionStart.hour];

      for (let day = startDay; day <= endDay; day++) {
        for (let hour = startHour; hour <= endHour; hour++) {
          newSchedule[day][hour] = startValue;
        }
      }
      setLocalSchedule(newSchedule);
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
    setSelectionStart(null);
  };

  const handleSave = () => {
    const updates: { dayOfWeek: number; hourOfDay: number; isActive: boolean }[] = [];

    for (let day = 0; day <= 6; day++) {
      for (let hour = 0; hour <= 23; hour++) {
        if (localSchedule[day][hour] !== (schedule?.[day]?.[hour] || false)) {
          updates.push({
            dayOfWeek: day,
            hourOfDay: hour,
            isActive: localSchedule[day][hour]
          });
        }
      }
    }

    if (updates.length > 0) {
      onUpdate(updates);
    } else {
      alert("No changes to save.");
    }
  };

  const handleSetWeekdaySchedule = () => {
    // Set Monday-Friday: 6pm-9am next day
    const newSchedule = JSON.parse(JSON.stringify(localSchedule));

    // Clear all first
    for (let day = 0; day <= 6; day++) {
      for (let hour = 0; hour <= 23; hour++) {
        newSchedule[day][hour] = false;
      }
    }

    // Weekdays (Monday=0 to Friday=4): 6pm-23:59 and 00:00-08:59
    for (let day = 0; day <= 4; day++) {
      // Evening 6pm-11pm
      for (let hour = 18; hour <= 23; hour++) {
        newSchedule[day][hour] = true;
      }
      // Early morning 0am-8am for next day
      const nextDay = (day + 1) % 7;
      for (let hour = 0; hour <= 8; hour++) {
        newSchedule[nextDay][hour] = true;
      }
    }

    // Saturday (5): Full day until Monday 9am
    for (let hour = 0; hour <= 23; hour++) {
      newSchedule[5][hour] = true;
    }
    // Sunday (6): Full day
    for (let hour = 0; hour <= 23; hour++) {
      newSchedule[6][hour] = true;
    }
    // Monday (0): Early morning 0am-8am from Sunday
    for (let hour = 0; hour <= 8; hour++) {
      newSchedule[0][hour] = true;
    }

    setLocalSchedule(newSchedule);
  };

  const handleClearSchedule = () => {
    const newSchedule = JSON.parse(JSON.stringify(localSchedule));
    for (let day = 0; day <= 6; day++) {
      for (let hour = 0; hour <= 23; hour++) {
        newSchedule[day][hour] = false;
      }
    }
    setLocalSchedule(newSchedule);
  };

  React.useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const getActiveCount = () => {
    let count = 0;
    for (let day = 0; day <= 6; day++) {
      for (let hour = 0; hour <= 23; hour++) {
        if (localSchedule[day][hour]) count++;
      }
    }
    return count;
  };

  return (
    <div className="agent-setting-card">
      <div className="setting-header">
        <h3 className="agent-name">📅 Weekly Schedule</h3>
        <div className="setting-status">
          <span className="status-badge">
            {schedule ? `${getActiveCount()}/168 hours active` : 'Not Set'}
          </span>
        </div>
      </div>

      <div className="setting-field">
        <label className="setting-label">
          📅 Weekly Schedule Configuration
          <span className="field-help">Click hours to toggle, or click and drag for multi-selection</span>
        </label>

        <div className="schedule-controls">
          <button
            onClick={handleSetWeekdaySchedule}
            className="btn btn-secondary"
            disabled={updating}
            style={{ marginRight: '10px' }}
          >
            Set Default (Weekdays 6pm-9am, Weekends 24h)
          </button>
          <button
            onClick={handleClearSchedule}
            className="btn btn-secondary"
            disabled={updating}
          >
            Clear All
          </button>
        </div>

        <div className="schedule-grid-container" style={{ marginTop: '15px' }}>
          <div className="schedule-grid">
            {/* Header row with hour labels */}
            <div className="schedule-header">
              <div className="day-label"></div>
              {hourLabels.map((hour, index) => (
                <div key={index} className="hour-label" title={hour}>
                  {index % 6 === 0 ? hour : ''}
                </div>
              ))}
            </div>

            {/* Schedule rows */}
            {dayNames.map((dayName, dayIndex) => (
              <div key={dayIndex} className="schedule-row">
                <div className="day-label" title={dayName}>
                  {dayName.substring(0, 3)}
                </div>
                {hourLabels.map((_, hourIndex) => (
                  <div
                    key={`${dayIndex}-${hourIndex}`}
                    className={`schedule-cell ${localSchedule[dayIndex][hourIndex] ? 'active' : 'inactive'}`}
                    onMouseDown={() => handleCellMouseDown(dayIndex, hourIndex)}
                    onMouseEnter={() => handleMouseEnter(dayIndex, hourIndex)}
                    title={`${dayName} ${hourLabels[hourIndex]} - ${localSchedule[dayIndex][hourIndex] ? 'Active' : 'Inactive'}`}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="schedule-legend" style={{ marginTop: '10px' }}>
          <span style={{ marginRight: '20px' }}>
            <span className="legend-cell active" style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#4CAF50', marginRight: '5px' }}></span>
            Active Hours
          </span>
          <span>
            <span className="legend-cell inactive" style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#f0f0f0', marginRight: '5px' }}></span>
            Inactive Hours
          </span>
        </div>

        <small className="field-help" style={{ marginTop: '10px', display: 'block' }}>
          <strong>📋 Default Schedule:</strong> Monday-Friday: 6pm-9am next day | Saturday-Sunday: 24 hours | Total: 126 hours/week<br />
          <strong>🖱️ Controls:</strong> Single click to toggle hour | Click and drag to select multiple hours | Use buttons for quick presets
        </small>
      </div>

      <div className="setting-actions">
        <button
          onClick={handleSave}
          disabled={updating}
          className="btn btn-primary"
        >
          {updating ? 'Saving...' : 'Save Schedule'}
        </button>
      </div>
    </div>
  );
};

const ArchivedSessions: React.FC<{ onRestore: () => void }> = ({ onRestore }) => {
  const [archivedEntities, setArchivedEntities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState<{ id: string; name: string; phone: string } | null>(null);
  const [phoneNumberInput, setPhoneNumberInput] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadArchivedEntities();
  }, []);

  const loadArchivedEntities = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await orchestratorApi.getArchivedEntities();
      if (result.success) {
        setArchivedEntities(result.entities || []);
      } else {
        setError(result.error || 'Failed to load archived sessions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id: string, name: string) => {
    if (!window.confirm(`Restore "${name}"?\n\nThis will make the session available again in the active sessions dropdown.`)) {
      return;
    }

    setRestoring(id);
    try {
      const result = await orchestratorApi.restoreEntity(id);
      if (result.success) {
        // Remove from archived list
        setArchivedEntities(prev => prev.filter(e => e.id !== id));
        // Notify parent to reload active entities
        onRestore();
        alert(`✅ "${name}" has been restored successfully!`);
      } else {
        alert(`❌ Failed to restore: ${result.error}`);
      }
    } catch (err) {
      alert(`❌ Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setRestoring(null);
    }
  };

  const openDeleteModal = (id: string, name: string, phone: string) => {
    setEntityToDelete({ id, name, phone });
    setPhoneNumberInput('');
    setCopied(false);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setEntityToDelete(null);
    setPhoneNumberInput('');
    setCopied(false);
  };

  const copyPhoneNumber = async () => {
    if (entityToDelete) {
      try {
        await navigator.clipboard.writeText(entityToDelete.phone);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const confirmPermanentDelete = async () => {
    if (!entityToDelete) return;

    // Validate phone number input
    if (phoneNumberInput !== entityToDelete.phone) {
      alert('❌ Phone number does not match. Deletion cancelled.');
      return;
    }

    closeDeleteModal();
    setDeleting(entityToDelete.id);

    try {
      const result = await orchestratorApi.permanentlyDeleteEntity(entityToDelete.id);
      if (result.success) {
        // Remove from archived list
        setArchivedEntities(prev => prev.filter(e => e.id !== entityToDelete.id));

        // Show success with stats
        const stats = result.stats;
        alert(
          `✅ "${entityToDelete.name}" has been permanently deleted.\n\n` +
          `Deleted:\n` +
          `• ${stats?.messages || 0} messages\n` +
          `• ${stats?.chats || 0} chats\n` +
          `• ${stats?.recipients || 0} broadcast recipients\n` +
          `• ${stats?.campaigns || 0} broadcast campaigns`
        );
      } else {
        alert(`❌ Failed to permanently delete: ${result.error}`);
      }
    } catch (err) {
      alert(`❌ Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading archived sessions...</div>;
  }

  if (error) {
    return (
      <div className="error-message" style={{ padding: '20px', color: '#d32f2f' }}>
        ❌ {error}
        <button
          onClick={loadArchivedEntities}
          style={{ marginLeft: '10px', padding: '5px 10px' }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (archivedEntities.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        📭 No archived sessions found.
      </div>
    );
  }

  return (
    <div className="archived-entities-list">
      <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
        ⚠️ <strong>Warning:</strong> Permanent delete will remove ALL data including messages, chats, and campaigns. This action cannot be undone.
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ddd' }}>
            <th style={{ padding: '10px', textAlign: 'left' }}>Name</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Phone</th>
            <th style={{ padding: '10px', textAlign: 'left' }}>Archived Date</th>
            <th style={{ padding: '10px', textAlign: 'center' }}>Restore</th>
            <th style={{ padding: '10px', textAlign: 'center' }}>Permanent Delete</th>
          </tr>
        </thead>
        <tbody>
          {archivedEntities.map((entity) => (
            <tr key={entity.id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '10px' }}>{entity.name}</td>
              <td style={{ padding: '10px' }}>{entity.phone}</td>
              <td style={{ padding: '10px' }}>
                {new Date(entity.updated_at).toLocaleString()}
              </td>
              <td style={{ padding: '10px', textAlign: 'center' }}>
                <button
                  onClick={() => handleRestore(entity.id, entity.name)}
                  disabled={restoring === entity.id || deleting === entity.id}
                  style={{
                    padding: '5px 15px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (restoring === entity.id || deleting === entity.id) ? 'not-allowed' : 'pointer',
                    opacity: (restoring === entity.id || deleting === entity.id) ? 0.6 : 1
                  }}
                >
                  {restoring === entity.id ? '⏳ Restoring...' : '♻️ Restore'}
                </button>
              </td>
              <td style={{ padding: '10px', textAlign: 'center' }}>
                <button
                  onClick={() => openDeleteModal(entity.id, entity.name, entity.phone)}
                  disabled={restoring === entity.id || deleting === entity.id}
                  style={{
                    padding: '5px 15px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (restoring === entity.id || deleting === entity.id) ? 'not-allowed' : 'pointer',
                    opacity: (restoring === entity.id || deleting === entity.id) ? 0.6 : 1
                  }}
                >
                  {deleting === entity.id ? '⏳ Deleting...' : '🗑️ Delete Forever'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && entityToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#d32f2f', fontSize: '24px' }}>
              ⚠️ Permanent Delete Warning
            </h2>

            <p style={{ marginBottom: '20px', color: '#333', lineHeight: '1.6' }}>
              This action will perform a <strong>permanent deletion</strong> of the session:
            </p>

            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '15px',
              borderRadius: '4px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                  Phone Number:
                </div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: 'monospace', color: '#333' }}>
                  {entityToDelete.phone}
                </div>
              </div>
              <button
                onClick={copyPhoneNumber}
                style={{
                  padding: '8px 16px',
                  backgroundColor: copied ? '#4CAF50' : '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'background-color 0.3s'
                }}
              >
                {copied ? '✓ Copied!' : '📋 Copy'}
              </button>
            </div>

            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <strong>This will permanently delete:</strong>
              <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
                <li>All chat messages</li>
                <li>All chat history</li>
                <li>All broadcast campaigns</li>
                <li>All broadcast recipients</li>
                <li>The entity itself</li>
              </ul>
              <p style={{ margin: '10px 0 0 0', fontWeight: 'bold', color: '#d32f2f' }}>
                THIS CANNOT BE UNDONE!
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                To confirm deletion, enter the phone number below:
              </label>
              <input
                type="text"
                value={phoneNumberInput}
                onChange={(e) => setPhoneNumberInput(e.target.value)}
                placeholder="Enter phone number to confirm"
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '2px solid #ddd',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeDeleteModal}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#9e9e9e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmPermanentDelete}
                disabled={phoneNumberInput !== entityToDelete.phone}
                style={{
                  padding: '10px 20px',
                  backgroundColor: phoneNumberInput === entityToDelete.phone ? '#d32f2f' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: phoneNumberInput === entityToDelete.phone ? 'pointer' : 'not-allowed',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const Admin: React.FC = () => {
  // 🏢 MULTI-TENANT: tenant always comes from the authenticated JWT, never hardcoded
  const authTenantId = useTenantId();

  // Multi-entity state
  const [entities, setEntities] = useState<WhatsAppEntity[]>([]);
  // Initialize from localStorage with fallback to null
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('selectedEntityId');
    }
    return null;
  });
  const [showAddEntityModal, setShowAddEntityModal] = useState(false);
  const [editingEntity, setEditingEntity] = useState<any>(null);

  // Legacy state (will be moved to entity-specific state)
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  // Initialize activeTab from localStorage or default to "agents"
  const [activeTab, setActiveTab] = useState<"agents" | "create-agent" | "entity-assignments" | "whatsapp" | "system-prompt" | "knowledge" | "learning" | "urls" | "rag" | "curated-qa" | "tokens" | "translation" | "llm-config" | "monitoring" | "settings" | "google-calendar" | "zoho-crm" | "zoho-response-control" | "memory" | "tools" | "handoff" | "management-messages" | "testing" | "ai-models" | "archived-sessions" | "guardrails" | "service-config" | "tenant-topics" | "tenant-config" | "chat-routing-rules" | "dead-letter" | "entity-management">(() => {
    const savedTab = localStorage.getItem('admin-active-tab');
    const validTabs = ["agents", "create-agent", "entity-assignments", "whatsapp", "system-prompt", "knowledge", "learning", "urls", "rag", "curated-qa", "tokens", "translation", "llm-config", "monitoring", "settings", "google-calendar", "zoho-crm", "zoho-response-control", "memory", "tools", "handoff", "testing", "ai-models", "archived-sessions", "guardrails", "service-config", "tenant-topics", "tenant-config", "chat-routing-rules", "dead-letter", "entity-management"];
    if (savedTab && validTabs.includes(savedTab)) {
      // console.log('[Admin] Initializing activeTab from localStorage:', savedTab);
      return savedTab as any;
    }
    // console.log('[Admin] No saved tab, defaulting to "agents"');
    return "agents";
  });

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    // console.log('[Admin] activeTab changed to:', activeTab, '- saving to localStorage');
    localStorage.setItem('admin-active-tab', activeTab);
  }, [activeTab]);

  // Check URL parameter for tab selection on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      // Validate it's a valid tab name
      const validTabs = ["agents", "create-agent", "entity-assignments", "whatsapp", "system-prompt", "knowledge", "learning", "urls", "rag", "curated-qa", "tokens", "translation", "llm-config", "monitoring", "settings", "google-calendar", "zoho-crm", "memory", "tools", "handoff", "testing", "ai-models", "archived-sessions", "service-config", "tenant-topics", "tenant-config", "chat-routing-rules", "dead-letter", "entity-management"];
      if (validTabs.includes(tabParam)) {
        setActiveTab(tabParam as any);
        // Clean URL to remove the parameter
        window.history.replaceState({}, '', '/admin');
      }
    }
  }, []);

  // Initialize collapsible section state from localStorage or defaults
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>(() => {
    const saved = localStorage.getItem('admin-expanded-sections');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { aiChatAgent: true };
      }
    }
    return { aiChatAgent: true }; // AI Agents section expanded by default
  });

  // Service configuration state
  const [serviceConfigs, setServiceConfigs] = useState<Array<{
    id: number;
    tenant_id: string;
    service_name: string;
    service_code: string;
    keywords: string[];
    description: string;
    enabled: boolean;
    required_keys?: string[];
  }>>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [editingServiceKeys, setEditingServiceKeys] = useState<{ [serviceCode: string]: string[] }>({});

  // Save expandedSections to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('admin-expanded-sections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  // New state for WhatsApp connection
  const [whatsAppStatus, setWhatsAppStatus] = useState<string>("DISCONNECTED");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [isWhatsAppLoading, setIsWhatsAppLoading] = useState(false);

  // Emergency Cleanup History state
  const [emergencyCleanupEvents, setEmergencyCleanupEvents] = useState<any[]>([]);
  const [cleanupEventsLoading, setCleanupEventsLoading] = useState(false);

  // QR code expiration timer (2 minutes to match backend auto-deletion)
  const qrExpirationTimerRef = useRef<NodeJS.Timeout | null>(null);

  // URL management state
  const [scrapedUrls, setScrapedUrls] = useState<ScrapedUrl[]>([]);
  const [scrapingStats, setScrapingStats] = useState<ScrapingStats | null>(null);
  const [urlsLoading, setUrlsLoading] = useState(false);
  const [manualScrapingRunning, setManualScrapingRunning] = useState(false);

  // Knowledge Base state
  const [kbDocuments, setKbDocuments] = useState<any[]>([]);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbUploading, setKbUploading] = useState(false);
  const [kbStats, setKbStats] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadMetadata, setUploadMetadata] = useState({
    description: '',
    source: ''
  });
  const [kbSearchQuery, setKbSearchQuery] = useState('');
  const [viewingDocument, setViewingDocument] = useState<{
    id: number;
    filename: string;
    content: string;
    mime_type: string;
    description?: string;
    source?: string;
  } | null>(null);
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedSource, setEditedSource] = useState('');

  // ESC key handler for closing document viewer modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && viewingDocument) {
        if (isEditingDocument) {
          // If in edit mode, first cancel edit
          handleCancelEdit();
        } else {
          // If in view mode, close modal
          setViewingDocument(null);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [viewingDocument, isEditingDocument]);

  // Model settings modal state
  const [showModelSettings, setShowModelSettings] = useState(false);

  // New state for scraping schedule (legacy - kept for backwards compatibility)
  // const [scrapingSchedule, setScrapingSchedule] = useState<ScrapingSchedule | null>(null);
  const [urlOnlyScrapingRunning, setUrlOnlyScrapingRunning] = useState(false);
  const [selectedUrlData, setSelectedUrlData] = useState<any>(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [newUrl, setNewUrl] = useState({ url: '', title: '', description: '', category: 'citizenship', source: 'ica', priority: 1 });

  // State for schedule settings (user-friendly UI)
  const [scheduleSettings, setScheduleSettings] = useState({
    frequency: 'daily',
    time_of_day: '03:00',
    day_of_week: 1,
    day_of_month: 1,
    notification_email: 'tien.ho@eh-immigration.com',
    notify_on_changes: true,
    notify_on_failures: true,
    next_run_at: null as string | null
  });

  // QR Code timestamp state
  const [qrTimestamp, setQrTimestamp] = useState<string | null>(null);
  const [qrAgeStatus, setQrAgeStatus] = useState<string>("unknown");
  const [qrAgeMinutes, setQrAgeMinutes] = useState<number>(0);

  // QR Code comparison state
  const [qrCodeContent, setQrCodeContent] = useState<string | null>(null);
  const [previousQrCodeContent, setPreviousQrCodeContent] = useState<
    string | null
  >(null);
  const [qrCodeImageKey, setQrCodeImageKey] = useState<number>(Date.now()); // Force image refresh

  // Rate limiting state with localStorage persistence
  const [qrRateLimit, setQrRateLimit] = useState<{
    limited: boolean;
    remainingTime: number;
    message: string;
    expiresAt?: number; // When the rate limit expires
  }>(() => {
    // Try to load from localStorage on component mount
    try {
      const saved = localStorage.getItem('qrRateLimit');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if the rate limit has expired
        if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
          localStorage.removeItem('qrRateLimit');
          return { limited: false, remainingTime: 0, message: '' };
        }
        return parsed;
      }
    } catch (e) {
      console.error('[QR] Failed to load rate limit from localStorage:', e);
    }
    return { limited: false, remainingTime: 0, message: '' };
  });
  const [countdown, setCountdown] = useState<string>('');
  const [isDecodingQr, setIsDecodingQr] = useState<boolean>(false);
  const [lastQrRefreshTime, setLastQrRefreshTime] = useState<number | null>(
    null,
  ); // Track when QR was last refreshed

  // QR Code validation state
  const [qrValidationResult, setQrValidationResult] = useState<{
    isValid: boolean;
    isReadable: boolean;
    content?: string;
    formatValid: boolean;
    freshness?: string;
    recommendations?: string[];
  } | null>(null);

  // Chat sync state
  const [syncingChats, setSyncingChats] = useState(false);
  const [lastSyncStats, setLastSyncStats] = useState<{
    total: number;
    synced: number;
    errors: number;
    totalMessages?: number;
    status: string;
    message?: string;
  } | null>(null);
  const syncJobTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Campaign progress polling intervals - ref-based to prevent memory leaks
  const campaignIntervalsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  // Lead agent config state
  const [leadConfig, setLeadConfig] = useState<{
    enabled: boolean;
    config: { targetChat: string };
  } | null>(null);
  const [updatingLeadConfig, setUpdatingLeadConfig] = useState(false);

  // Agent settings state
  const [agentSettings, setAgentSettings] = useState<AgentSettings[]>([]);
  const [loadingAgentSettings, setLoadingAgentSettings] = useState(false);
  const [updatingAgentSettings, setUpdatingAgentSettings] = useState<string | null>(null);

  // Broadcast state
  const [broadcastCampaigns, setBroadcastCampaigns] = useState<any[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    message: '',
    source_type: 'csv' as 'csv' | 'zoho'
  });
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [loadingBroadcast, setLoadingBroadcast] = useState(false);
  const [campaignProgress, setCampaignProgress] = useState<{ [key: string]: any }>({});

  // Calendar scheduling state
  const [calendarSchedule, setCalendarSchedule] = useState<CalendarSchedule | null>(null);
  const [loadingCalendarSchedule, setLoadingCalendarSchedule] = useState(false);
  const [updatingCalendarSchedule, setUpdatingCalendarSchedule] = useState(false);

  // Unanswered Messages scheduling state
  const [unansweredSchedule, setUnansweredSchedule] = useState<UnansweredMessagesSchedule | null>(null);
  const [loadingUnansweredSchedule, setLoadingUnansweredSchedule] = useState(false);
  const [updatingUnansweredSchedule, setUpdatingUnansweredSchedule] = useState(false);
  const [showUnansweredTestModal, setShowUnansweredTestModal] = useState(false);
  const [unansweredTestResults, setUnansweredTestResults] = useState<any[] | null>(null);
  const [unanswerTestLoading, setUnansweredTestLoading] = useState(false);
  const [maxChatsInput, setMaxChatsInput] = useState(100);

  // Qualifier Agent Schedule State
  const [qualifierSchedule, setQualifierSchedule] = useState<QualifierSchedule | null>(null);
  const [loadingQualifierSchedule, setLoadingQualifierSchedule] = useState(false);
  const [updatingQualifierSchedule, setUpdatingQualifierSchedule] = useState(false);

  // Translation settings state
  const [translationSettings, setTranslationSettings] = useState<any>(null);

  // Persist selected entity to localStorage
  useEffect(() => {
    if (selectedEntityId) {
      localStorage.setItem('selectedEntityId', selectedEntityId);
    }
  }, [selectedEntityId]);

  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [loadingTranslationSettings, setLoadingTranslationSettings] = useState(false);
  const [updatingTranslationSettings, setUpdatingTranslationSettings] = useState(false);
  const [runningBackfill, setRunningBackfill] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState<{
    translated: number;
    skipped: number;
    failed: number;
    remaining: number;
  } | null>(null);

  // Learning System state
  const [learningStatistics, setLearningStatistics] = useState<any>(null);
  const [learningRuns, setLearningRuns] = useState<any[]>([]);
  const [learningConfig, setLearningConfig] = useState<any>(null);
  const [qaPairs, setQaPairs] = useState<any[]>([]);
  const [loadingLearning, setLoadingLearning] = useState(false);
  const [runningLearning, setRunningLearning] = useState(false);
  const [showQAPairs, setShowQAPairs] = useState(false);
  const [qaPairFilter, setQaPairFilter] = useState<'all' | 'auto_approved' | 'needs_review' | 'rejected'>('all');

  // Manual Review state
  const [rejectedQAPairs, setRejectedQAPairs] = useState<any[]>([]);
  const [loadingRejected, setLoadingRejected] = useState(false);
  const [selectedQAPairs, setSelectedQAPairs] = useState<Set<number>>(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [reviewerName, setReviewerName] = useState('');
  const [manualReviewStats, setManualReviewStats] = useState<any>(null);
  const [serviceAreaSelections, setServiceAreaSelections] = useState<{ [key: number]: { immigration: boolean, workPass: boolean } }>({});

  // Needs Review state (for pairs that need manual review)
  const [needsReviewQAPairs, setNeedsReviewQAPairs] = useState<any[]>([]);
  const [loadingNeedsReview, setLoadingNeedsReview] = useState(false);
  const [selectedNeedsReviewPairs, setSelectedNeedsReviewPairs] = useState<Set<number>>(new Set());
  const [bulkProcessingNeedsReview, setBulkProcessingNeedsReview] = useState(false);
  const [needsReviewServiceAreaSelections, setNeedsReviewServiceAreaSelections] = useState<{ [key: number]: { immigration: boolean, workPass: boolean } }>({});

  // AI Configuration state
  const [aiModels, setAiModels] = useState<any[]>([]);
  const [activeAiModel, setActiveAiModel] = useState<any>(null);
  // const [promptTemplates, setPromptTemplates] = useState<any[]>([]);
  const [activePromptTemplates, setActivePromptTemplates] = useState<any>({});
  // const [modelProviders, setModelProviders] = useState<any>({});
  const [loadingAiConfig, setLoadingAiConfig] = useState(false);
  const [showAiConfig, setShowAiConfig] = useState(false);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<any>(null);
  const [promptContent, setPromptContent] = useState('');
  const [versionDescription, setVersionDescription] = useState('');
  const [newModelForm, setNewModelForm] = useState({
    config_name: '',
    model_provider: 'gemini',
    model_name: '',
    api_endpoint: '',
    temperature: 0.1,
    description: ''
  });

  // Curated QA Library state
  const [curatedQAPairs, setCuratedQAPairs] = useState<any[]>([]);
  const [curatedQALoading, setCuratedQALoading] = useState(false);
  const [curatedQASaving, setCuratedQASaving] = useState(false);
  const [curatedQAStats, setCuratedQAStats] = useState<any>(null);
  const [curatedQACategories, setCuratedQACategories] = useState<any[]>([]);
  const [curatedQASearchResults, setCuratedQASearchResults] = useState<any>(null);
  const [curatedQASearchQuery, setCuratedQASearchQuery] = useState('');
  const [curatedQACategoryFilter, setCuratedQACategoryFilter] = useState('');
  const [showCreateQAModal, setShowCreateQAModal] = useState(false);
  const [editingQAPair, setEditingQAPair] = useState<any>(null);
  const [curatedQAForm, setCuratedQAForm] = useState({
    question: '',
    answer: '',
    category: 'General',
    subcategory: '',
    tags: [] as string[],
    keywords: [] as string[],
    priority: 50,
    quality_score: 0.8,
    language: 'english'
  });
  // Settings reserved for future UI enhancement
  const [_curatedQASettings, _setCuratedQASettings] = useState<any>(null);
  const [tagInput, setTagInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');

  // Tenant Topics Management state
  const [tenantTopics, setTenantTopics] = useState<any[]>([]);
  const [tenantTopicsLoading, setTenantTopicsLoading] = useState(false);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState<any>(null);
  const [topicForm, setTopicForm] = useState({
    topic_name: '',
    topic_patterns: '',
    is_active: true,
    priority: 0
  });
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState<any>(null);

  // 🏢 Tenant Configuration state
  const [tenantConfig, setTenantConfig] = useState<any>(null);
  const [tenantDomains, setTenantDomains] = useState<any[]>([]);
  const [chatRoutingRuleTypes, setChatRoutingRuleTypes] = useState<any[]>([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [showChatRoutingRuleModal, setShowChatRoutingRuleModal] = useState(false);
  const [editingChatRoutingRuleId, setEditingChatRoutingRuleId] = useState<string | null>(null);
  const [chatRoutingRuleForm, setChatRoutingRuleForm] = useState({
    id: '',
    name: '',
    actionType: 'management_message',
    enabled: true,
    priority: 10,
    responseMessage: '',
    managementGroupName: '',
    triggerText: ''
  });
  const [domainTestQuery, setDomainTestQuery] = useState('');
  const [domainTestResult, setDomainTestResult] = useState<any>(null);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [editingDomain, setEditingDomain] = useState<any>(null);
  const [domainForm, setDomainForm] = useState({
    domain_key: '',
    domain_name: '',
    description: '',
    confidence_threshold: 0.70,
    priority: 0,
    system_prompt_addition: '',
    is_active: true
  });

  useEffect(() => {
    loadEntities();
    loadFeatureFlags();
    loadLeadConfig();
    loadAgentSettings();
    loadCalendarSchedule();
    loadUnansweredSchedule();
    loadQualifierSchedule();
    loadLearningData();
    loadAiConfiguration();
    loadTranslationSettings();
  }, []);

  // Separate useEffect for WhatsApp status that waits for entities to load
  useEffect(() => {
    if (entities.length > 0 && selectedEntityId) {
      fetchWhatsAppStatus();
      const statusInterval = setInterval(fetchWhatsAppStatus, 5000); // Poll every 5 seconds
      return () => clearInterval(statusInterval);
    }
  }, [entities, selectedEntityId]);

  // ⚠️ CRITICAL: Check rate limit status ONLY (does NOT request new QR code)
  // This function only checks if there's an active rate limit and displays it to the user.
  // It does NOT trigger QR code generation. User must explicitly click "Refresh QR Code" button.
  // DO NOT modify this to automatically fetch QR codes - only user action should request QR.
  const checkRateLimitStatus = async (entityId: string) => {
    try {
      // console.log('[QR] Checking rate limit status for entity:', entityId);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/qr-code/rate-limit/${entityId}`, {
        headers: {
          ...authHeaders,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      // console.log('[QR] Rate limit response:', { status: response.status, data });
      // console.log('[QR] Checking condition - data.ok:', data.ok, 'data.rateLimit:', data.rateLimit);

      if (data.ok && data.rateLimit) {
        const remainingTime = data.rateLimit.remainingTime;
        const expiresAt = Date.now() + (remainingTime * 1000);

        // console.log('[QR] Rate limit active on load:', {
        //   limited: true,
        //   remainingTime,
        //   expiresAt: new Date(expiresAt).toISOString()
        // });

        const newState = {
          limited: true,
          remainingTime,
          message: `Rate limit active. Please wait before refreshing.`,
          expiresAt
        };

        localStorage.setItem('qrRateLimit', JSON.stringify(newState));
        setQrRateLimit(newState);
      } else {
        // console.log('[QR] No rate limit active - data.ok:', data.ok, 'data.rateLimit:', data.rateLimit);
      }
    } catch (error) {
      // Only log if not a network connectivity issue
      if (!(error instanceof TypeError && error.message.includes('NetworkError'))) {
        console.error('[QR] Failed to check rate limit status:', error);
      }
    }
  };

  // ⚠️ CRITICAL: Auto-check rate limit status when entity changes (STATUS CHECK ONLY)
  // This effect ONLY checks if there's an active rate limit - it does NOT fetch QR codes.
  // DO NOT add automatic QR code fetching here or anywhere else.
  // QR codes must ONLY be fetched when user explicitly clicks "Refresh QR Code" button.
  // User requirement: "only the user can request for refresh of QR code"
  useEffect(() => {
    // console.log('[QR] Rate limit check effect triggered:', { selectedEntityId, entitiesCount: entities.length });
    if (selectedEntityId && entities.length > 0) {
      const selectedEntity = entities.find((e) => e.id === selectedEntityId);
      // console.log('[QR] Found selected entity:', selectedEntity);
      const whatsappEntityId = selectedEntity ? getWhatsAppEntityId(selectedEntity) : undefined;
      // console.log('[QR] WhatsApp Entity ID for rate limit:', whatsappEntityId);
      if (whatsappEntityId) {
        checkRateLimitStatus(whatsappEntityId); // Status check only - no QR fetch
      }
    }
  }, [selectedEntityId, entities]); // Add entities to dependency array

  // Countdown timer for QR rate limit
  useEffect(() => {
    if (qrRateLimit.limited) {
      // Calculate remaining time based on expiresAt
      const calculateRemainingTime = () => {
        if (qrRateLimit.expiresAt) {
          return Math.max(0, Math.ceil((qrRateLimit.expiresAt - Date.now()) / 1000));
        }
        return qrRateLimit.remainingTime;
      };

      const initialRemainingTime = calculateRemainingTime();

      if (initialRemainingTime <= 0) {
        // Rate limit expired
        localStorage.removeItem('qrRateLimit');
        setQrRateLimit({ limited: false, remainingTime: 0, message: '' });
        setCountdown('');
        return;
      }

      // Set initial countdown
      const minutes = Math.floor(initialRemainingTime / 60);
      const seconds = initialRemainingTime % 60;
      setCountdown(`${minutes}:${seconds.toString().padStart(2, '0')}`);

      const timer = setInterval(() => {
        const newRemainingTime = calculateRemainingTime();

        if (newRemainingTime <= 0) {
          // Rate limit expired
          localStorage.removeItem('qrRateLimit');
          setQrRateLimit({ limited: false, remainingTime: 0, message: '' });
          setCountdown('');
          clearInterval(timer);
        } else {
          // Update countdown
          const minutes = Math.floor(newRemainingTime / 60);
          const seconds = newRemainingTime % 60;
          setCountdown(`${minutes}:${seconds.toString().padStart(2, '0')}`);
          // Update state but don't change expiresAt
          setQrRateLimit(prev => ({ ...prev, remainingTime: newRemainingTime }));
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [qrRateLimit.limited, qrRateLimit.expiresAt]);

  // Cleanup QR expiration timer on component unmount
  useEffect(() => {
    return () => {
      cancelQrExpirationTimer();
    };
  }, []);

  // Load Curated QA data when tab is activated
  useEffect(() => {
    if (activeTab === 'curated-qa' && curatedQAPairs.length === 0) {
      loadCuratedQAData();
    }
  }, [activeTab]);

  // Load Tenant Topics when tab is activated
  useEffect(() => {
    if (activeTab === 'tenant-topics' && tenantTopics.length === 0) {
      loadTenantTopics();
    }
  }, [activeTab]);

  // Load Tenant Config when tab is activated
  useEffect(() => {
    if ((activeTab === 'tenant-config' || activeTab === 'chat-routing-rules') && !tenantConfig) {
      loadTenantConfig();
    }
  }, [activeTab]);

  const loadLearningData = async () => {
    setLoadingLearning(true);
    try {
      // Load learning status
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const statusResponse = await fetch('/api/learning/status', { headers: authHeaders });
      let lastProcessingDate = null;
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setLearningRuns(statusData.status?.recentRuns || []);
        setLearningConfig(statusData.status?.config);
        lastProcessingDate = statusData.status?.stats?.last_processing_date;
      }

      // Load detailed statistics
      const statsResponse = await fetch('/api/learning/statistics', { headers: authHeaders });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        // Merge statistics with last processing date from status
        setLearningStatistics({
          ...statsData.statistics,
          last_processing_date: lastProcessingDate
        });
      }
    } catch (error) {
      console.error('Failed to load learning data:', error);
    } finally {
      setLoadingLearning(false);
    }
  };

  const loadQAPairs = async (status: string = 'all') => {
    try {
      const url = status === 'all'
        ? '/api/learning/qa-pairs?limit=100'
        : `/api/learning/qa-pairs?limit=100&status=${status}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setQaPairs(data.qaPairs || []);
      }
    } catch (error) {
      console.error('Failed to load Q&A pairs:', error);
    }
  };

  const startManualLearningRun = async () => {
    setRunningLearning(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/learning/run', {
        method: 'POST',
        headers: authHeaders
      });
      if (response.ok) {
        await response.json();
        // console.log('Learning run result:', result);
        // Refresh data after run
        setTimeout(() => {
          loadLearningData();
        }, 1000);
      } else {
        const error = await response.json();
        console.error('Learning run failed:', error);
      }
    } catch (error) {
      console.error('Failed to start learning run:', error);
    } finally {
      setRunningLearning(false);
    }
  };

  // Manual Review Functions
  const loadRejectedQAPairs = async () => {
    setLoadingRejected(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/learning/rejected-qa-pairs?limit=100', { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setRejectedQAPairs(data.rejectedPairs || []);

        // Also load manual review stats
        const statsResponse = await fetch('/api/learning/manual-review-stats', { headers: authHeaders });
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setManualReviewStats(statsData.stats);
        }
      } else {
        console.error('Failed to load rejected Q&A pairs');
      }
    } catch (error) {
      console.error('Error loading rejected Q&A pairs:', error);
    } finally {
      setLoadingRejected(false);
    }
  };

  const approveIndividualQAPair = async (qaPairId: number) => {
    if (!reviewerName.trim()) {
      alert('Please enter your reviewer name before approving Q&A pairs');
      return;
    }

    setBulkApproving(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/learning/manual-review/${qaPairId}`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          manual_review_status: 'approved',
          manual_reviewer: reviewerName.trim(),
          manual_review_notes: 'Individual approval from manual review interface',
          override_ai_decision: true,
          service_area_classification: {
            immigration_ica: serviceAreaSelections[qaPairId]?.immigration || false,
            work_passes_mom: serviceAreaSelections[qaPairId]?.workPass || false
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Individual approval successful:', result);

        // Remove the approved pair from the rejected list
        setRejectedQAPairs(prev => prev.filter(qa => qa.id !== qaPairId));
        setSelectedQAPairs(prev => {
          const newSet = new Set(prev);
          newSet.delete(qaPairId);
          return newSet;
        });

        // Refresh stats
        loadRejectedQAPairs();
      } else {
        const error = await response.json();
        console.error('Failed to approve Q&A pair:', error);
        alert(`Failed to approve Q&A pair: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving Q&A pair:', error);
      alert('Error approving Q&A pair. Please try again.');
    } finally {
      setBulkApproving(false);
    }
  };

  const bulkApproveSelected = async () => {
    if (selectedQAPairs.size === 0) {
      alert('Please select at least one Q&A pair to approve');
      return;
    }

    if (!reviewerName.trim()) {
      alert('Please enter your reviewer name before approving Q&A pairs');
      return;
    }

    setBulkApproving(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const qaPairIds = Array.from(selectedQAPairs);
      const response = await fetch('/api/learning/bulk-approve', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          qaPairIds,
          manual_reviewer: reviewerName.trim(),
          manual_review_notes: `Bulk approval of ${qaPairIds.length} Q&A pairs from manual review interface`,
          service_area_classifications: Object.fromEntries(
            qaPairIds.map(id => [
              id,
              {
                immigration_ica: serviceAreaSelections[id]?.immigration || false,
                work_passes_mom: serviceAreaSelections[id]?.workPass || false
              }
            ])
          )
        })
      });

      if (response.ok) {
        const result = await response.json();
        // console.log('Bulk approval result:', result);

        // Remove approved pairs from the rejected list
        setRejectedQAPairs(prev => prev.filter(qa => !selectedQAPairs.has(qa.id)));
        setSelectedQAPairs(new Set());

        // Show success message
        const successCount = result.summary?.successful || 0;
        const failedCount = result.summary?.failed || 0;

        if (failedCount > 0) {
          alert(`Bulk approval completed: ${successCount} successful, ${failedCount} failed. Check console for details.`);
        } else {
          alert(`Successfully approved ${successCount} Q&A pairs!`);
        }

        // Refresh stats
        loadRejectedQAPairs();
      } else {
        const error = await response.json();
        console.error('Failed to bulk approve Q&A pairs:', error);
        alert(`Failed to bulk approve Q&A pairs: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error bulk approving Q&A pairs:', error);
      alert('Error bulk approving Q&A pairs. Please try again.');
    } finally {
      setBulkApproving(false);
    }
  };

  // Needs Review Functions
  const loadNeedsReviewQAPairs = async () => {
    setLoadingNeedsReview(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/learning/needs-review-qa-pairs?limit=100', { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setNeedsReviewQAPairs(data.needsReviewPairs || []);

        // Also load manual review stats
        const statsResponse = await fetch('/api/learning/manual-review-stats', { headers: authHeaders });
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setManualReviewStats(statsData.stats);
        }
      } else {
        console.error('Failed to load needs review Q&A pairs');
      }
    } catch (error) {
      console.error('Error loading needs review Q&A pairs:', error);
    } finally {
      setLoadingNeedsReview(false);
    }
  };

  const approveNeedsReviewQAPair = async (qaPairId: number) => {
    if (!reviewerName.trim()) {
      alert('Please enter your reviewer name before approving Q&A pairs');
      return;
    }

    setBulkProcessingNeedsReview(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/learning/manual-review/${qaPairId}`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          manual_review_status: 'approved',
          manual_reviewer: reviewerName,
          manual_review_notes: 'Manual approval of needs_review Q&A pair',
          override_ai_decision: true
        }),
      });

      if (response.ok) {
        // Remove from selected if it was selected
        setSelectedNeedsReviewPairs(prevSet => {
          const newSet = new Set(prevSet);
          newSet.delete(qaPairId);
          return newSet;
        });

        // Refresh the list
        loadNeedsReviewQAPairs();
      } else {
        const error = await response.json();
        console.error('Failed to approve Q&A pair:', error);
        alert(`Failed to approve Q&A pair: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving Q&A pair:', error);
      alert('Error approving Q&A pair. Please try again.');
    } finally {
      setBulkProcessingNeedsReview(false);
    }
  };

  const rejectNeedsReviewQAPair = async (qaPairId: number) => {
    if (!reviewerName.trim()) {
      alert('Please enter your reviewer name before rejecting Q&A pairs');
      return;
    }

    setBulkProcessingNeedsReview(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/learning/manual-review/${qaPairId}`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          manual_review_status: 'rejected',
          manual_reviewer: reviewerName,
          manual_review_notes: 'Manual rejection of needs_review Q&A pair',
          override_ai_decision: false
        }),
      });

      if (response.ok) {
        // Remove from selected if it was selected
        setSelectedNeedsReviewPairs(prevSet => {
          const newSet = new Set(prevSet);
          newSet.delete(qaPairId);
          return newSet;
        });

        // Refresh the list
        loadNeedsReviewQAPairs();
      } else {
        const error = await response.json();
        console.error('Failed to reject Q&A pair:', error);
        alert(`Failed to reject Q&A pair: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error rejecting Q&A pair:', error);
      alert('Error rejecting Q&A pair. Please try again.');
    } finally {
      setBulkProcessingNeedsReview(false);
    }
  };

  const bulkApproveNeedsReviewSelected = async () => {
    if (selectedNeedsReviewPairs.size === 0) {
      alert('Please select at least one Q&A pair to approve');
      return;
    }

    if (!reviewerName.trim()) {
      alert('Please enter your reviewer name before bulk approving Q&A pairs');
      return;
    }

    setBulkProcessingNeedsReview(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/learning/bulk-approve', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          qaPairIds: Array.from(selectedNeedsReviewPairs),
          manual_reviewer: reviewerName,
          manual_review_notes: 'Bulk approval of needs_review Q&A pairs'
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setSelectedNeedsReviewPairs(new Set());

        const successCount = result.summary?.successful || 0;
        const failedCount = result.summary?.failed || 0;

        if (failedCount > 0) {
          alert(`Bulk approval completed: ${successCount} successful, ${failedCount} failed. Check console for details.`);
        } else {
          alert(`Successfully approved ${successCount} Q&A pairs!`);
        }

        // Refresh the list
        loadNeedsReviewQAPairs();
      } else {
        const error = await response.json();
        console.error('Failed to bulk approve Q&A pairs:', error);
        alert(`Failed to bulk approve Q&A pairs: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error bulk approving Q&A pairs:', error);
      alert('Error bulk approving Q&A pairs. Please try again.');
    } finally {
      setBulkProcessingNeedsReview(false);
    }
  };

  // AI Configuration Functions
  const loadAiConfiguration = async () => {
    setLoadingAiConfig(true);
    try {
      // Load AI models, active model, and model providers in parallel
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const [modelsResponse, activeModelResponse, providersResponse] = await Promise.all([
        fetch('/api/ai-config/models', { headers: authHeaders }),
        fetch('/api/ai-config/models/active', { headers: authHeaders }),
        fetch('/api/ai-config/model-providers', { headers: authHeaders })
      ]);

      if (modelsResponse.ok) {
        const modelsData = await modelsResponse.json();
        setAiModels(modelsData.models || []);
      }

      if (activeModelResponse.ok) {
        const activeModelData = await activeModelResponse.json();
        setActiveAiModel(activeModelData.activeModel);
      }

      if (providersResponse.ok) {
        // const providersData = await providersResponse.json();
        // setModelProviders(providersData.providers || {});
      }

      // Load prompt templates for different types
      const promptTypes = ['qa_extraction', 'relevance_validation'];
      const activePrompts: any = {};

      for (const type of promptTypes) {
        try {
          const authHeaders = await orchestratorApi.getAuthHeaders();
          const response = await fetch(`/api/ai-config/prompts/${type}/active`, {
            headers: authHeaders
          });
          if (response.ok) {
            const data = await response.json();
            activePrompts[type] = data.activePrompt;
          }
        } catch (error) {
          console.error(`Error loading ${type} prompt:`, error);
        }
      }

      setActivePromptTemplates(activePrompts);

    } catch (error) {
      console.error('Error loading AI configuration:', error);
    } finally {
      setLoadingAiConfig(false);
    }
  };

  const createNewModel = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/ai-config/models', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newModelForm)
      });

      if (response.ok) {
        await response.json();
        // console.log('Model created successfully:', result);
        alert('AI model configuration created successfully!');

        // Reset form and refresh models
        setNewModelForm({
          config_name: '',
          model_provider: 'gemini',
          model_name: '',
          api_endpoint: '',
          temperature: 0.1,
          description: ''
        });

        loadAiConfiguration();
      } else {
        const error = await response.json();
        console.error('Failed to create model:', error);
        alert(`Failed to create model: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating model:', error);
      alert('Error creating model. Please try again.');
    }
  };

  const activateModel = async (modelId: number) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/ai-config/models/${modelId}/activate`, {
        method: 'POST',
        headers: authHeaders
      });

      if (response.ok) {
        await response.json();
        // console.log('Model activated:', result);
        alert('AI model activated successfully!');
        loadAiConfiguration(); // Refresh to show new active model
      } else {
        const error = await response.json();
        console.error('Failed to activate model:', error);
        alert(`Failed to activate model: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error activating model:', error);
      alert('Error activating model. Please try again.');
    }
  };

  const deleteModel = async (modelId: number) => {
    if (!confirm('Are you sure you want to delete this AI model configuration?')) {
      return;
    }

    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/ai-config/models/${modelId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        await response.json();
        // console.log('Model deleted:', result);
        alert('AI model configuration deleted successfully!');
        loadAiConfiguration(); // Refresh models list
      } else {
        const error = await response.json();
        console.error('Failed to delete model:', error);
        alert(`Failed to delete model: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting model:', error);
      alert('Error deleting model. Please try again.');
    }
  };

  // Prompt Editor Functions
  const openPromptEditor = (prompt: any, type: string) => {
    setEditingPrompt({ ...prompt, type });
    setPromptContent('');
    setVersionDescription('');
    setShowPromptEditor(true);
  };

  const saveNewPromptVersion = async () => {
    if (!editingPrompt || !promptContent.trim()) {
      alert('Please enter prompt content to save a new version.');
      return;
    }

    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/ai-config/prompts', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt_type: editingPrompt.type,
          prompt_name: editingPrompt.prompt_name,
          prompt_content: promptContent,
          description: versionDescription || `Updated version of ${editingPrompt.prompt_name}`,
          created_by: 'Admin User'
        })
      });

      if (response.ok) {
        await response.json();
        // console.log('New prompt version saved:', result);
        alert('New prompt version saved successfully!');
        // Reload AI configuration to get updated prompts
        await loadAiConfiguration();
        // Clear form and close modal
        setPromptContent('');
        setVersionDescription('');
        setShowPromptEditor(false);
      } else {
        const error = await response.json();
        console.error('Failed to save new prompt version:', error);
        alert(`Failed to save prompt version: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving prompt version:', error);
      alert('Error saving prompt version. Please try again.');
    }
  };

  // Load selected entity when it changes
  useEffect(() => {
    if (selectedEntityId && entities.length > 0) {
      loadEntityAdminState(selectedEntityId);
    }
  }, [selectedEntityId]);

  // Utility function to convert entity phone to WhatsApp entity ID
  const getWhatsAppEntityId = (entity: WhatsAppEntity): string => {
    // Extract phone number for worker compatibility (all entities use phone as ID)
    return entity.phone.replace(/[^\d]/g, '');
  };

  // Migrate old timestamp-based entity IDs to phone numbers
  const migrateOldEntityIds = async () => {
    try {
      // Check localStorage for old entity IDs
      const oldEntities = localStorage.getItem('entities');
      if (oldEntities) {
        const parsed = JSON.parse(oldEntities);
        let needsMigration = false;

        // Check if any entity has timestamp-based ID (old format)
        for (const entity of parsed) {
          if (entity.id && entity.id.startsWith('entity-')) {
            // This is a timestamp-based ID
            needsMigration = true;
            break;
          }
        }

        if (needsMigration) {
          // console.log('[Migration] Detected old timestamp-based entity IDs, migrating...');
          // Clear old entities from localStorage
          localStorage.removeItem('entities');
          localStorage.removeItem('selectedEntityId');
          // console.log('[Migration] Cleared old entity cache, reloading...');
          // Reload the page to fetch fresh data
          window.location.reload();
          return true; // Indicate migration in progress
        }
      }
      return false; // No migration needed
    } catch (error) {
      console.error('[Migration] Error during migration:', error);
      return false;
    }
  };

  const loadEntities = async () => {
    try {
      // First, check if migration is needed
      const migrating = await migrateOldEntityIds();
      if (migrating) {
        return; // Page is reloading
      }

      // Load entities from database API
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/entities', { headers: authHeaders });
      const data = await response.json();

      if (data.ok && data.entities) {
        // Convert database format to WhatsAppEntity format
        const entitiesList: WhatsAppEntity[] = data.entities.map((entity: any) => ({
          id: entity.id,
          name: entity.name,
          phone: entity.phone.startsWith('+65') ? entity.phone : `+65 ${entity.phone.slice(2, 6)} ${entity.phone.slice(6)}`,
          status: entity.status,
          created_at: entity.created_at || new Date().toISOString(),
          last_connected_at: entity.last_activity || new Date().toISOString(),
          is_active: true,
          tenant_id: entity.tenant_id, // 🏢 MULTI-TENANT: Include tenant ID from database
        }));
        setEntities(entitiesList);

        // Auto-select the first entity
        if (entitiesList.length > 0 && !selectedEntityId) {
          setSelectedEntityId(entitiesList[0].id);
        }
      } else {
        throw new Error('Failed to load entities from API');
      }
    } catch (error) {
      console.error("Failed to load entities:", error);
      // No fallback entities - entities must be loaded from the API
      const fallbackEntities: WhatsAppEntity[] = [];
      console.warn('[Admin] No entities available from API - Please configure entities in the database');
      setEntities(fallbackEntities);
      if (fallbackEntities.length > 0 && !selectedEntityId) {
        setSelectedEntityId(fallbackEntities[0].id);
      }
    }
  };

  const loadEntityAdminState = async (entityId: string) => {
    try {
      // console.log(`Loading admin state for entity: ${entityId}`);

      // Clear QR code state when switching entities
      setQrCodeDataUrl(null);
      setQrCodeContent(null);
      setPreviousQrCodeContent(null);
      setQrValidationResult(null);
      setQrTimestamp(null);
      setQrAgeStatus("unknown");
      setQrAgeMinutes(0);

      // Get the selected entity and update its phone number from worker if available
      const selectedEntity = entities.find((e) => e.id === entityId);
      if (selectedEntity && (entityId === "entity-2" || entityId === "entity-3")) {
        // Fetch actual phone number from worker
        await updateEntityPhoneFromWorker(selectedEntity);
      }

      // Refresh WhatsApp status for the selected entity
      await fetchWhatsAppStatus();

      // ⚠️ ANTI-BAN PROTECTION: Do NOT automatically fetch QR codes
      // QR codes should ONLY be fetched when user explicitly clicks "Refresh QR Code" button
      // Automatic QR fetching can trigger WhatsApp anti-bot detection and lead to account bans
      // Reference: CLAUDE.md section "QR Code Protection (Anti-Ban)"

      // Note: QR code will be fetched only when user clicks "Refresh QR Code" button
      // This prevents unnecessary QR generation and respects rate limiting (max 3 per 15 min)

    } catch (error) {
      console.error("Failed to load entity admin state:", error);
    }
  };

  const updateEntityPhoneFromWorker = async (entity: WhatsAppEntity) => {
    try {
      const whatsappEntityId = getWhatsAppEntityId(entity);
      // console.log('[Entity Update] Fetching phone number from worker', { entityId: entity.id, whatsappEntityId });

      const response = await orchestratorApi.getCurrentUser(whatsappEntityId);

      if (response.ok && response.user && response.user.phone) {
        // Update the entity's phone number
        const newPhone = response.user.phone.startsWith('+')
          ? response.user.phone
          : `+${response.user.phone}`;

        setEntities(prev => prev.map(e =>
          e.id === entity.id
            ? { ...e, phone: newPhone, status: 'connected' }
            : e
        ));

        // console.log('[Entity Update] Phone number updated', {
        //   entityId: entity.id,
        //   oldPhone: entity.phone,
        //   newPhone
        // });
      }
    } catch (error) {
      // console.log('[Entity Update] Could not fetch phone number (expected for disconnected entities)', { entityId: entity.id });
      // Don't throw error - this is expected for disconnected entities
    }
  };

  const handleAddEntity = async (name: string, phone: string, timezone?: string) => {
    try {
      // Normalize phone number to use as entity ID (proper SaaS architecture)
      const { normalizePhoneNumber, isValidPhoneNumber } = await import('../utils/phoneNormalization');

      if (!isValidPhoneNumber(phone)) {
        throw new Error(`Invalid phone number format: ${phone}. Please use international format (e.g., +65 9133 4435)`);
      }

      const normalizedPhone = normalizePhoneNumber(phone);
      const entityId = normalizedPhone; // Entity ID = normalized phone number

      // console.log('[Entity Creation] Using normalized phone as entity ID', {
      //   inputPhone: phone,
      //   normalizedPhone,
      //   entityId,
      //   timezone
      // });

      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/entities', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: entityId,
          name,
          phone: normalizedPhone,
          status: 'disconnected',
          timezone: timezone || 'Asia/Singapore'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save entity to database');
      }

      const data = await response.json();
      if (data.ok) {
        const newEntity: WhatsAppEntity = {
          id: data.entity.id,
          name: data.entity.name,
          phone: data.entity.phone,
          status: data.entity.status,
          created_at: data.entity.created_at,
          is_active: true,
          timezone: data.entity.timezone || timezone || 'Asia/Singapore',
        };

        setEntities((prev) => [...prev, newEntity]);
        setSelectedEntityId(newEntity.id);
        setShowAddEntityModal(false);

        // Initialize empty state for the new entity
        await loadEntityAdminState(newEntity.id);
      }
    } catch (error) {
      console.error("Failed to add entity:", error);
      alert("Failed to add new entity. Please try again.");
    }
  };

  const handleEditEntity = (entity: any) => {
    setEditingEntity(entity);
    setShowAddEntityModal(true);
  };

  const handleDeleteEntity = async (entity: any) => {
    if (!window.confirm(`Archive "${entity.name}"?\n\nThis will:\n✓ Remove it from the active sessions dropdown\n✓ Preserve all chat history and data in the database\n✓ Allow you to restore it later from Archived Sessions\n\nThe session will be archived, not permanently deleted.`)) {
      return;
    }

    const wasSelected = selectedEntityId === entity.id;

    try {
      const result = await orchestratorApi.deleteEntity(entity.id);
      if (result.success) {
        // Remove entity from state
        setEntities((prev) => prev.filter((e) => e.id !== entity.id));

        // If deleted entity was selected, clear selection and localStorage
        if (wasSelected) {
          setSelectedEntityId(null);
          localStorage.removeItem('selectedEntityId');
          // Navigate to archived sessions page
          setActiveTab("archived-sessions");
        }

        // Show success message
        alert(`Successfully archived "${entity.name}". ${wasSelected ? 'Opening Archived Sessions page where you can restore it.' : 'You can restore it from the Archived Sessions page.'}`);
      } else {
        alert(`Failed to archive entity: ${result.error}`);
      }
    } catch (error) {
      console.error("Failed to delete entity:", error);
      alert("Failed to delete entity. Please try again.");
    }
  };

  const handleUpdateEntity = async (name: string, phone: string, timezone?: string) => {
    if (!editingEntity) return;

    try {
      const result = await orchestratorApi.updateEntity(editingEntity.id, name, phone, timezone);
      if (result.success) {
        // Update entity in state
        setEntities((prev) =>
          prev.map((e) =>
            e.id === editingEntity.id
              ? { ...e, name, phone, timezone: timezone || e.timezone }
              : e
          )
        );

        // Clear editing state
        setEditingEntity(null);
        setShowAddEntityModal(false);

        // Show success message
        alert(`Successfully updated "${name}"`);
      } else {
        alert(`Failed to update entity: ${result.error}`);
      }
    } catch (error) {
      console.error("Failed to update entity:", error);
      alert("Failed to update entity. Please try again.");
    }
  };

  const loadFeatureFlags = async () => {
    try {
      const flagsData = await orchestratorApi.getFeatureFlags();
      setFlags(flagsData);
    } catch (error) {
      console.error("Failed to load feature flags:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeadConfig = async () => {
    try {
      const config = await orchestratorApi.getLeadConfig();
      setLeadConfig(config);
    } catch (error) {
      console.error("Failed to load lead config:", error);
    }
  };

  const loadAgentSettings = async () => {
    try {
      setLoadingAgentSettings(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch("/api/agent-settings", {
        headers: authHeaders
      });
      const data = await response.json();

      if (data.ok) {
        setAgentSettings(data.settings || []);
      } else {
        console.error("Failed to load agent settings:", data.error);
      }
    } catch (error) {
      console.error("Failed to load agent settings:", error);
    } finally {
      setLoadingAgentSettings(false);
    }
  };

  const updateAgentSettings = async (
    agentName: string,
    updates: Partial<AgentSettings>
  ) => {
    try {
      setUpdatingAgentSettings(agentName);

      const requestBody: any = {
        sendClientMessages: updates.send_client_messages,
        notificationChats: updates.notification_chats,
      };

      if (updates.typeform_message_template !== undefined) {
        requestBody.typeformMessageTemplate = updates.typeform_message_template;
      }

      if (updates.webform_ai_prompt !== undefined) {
        requestBody.webformAiPrompt = updates.webform_ai_prompt;
      }

      if (updates.qualifier_message_template !== undefined) {
        requestBody.qualifierMessageTemplate = updates.qualifier_message_template;
      }

      if (updates.qualifier_ai_prompt !== undefined) {
        requestBody.qualifierAiPrompt = updates.qualifier_ai_prompt;
      }

      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/agent-settings/${agentName}`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (data.ok) {
        // Update local state
        setAgentSettings(prev =>
          prev.map(setting =>
            setting.agent_name === agentName
              ? {
                ...setting,
                send_client_messages: updates.send_client_messages !== undefined ? updates.send_client_messages : setting.send_client_messages,
                notification_chats: updates.notification_chats !== undefined ? updates.notification_chats : setting.notification_chats,
                typeform_message_template: updates.typeform_message_template !== undefined ? updates.typeform_message_template : setting.typeform_message_template,
                webform_ai_prompt: updates.webform_ai_prompt !== undefined ? updates.webform_ai_prompt : setting.webform_ai_prompt,
                qualifier_message_template: updates.qualifier_message_template !== undefined ? updates.qualifier_message_template : setting.qualifier_message_template,
                qualifier_ai_prompt: updates.qualifier_ai_prompt !== undefined ? updates.qualifier_ai_prompt : setting.qualifier_ai_prompt
              }
              : setting
          )
        );
      } else {
        console.error("Failed to update agent settings:", data.error);
      }
    } catch (error) {
      console.error("Failed to update agent settings:", error);
    } finally {
      setUpdatingAgentSettings(null);
    }
  };

  // Translation settings functions
  const loadTranslationSettings = async () => {
    try {
      setLoadingTranslationSettings(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch("/api/translation/settings", {
        headers: authHeaders
      });
      const data = await response.json();

      if (data.ok) {
        setTranslationSettings(data.settings);
        setAvailableModels(data.availableModels || []);
      } else {
        console.error("Failed to load translation settings:", data.error);
      }
    } catch (error) {
      console.error("Failed to load translation settings:", error);
    } finally {
      setLoadingTranslationSettings(false);
    }
  };

  const updateTranslationSettings = async (updates: any) => {
    try {
      setUpdatingTranslationSettings(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch("/api/translation/settings", {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(updates)
      });
      const data = await response.json();

      if (data.ok) {
        setTranslationSettings(data.settings);
        alert("Translation settings updated successfully!");
      } else {
        console.error("Failed to update translation settings:", data.error);
        alert("Failed to update translation settings: " + data.error);
      }
    } catch (error) {
      console.error("Failed to update translation settings:", error);
      alert("Failed to update translation settings");
    } finally {
      setUpdatingTranslationSettings(false);
    }
  };

  const runTranslationBackfill = async () => {
    if (!confirm("Are you sure you want to run translation backfill? This will translate all untranslated messages.")) {
      return;
    }

    try {
      setRunningBackfill(true);
      setBackfillProgress({ translated: 0, skipped: 0, failed: 0, remaining: 0 });

      let continueBackfill = true;
      let totalTranslated = 0;
      let totalSkipped = 0;
      let totalFailed = 0;

      while (continueBackfill) {
        const authHeaders = await orchestratorApi.getAuthHeaders();
        const response = await fetch("/api/translation/backfill", {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ batchSize: 10 })
        });
        const data = await response.json();

        if (!data.ok) {
          console.error("Backfill failed:", data.error);
          alert("Backfill failed: " + data.error);
          break;
        }

        totalTranslated += data.translated;
        totalSkipped += data.skipped;
        totalFailed += data.failed;

        setBackfillProgress({
          translated: totalTranslated,
          skipped: totalSkipped,
          failed: totalFailed,
          remaining: data.remaining
        });

        if (data.remaining === 0) {
          continueBackfill = false;
          alert(`Backfill complete! Translated: ${totalTranslated}, Skipped: ${totalSkipped}, Failed: ${totalFailed}`);
        }

        // Small delay between batches
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.error("Failed to run backfill:", error);
      alert("Failed to run backfill");
    } finally {
      setRunningBackfill(false);
    }
  };

  // Calendar scheduling functions
  const loadCalendarSchedule = async () => {
    try {
      setLoadingCalendarSchedule(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch("/api/calendar/schedule", {
        headers: authHeaders
      });
      const data = await response.json();

      if (data.ok) {
        setCalendarSchedule(data.schedule);
      } else {
        console.error("Failed to load calendar schedule:", data.error);
      }
    } catch (error) {
      console.error("Failed to load calendar schedule:", error);
    } finally {
      setLoadingCalendarSchedule(false);
    }
  };

  const updateCalendarSchedule = async (checkTime: string, timezone: string = 'Asia/Singapore', daysAhead: number = 1) => {
    try {
      setUpdatingCalendarSchedule(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch("/api/calendar/schedule", {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          checkTime,
          timezone,
          daysAhead
        }),
      });

      const data = await response.json();

      if (data.ok) {
        setCalendarSchedule(data.schedule);
        alert("Calendar schedule updated successfully!");
      } else {
        console.error("Failed to update calendar schedule:", data.error);
        alert(`Failed to update calendar schedule: ${data.error}`);
      }
    } catch (error) {
      console.error("Failed to update calendar schedule:", error);
      alert("Failed to update calendar schedule. Please try again.");
    } finally {
      setUpdatingCalendarSchedule(false);
    }
  };

  const testCalendarCheck = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch("/api/calendar/test-check", {
        method: 'POST',
        headers: authHeaders
      });

      const data = await response.json();

      if (data.ok) {
        alert(`Calendar check completed successfully! Processed ${data.eventsProcessed} test events.`);
      } else {
        alert(`Failed to trigger calendar check: ${data.error}`);
      }
    } catch (error) {
      console.error("Failed to trigger calendar check:", error);
      alert("Failed to trigger calendar check. Please try again.");
    }
  };

  // Unanswered Messages scheduling functions
  const loadUnansweredSchedule = async () => {
    try {
      setLoadingUnansweredSchedule(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();

      // Get the selected entity's WhatsApp entity ID
      const selectedEntity = entities.find((e) => e.id === selectedEntityId);
      if (!selectedEntity) {
        console.error("No entity selected");
        return;
      }

      // Convert frontend entity ID to WhatsApp entity ID format
      const whatsappEntityId = getWhatsAppEntityId(selectedEntity);
      if (!whatsappEntityId) {
        console.error("Could not determine WhatsApp entity ID");
        return;
      }

      const response = await fetch(`/api/unanswered/entity-assignments/${whatsappEntityId}`, {
        headers: authHeaders
      });
      const data = await response.json();

      if (data.ok && data.assignment) {
        setUnansweredSchedule({
          checkTimes: data.assignment.checkTimes,
          timezone: data.assignment.timezone,
          maxChats: data.assignment.maxChats,
          minutesThreshold: data.assignment.minutesThreshold,
          llmTimeoutSeconds: data.assignment.llmTimeoutSeconds || 60,
          lastCheck: data.assignment.lastCheck,
          nextCheck: data.assignment.nextCheck,
          enabled: data.assignment.enabled,
        });
      } else if (response.status === 404) {
        // No assignment yet, set default schedule
        console.log("No entity assignment found, using defaults");
        setUnansweredSchedule({
          checkTimes: ['12:00', '17:30'],
          timezone: 'Asia/Singapore',
          maxChats: 100,
          minutesThreshold: 30,
          llmTimeoutSeconds: 60,
          lastCheck: undefined,
          nextCheck: undefined,
          enabled: false,
        });
      } else {
        console.error("Failed to load unanswered messages schedule:", data.error);
      }
    } catch (error) {
      console.error("Failed to load unanswered messages schedule:", error);
    } finally {
      setLoadingUnansweredSchedule(false);
    }
  };

  const loadQualifierSchedule = async () => {
    try {
      setLoadingQualifierSchedule(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch("/api/qualifier-agent/schedule", {
        headers: authHeaders
      });
      const data = await response.json();

      if (data.ok) {
        setQualifierSchedule(data.schedule);
      } else {
        console.error("Failed to load qualifier agent schedule:", data.error);
      }
    } catch (error) {
      console.error("Failed to load qualifier agent schedule:", error);
    } finally {
      setLoadingQualifierSchedule(false);
    }
  };

  const updateUnansweredSchedule = async (
    checkTimes: string[],
    timezone: string = 'Asia/Singapore',
    maxChats: number = 100,
    minutesThreshold: number = 30,
    notificationChats: string = 'E&H Immigration Internal',
    llmTimeoutSeconds: number = 60
  ) => {
    try {
      setUpdatingUnansweredSchedule(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();

      // Get the selected entity's WhatsApp entity ID
      const selectedEntity = entities.find((e) => e.id === selectedEntityId);
      if (!selectedEntity) {
        alert("No entity selected");
        return;
      }

      const whatsappEntityId = getWhatsAppEntityId(selectedEntity);
      if (!whatsappEntityId) {
        alert("Could not determine WhatsApp entity ID");
        return;
      }

      const response = await fetch(`/api/unanswered/entity-assignments/${whatsappEntityId}`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled: true,
          checkTimes,
          timezone,
          maxChats,
          minutesThreshold,
          llmTimeoutSeconds,
          notificationChats
        }),
      });

      const data = await response.json();

      if (data.ok) {
        setUnansweredSchedule(data.assignment ? {
          checkTimes: data.assignment.checkTimes,
          timezone: data.assignment.timezone,
          maxChats: data.assignment.maxChats,
          minutesThreshold: data.assignment.minutesThreshold,
          llmTimeoutSeconds: data.assignment.llmTimeoutSeconds || 60,
          lastCheck: data.assignment.lastCheck,
          nextCheck: data.assignment.nextCheck,
          enabled: data.assignment.enabled,
          notificationChats: data.assignment.notificationChats,
        } : null);
        alert("Unanswered messages schedule updated successfully!");
      } else {
        console.error("Failed to update unanswered messages schedule:", data.error);
        alert(`Failed to update unanswered messages schedule: ${data.error}`);
      }
    } catch (error) {
      console.error("Failed to update unanswered messages schedule:", error);
      alert("Failed to update unanswered messages schedule. Please try again.");
    } finally {
      setUpdatingUnansweredSchedule(false);
    }
  };

  const testUnansweredCheck = () => {
    // Show confirmation modal
    setShowUnansweredTestModal(true);
  };

  const runUnansweredTest = async (maxChats: number) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();

      // Get the selected entity's WhatsApp entity ID
      const selectedEntity = entities.find((e) => e.id === selectedEntityId);
      if (!selectedEntity) {
        alert("No entity selected");
        return;
      }

      const whatsappEntityId = getWhatsAppEntityId(selectedEntity);
      if (!whatsappEntityId) {
        alert("Could not determine WhatsApp entity ID");
        return;
      }

      setShowUnansweredTestModal(false);
      setUnansweredTestLoading(true);
      setUnansweredTestResults(null);

      const response = await fetch(`/api/unanswered/entity-assignments/${whatsappEntityId}/unanswered-chats?maxChats=${maxChats}`, {
        headers: authHeaders
      });

      const data = await response.json();

      if (data.ok && data.unansweredChats) {
        setUnansweredTestResults(data.unansweredChats);
      } else if (response.status === 404 && data.error?.includes('not enabled')) {
        alert('Unanswered messages monitor is not enabled for this entity. Please enable it first.');
      } else {
        alert(data.error || 'Failed to check for unanswered messages');
      }
    } catch (error) {
      console.error("Failed to check unanswered messages:", error);
      alert("Failed to check for unanswered messages. Please try again.");
    } finally {
      setUnansweredTestLoading(false);
    }
  };

  const toggleUnansweredEnabled = async (enabled: boolean) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();

      // Get the selected entity's WhatsApp entity ID
      const selectedEntity = entities.find((e) => e.id === selectedEntityId);
      if (!selectedEntity) {
        alert("No entity selected");
        return;
      }

      const whatsappEntityId = getWhatsAppEntityId(selectedEntity);
      if (!whatsappEntityId) {
        alert("Could not determine WhatsApp entity ID");
        return;
      }

      const response = await fetch(`/api/unanswered/entity-assignments/${whatsappEntityId}/enable`, {
        method: 'PATCH',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled })
      });

      const data = await response.json();

      if (data.ok) {
        // Reload the schedule to get updated status
        await loadUnansweredSchedule();
        alert(`Unanswered messages agent ${enabled ? 'enabled' : 'disabled'} for ${selectedEntity.name}!`);
      } else {
        alert(`Failed to ${enabled ? 'enable' : 'disable'} unanswered messages agent: ${data.error}`);
      }
    } catch (error) {
      console.error("Failed to toggle unanswered messages agent:", error);
      alert(`Failed to ${enabled ? 'enable' : 'disable'} unanswered messages agent. Please try again.`);
    }
  };

  const updateQualifierSchedule = async (updates: { dayOfWeek: number; hourOfDay: number; isActive: boolean }[]) => {
    try {
      setUpdatingQualifierSchedule(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch("/api/qualifier-agent/schedule", {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      const data = await response.json();

      if (data.ok) {
        await loadQualifierSchedule(); // Reload the schedule
        alert("Qualifier agent schedule updated successfully!");
      } else {
        console.error("Failed to update qualifier agent schedule:", data.error);
        alert(`Failed to update qualifier agent schedule: ${data.error}`);
      }
    } catch (error) {
      console.error("Failed to update qualifier agent schedule:", error);
      alert("Failed to update qualifier agent schedule. Please try again.");
    } finally {
      setUpdatingQualifierSchedule(false);
    }
  };

  const fetchWhatsAppStatus = async () => {
    try {
      // Check if current selected entity is a placeholder
      const selectedEntity = entities.find((e) => e.id === selectedEntityId);
      const isPlaceholderEntity = selectedEntity &&
        (selectedEntity.id.includes("placeholder") || !selectedEntity.is_active);

      if (isPlaceholderEntity) {
        // Check if entity exists in database but has no worker configured
        const whatsappEntityId = selectedEntity ? getWhatsAppEntityId(selectedEntity) : undefined;

        try {
          const { status } = await orchestratorApi.getWhatsAppStatus(whatsappEntityId);
          // If we get a valid response from orchestrator, use it
          // (This handles entities that have workers but aren't "active" in frontend)
          setWhatsAppStatus(prev => prev === status ? prev : status);

          // Update entity status in entities array for dropdown consistency.
          // Bail out (return the same array/entity reference) when nothing actually
          // changed — this poll runs every 5s regardless of tab, and unconditionally
          // creating new references here forces a re-render (and DOM mutation) on every
          // tick, which can interrupt unrelated UI (e.g. closes open native <select> popups
          // in Chromium-based browsers) even when the status hasn't changed at all.
          if (selectedEntityId) {
            setEntities(prev => {
              const newStatus = status.toLowerCase() as 'connected' | 'disconnected' | 'authenticating' | 'needs_qr';
              const idx = prev.findIndex(e => e.id === selectedEntityId);
              if (idx === -1 || prev[idx].status === newStatus) return prev;
              const next = prev.slice();
              next[idx] = { ...prev[idx], status: newStatus };
              return next;
            });
          }

          if (status === "CONNECTED") {
            setQrCodeDataUrl(null);
            cancelQrExpirationTimer();
          }
        } catch (statusError: any) {
          // If orchestrator returns error (no worker), show appropriate message
          const nextStatus = (statusError?.response?.status === 404 || statusError?.message?.includes('not found'))
            ? "NO_WORKER_CONFIGURED"
            : "DISCONNECTED";
          setWhatsAppStatus(prev => prev === nextStatus ? prev : nextStatus);

          // Update entity status in entities array
          if (selectedEntityId) {
            setEntities(prev => {
              const idx = prev.findIndex(e => e.id === selectedEntityId);
              if (idx === -1 || prev[idx].status === 'disconnected') return prev;
              const next = prev.slice();
              next[idx] = { ...prev[idx], status: 'disconnected' };
              return next;
            });
          }
        }
        return;
      }

      // Get the WhatsApp entity ID for the selected entity
      const whatsappEntityId = selectedEntity ? getWhatsAppEntityId(selectedEntity) : undefined;
      // console.log('[WhatsApp Status] Fetching status for entity:', {
      //   frontendEntityId: selectedEntityId,
      //   whatsappEntityId
      // });

      const { status } = await orchestratorApi.getWhatsAppStatus(whatsappEntityId);
      setWhatsAppStatus(prev => prev === status ? prev : status);

      // Update entity status in the entities array for dropdown consistency.
      // See bail-out note above — only touch state (and the DOM) when status actually changed.
      if (selectedEntityId) {
        setEntities(prev => {
          const newStatus = status.toLowerCase() as 'connected' | 'disconnected' | 'authenticating' | 'needs_qr';
          const idx = prev.findIndex(e => e.id === selectedEntityId);
          if (idx === -1 || prev[idx].status === newStatus) return prev;
          const next = prev.slice();
          next[idx] = { ...prev[idx], status: newStatus };
          return next;
        });
      }

      // Clear QR code when connected successfully
      if (status === "CONNECTED") {
        setQrCodeDataUrl(null);
        cancelQrExpirationTimer(); // Cancel timer when user successfully scans QR
      }
      // Don't automatically fetch QR code - user must click "Refresh QR Code"
    } catch (error) {
      // Only log if not a network connectivity issue
      if (!(error instanceof TypeError && error.message.includes('NetworkError'))) {
        console.error("Failed to load WhatsApp status:", error);
      }
      setWhatsAppStatus(prev => prev === "DISCONNECTED" ? prev : "DISCONNECTED");

      // Update entity status to disconnected in entities array
      if (selectedEntityId) {
        setEntities(prev => {
          const idx = prev.findIndex(e => e.id === selectedEntityId);
          if (idx === -1 || prev[idx].status === 'disconnected') return prev;
          const next = prev.slice();
          next[idx] = { ...prev[idx], status: 'disconnected' };
          return next;
        });
      }
    }
  };

  const decodeQrCode = async (dataUrl: string): Promise<string | null> => {
    try {
      setIsDecodingQr(true);

      // Create an image element to load the QR code
      const img = new Image();
      img.crossOrigin = "anonymous";

      return new Promise((resolve) => {
        img.onload = () => {
          try {
            // Create a canvas to extract image data
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            if (!ctx) {
              resolve(null);
              return;
            }

            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            // Get image data
            const imageData = ctx.getImageData(
              0,
              0,
              canvas.width,
              canvas.height,
            );
            const code = jsQR(
              imageData.data,
              imageData.width,
              imageData.height,
            );

            if (code) {
              resolve(code.data);
            } else {
              resolve(null);
            }
          } catch (error) {
            console.error("Error decoding QR code:", error);
            resolve(null);
          } finally {
            setIsDecodingQr(false);
          }
        };

        img.onerror = () => {
          console.error("Failed to load QR code image");
          setIsDecodingQr(false);
          resolve(null);
        };

        img.src = dataUrl;
      });
    } catch (error) {
      console.error("Error setting up QR code decoding:", error);
      setIsDecodingQr(false);
      return null;
    }
  };

  // Fetch Emergency Cleanup History events
  const fetchEmergencyCleanupEvents = async () => {
    try {
      setCleanupEventsLoading(true);
      const response = await fetch('/emergency-cleanup-events?limit=20');

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        // Endpoint doesn't exist or returned error - silently ignore
        setEmergencyCleanupEvents([]);
        return;
      }

      // Check content type to ensure it's JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Not JSON response (likely HTML error page) - silently ignore
        setEmergencyCleanupEvents([]);
        return;
      }

      const data = await response.json();
      setEmergencyCleanupEvents(data.events || []);
    } catch (error) {
      // Only log error if it's not a network/endpoint issue
      if (!(error instanceof TypeError && error.message.includes('fetch'))) {
        console.error('[Emergency Cleanup] Failed to load events:', error);
      }
      setEmergencyCleanupEvents([]);
    } finally {
      setCleanupEventsLoading(false);
    }
  };

  // Load emergency cleanup events on mount and when entity changes
  useEffect(() => {
    fetchEmergencyCleanupEvents();
  }, [selectedEntityId]);

  // Helper function to cancel QR expiration timer
  const cancelQrExpirationTimer = () => {
    if (qrExpirationTimerRef.current) {
      clearTimeout(qrExpirationTimerRef.current);
      qrExpirationTimerRef.current = null;
      // console.log('[QR Expiration] Timer cancelled');
    }
  };

  // Helper function to start QR expiration timer (70 seconds)
  const startQrExpirationTimer = () => {
    // Cancel any existing timer
    cancelQrExpirationTimer();

    // Start 70-second timer (matches QR code auto-deletion lifecycle)
    const QR_EXPIRATION_MS = 70 * 1000;
    qrExpirationTimerRef.current = setTimeout(() => {
      // console.log('[QR Expiration] QR code expired after 70 seconds - clearing display');
      setQrCodeDataUrl(null);
      setQrCodeContent(null);
      setPreviousQrCodeContent(null);
      setQrValidationResult(null);
      // Silently clear QR code - no alert needed
    }, QR_EXPIRATION_MS);

    // console.log('[QR Expiration] Started 70-second expiration timer');
  };

  // ⚠️ CRITICAL: Fetch QR code function - MUST only be called from user action
  // This function is ONLY called after user clicks "Refresh QR Code" button (via handleRefreshQR).
  // DO NOT call this function automatically on page load, entity change, or any other automatic trigger.
  // User requirement: "only the user can request for refresh of QR code"
  const fetchQrCode = async () => {
    // console.log('[QR] fetchQrCode called', { selectedEntityId });
    try {
      // Get the selected entity and convert to WhatsApp entity ID
      const selectedEntity = entities.find((e) => e.id === selectedEntityId);
      const whatsappEntityId = selectedEntity ? getWhatsAppEntityId(selectedEntity) : undefined;
      const tenantId = selectedEntity?.tenant_id || authTenantId || undefined; // 🏢 MULTI-TENANT: Use entity's tenant ID

      // console.log('[QR] Using WhatsApp entity ID:', {
      //   frontendEntityId: selectedEntityId,
      //   whatsappEntityId,
      //   tenantId,
      //   entityPhone: selectedEntity?.phone
      // });

      const result = await orchestratorApi.getQrCode(whatsappEntityId, tenantId);
      // console.log('[QR] Result from getQrCode', result);
      if ('error' in result) {
        console.error("Failed to fetch QR code:", result.error);
        setQrCodeDataUrl(null);
        setQrValidationResult(null);
        cancelQrExpirationTimer(); // Cancel timer if fetch failed
      } else {
        setQrCodeDataUrl(result.qrCodeDataUrl);

        // Start 70-second expiration timer
        startQrExpirationTimer();

        // Store validation results if available
        if (result.validation) {
          setQrValidationResult({
            isValid: result.validation.isValid,
            isReadable: result.validation.isReadable,
            content: result.validation.content,
            formatValid: result.validation.formatValid,
            freshness: result.freshness,
            recommendations: result.recommendations
          });
        }

        if (result.qrCodeDataUrl) {
          // Decode the QR code content for comparison
          const content = await decodeQrCode(result.qrCodeDataUrl);
          if (content) {
            // Store current content as previous before updating
            if (qrCodeContent && qrCodeContent !== content) {
              setPreviousQrCodeContent(qrCodeContent);
            }
            setQrCodeContent(content);

            // Log the comparison for debugging
            // console.log('[QR Comparison]', {
            //   current: content.substring(0, 50) + '...',
            //   previous: qrCodeContent ? qrCodeContent.substring(0, 50) + '...' : 'none',
            //   changed: content !== qrCodeContent
            // });
          }
        }
      }

      // Also fetch timestamp when we fetch QR code
      await fetchQrTimestamp();
    } catch (error) {
      console.error("Failed to fetch QR code:", error);
    }
  };

  const fetchQrTimestamp = async () => {
    try {
      // Get the selected entity and convert to WhatsApp entity ID
      const selectedEntity = entities.find((e) => e.id === selectedEntityId);
      const whatsappEntityId = selectedEntity ? getWhatsAppEntityId(selectedEntity) : undefined;

      const timestampData = await orchestratorApi.getQrCodeTimestamp(whatsappEntityId);
      if (timestampData.exists && timestampData.timestamp) {
        setQrTimestamp(timestampData.timestamp);
        setQrAgeStatus(timestampData.age_status || "unknown");
        setQrAgeMinutes(timestampData.age_minutes || 0);
      } else {
        setQrTimestamp(null);
        setQrAgeStatus("not_generated");
        setQrAgeMinutes(0);
      }
    } catch (error) {
      console.error("Failed to fetch QR timestamp:", error);
    }
  };

  const handleRefreshQrCode = async () => {
    setIsWhatsAppLoading(true);
    try {
      // Get the selected entity and convert to WhatsApp entity ID
      const selectedEntity = entities.find((e) => e.id === selectedEntityId);
      const whatsappEntityId = selectedEntity ? getWhatsAppEntityId(selectedEntity) : undefined;
      const tenantId = selectedEntity?.tenant_id || authTenantId || undefined; // 🏢 MULTI-TENANT: Use entity's tenant ID

      // console.log('[QR] Refreshing QR code for entity:', {
      //   frontendEntityId: selectedEntityId,
      //   whatsappEntityId,
      //   tenantId,
      //   entityName: selectedEntity?.name
      // });

      const response = await orchestratorApi.refreshQrCode(whatsappEntityId, tenantId);

      // console.log('[QR] API response:', {
      //   ok: response.ok,
      //   message: response.message,
      //   rateLimit: response.rateLimit
      // });

      // Check if the request failed
      if (!response.ok) {
        // Rate limit error
        if (response.message?.includes('Rate limit exceeded') || response.rateLimit) {
          const remainingTime = response.rateLimit?.remainingTime || 300;
          const expiresAt = Date.now() + (remainingTime * 1000);
          const newState = {
            limited: true,
            remainingTime,
            message: response.message,
            expiresAt
          };
          localStorage.setItem('qrRateLimit', JSON.stringify(newState));
          setQrRateLimit(newState);
          console.warn('[QR] Rate limit exceeded:', response.message);
        } else {
          // Auth or other server error — tell the user explicitly
          const msg = response.message || 'Request failed (possibly expired session). Please log out and log back in, then try again.';
          console.error('[QR] Refresh failed:', msg);
          alert(`Failed to refresh QR code: ${msg}`);
        }
        return;
      }

      // Success! Track when we triggered the refresh
      const refreshTime = Date.now();
      setLastQrRefreshTime(refreshTime);
      // Force image refresh by updating the key
      setQrCodeImageKey(refreshTime);
      // Wait a moment for the QR code to be generated, then fetch the updated data
      setTimeout(() => {
        fetchQrCode();
        fetchQrTimestamp();
        fetchWhatsAppStatus(); // Also refresh WhatsApp status
      }, 2000);
    } catch (error: any) {
      console.error("Failed to refresh QR code:", error);

      // Check if error message contains rate limit info
      if (error.message && error.message.includes('Rate limit exceeded')) {
        // Try to parse rate limit from string if possible
        const timeMatch = error.message.match(/wait (\d+) seconds?/);
        const remainingTime = timeMatch ? parseInt(timeMatch[1]) : 300; // Default 5 minutes

        const expiresAt = Date.now() + (remainingTime * 1000);
        const newState = {
          limited: true,
          remainingTime,
          message: error.message,
          expiresAt
        };

        localStorage.setItem('qrRateLimit', JSON.stringify(newState));
        setQrRateLimit(newState);
      } else {
        alert("Failed to refresh QR code. Please try again.");
      }
    } finally {
      setIsWhatsAppLoading(false);
    }
  };

  const formatTimestampSGT = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-SG", {
      timeZone: "Asia/Singapore",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const getQrRefreshStatus = (): "changed" | "unchanged" => {
    // If no previous content exists, treat as new/changed
    if (!previousQrCodeContent) {
      return "changed";
    }

    // If we recently triggered a refresh (within last 5 seconds), consider it changed
    if (lastQrRefreshTime && Date.now() - lastQrRefreshTime < 5000) {
      return "changed";
    }

    // Compare actual content
    return qrCodeContent === previousQrCodeContent ? "unchanged" : "changed";
  };

  const getQrRefreshStatusText = (): string => {
    // If we recently triggered a refresh, show refreshed
    if (lastQrRefreshTime && Date.now() - lastQrRefreshTime < 5000) {
      return "✅ QR Code Refreshed";
    }
    // Otherwise, compare content as before
    return qrCodeContent === previousQrCodeContent
      ? "⚠️ QR Code Unchanged"
      : "✅ QR Code Refreshed";
  };

  const getAgeStatusEmoji = (status: string): string => {
    switch (status) {
      case "fresh":
        return "🟢";
      case "recent":
        return "🟡";
      case "aging":
        return "🟠";
      case "old":
        return "🔴";
      default:
        return "⚪";
    }
  };

  const handleLogout = async () => {
    setIsWhatsAppLoading(true);
    try {
      // Get the selected entity and convert to WhatsApp entity ID
      const selectedEntity = entities.find((e) => e.id === selectedEntityId);
      const whatsappEntityId = selectedEntity ? getWhatsAppEntityId(selectedEntity) : undefined;

      // console.log('[Logout] Logging out entity:', {
      //   frontendEntityId: selectedEntityId,
      //   whatsappEntityId,
      //   entityName: selectedEntity?.name
      // });

      await orchestratorApi.logout(whatsappEntityId);

      // Update UI state
      setWhatsAppStatus("DISCONNECTED");
      setQrCodeDataUrl(null);

      // Update entity status in the list
      if (selectedEntityId) {
        setEntities(prev => prev.map(e =>
          e.id === selectedEntityId ? { ...e, status: 'disconnected' } : e
        ));
      }

      // console.log('[Logout] Logout successful');
    } catch (error) {
      console.error("Failed to logout:", error);
      alert("Failed to logout. Check the console for details.");
    } finally {
      setIsWhatsAppLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (syncJobTimerRef.current) {
        clearInterval(syncJobTimerRef.current);
        syncJobTimerRef.current = null;
      }
    };
  }, []);

  const handleSyncWhatsAppChats = async () => {
    if (syncJobTimerRef.current) {
      clearInterval(syncJobTimerRef.current);
      syncJobTimerRef.current = null;
    }

    setSyncingChats(true);
    setLastSyncStats(null);

    const result = await orchestratorApi.syncWhatsAppChats(100, 10);

    if (!result.success || !result.jobId) {
      setSyncingChats(false);
      const errorMessage = result.error || "Failed to start sync job";
      console.error("Failed to start WhatsApp chat sync:", errorMessage);
      alert(`Failed to sync WhatsApp chats: ${errorMessage}`);
      return;
    }

    const pollJobStatus = async () => {
      const jobStatus = await orchestratorApi.getSyncJobStatus(result.jobId!);

      if (!jobStatus.success || !jobStatus.job) {
        console.error("Failed to poll sync job status:", jobStatus.error);
        return;
      }

      const job = jobStatus.job;

      setLastSyncStats({
        total: job.totalChats ?? 0,
        synced: job.chatsSynced ?? 0,
        errors: job.chatsErrors ?? 0,
        totalMessages: job.totalMessages ?? 0,
        status: job.status,
        message:
          job.status === "running"
            ? `Syncing ${job.chatsProcessed}/${job.totalChats} chats...`
            : job.status === "failed"
              ? job.errorMessage || "Sync job failed"
              : undefined,
      });

      if (job.status === "completed" || job.status === "failed") {
        if (syncJobTimerRef.current) {
          clearInterval(syncJobTimerRef.current);
          syncJobTimerRef.current = null;
        }
        setSyncingChats(false);

        if (job.status === "completed") {
          alert(
            `Successfully synced ${job.chatsSynced}/${job.totalChats} chats (${job.totalMessages} messages).`,
          );
        } else {
          alert(`Sync failed: ${job.errorMessage || "Unknown error"}`);
        }
      }
    };

    // kick off immediately so UI updates without waiting for first interval tick
    await pollJobStatus();
    syncJobTimerRef.current = setInterval(pollJobStatus, 2000);
  };

  const handleToggleFlag = async (
    flagName: string,
    currentEnabled: boolean,
  ) => {
    setUpdating(flagName);
    try {
      const updatedFlag = await orchestratorApi.updateFeatureFlag(
        flagName,
        !currentEnabled,
      );
      setFlags((prevFlags) =>
        prevFlags.map((flag) => (flag.name === flagName ? updatedFlag : flag)),
      );
    } catch (error) {
      console.error("Failed to update feature flag:", error);
      alert("Failed to update feature flag. Please try again.");
    } finally {
      setUpdating(null);
    }
  };

  const handleUpdateLeadConfig = async (targetChat: string) => {
    setUpdatingLeadConfig(true);
    try {
      const result = await orchestratorApi.updateLeadConfig(targetChat);
      setLeadConfig((prev) =>
        prev ? { ...prev, config: result.config } : null,
      );
      alert("Lead agent target chat updated successfully!");
    } catch (error) {
      console.error("Failed to update lead config:", error);
      alert("Failed to update lead config. Please try again.");
    } finally {
      setUpdatingLeadConfig(false);
    }
  };

  const getFlagDisplayName = (flagName: string) => {
    const displayNames: Record<string, string> = {
      agent_watcher: "Agent 1 (Watcher)",
      agent_qualifier: "Lead Qualifier Agent",
      lead_webhook_agent: "Lead Webhook Agent",
      webform_email_agent: "Webform Email Agent",
      typeform_email_agent: "Typeform Email Agent",
    };
    return displayNames[flagName] || flagName;
  };

  const getFlagDescription = (flagName: string) => {
    const descriptions: Record<string, string> = {
      agent_watcher:
        "Forwards inbound events to WebSocket clients with enrichment",
      agent_qualifier:
        "AI-powered lead qualification with FSM logic and handover",
      lead_webhook_agent:
        "Processes lead webhooks and sends formatted notifications to WhatsApp",
      webform_email_agent:
        "Monitors Squarespace form submissions every minute and sends WhatsApp messages with AI-powered responses",
      typeform_email_agent:
        "Monitors Typeform submissions every minute and sends WhatsApp messages with internal notifications",
      calendar_webhook_agent:
        "Processes calendar webhook events to send WhatsApp meeting reminders for Sales appointments with extracted phone numbers",
      unanswered_messages_agent:
        "Scans recent chats for unanswered client messages and sends reminder alerts twice daily (default: 12:00 noon and 5:30pm SGT)",
    };
    return descriptions[flagName] || "Feature flag description";
  };

  // Broadcast functions
  const loadBroadcastCampaigns = async () => {
    try {
      setLoadingBroadcast(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/broadcast/campaigns', { headers: authHeaders });
      const data = await response.json();
      setBroadcastCampaigns(data.campaigns || []);
    } catch (error) {
      // Only log if not a network connectivity issue
      if (!(error instanceof TypeError && error.message.includes('NetworkError'))) {
        console.error('Failed to load campaigns:', error);
      }
      setBroadcastCampaigns([]);
    } finally {
      setLoadingBroadcast(false);
    }
  };

  const createCampaign = async () => {
    try {
      setLoadingBroadcast(true);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/broadcast/campaigns', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(campaignForm)
      });
      const data = await response.json();
      if (data.ok) {
        setSelectedCampaign(data.campaign);
        setShowCreateCampaign(false);
        setCampaignForm({ name: '', message: '', source_type: 'csv' });
        await loadBroadcastCampaigns();
      }
    } catch (error) {
      console.error('Failed to create campaign:', error);
    } finally {
      setLoadingBroadcast(false);
    }
  };

  const uploadCsv = async () => {
    if (!csvFile || !selectedCampaign) return;
    try {
      setLoadingBroadcast(true);
      const formData = new FormData();
      formData.append('csv', csvFile);
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/broadcast/campaigns/${selectedCampaign.id}/recipients/csv`, {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });
      const data = await response.json();
      if (data.ok) {
        alert(`✅ ${data.processed} recipients added successfully!`);
        setCsvFile(null);
      }
    } catch (error) {
      console.error('Failed to upload CSV:', error);
    } finally {
      setLoadingBroadcast(false);
    }
  };

  const startCampaign = async (campaignId: number) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/broadcast/campaigns/${campaignId}/start`, {
        method: 'POST',
        headers: authHeaders
      });
      if (response.ok) {
        await loadBroadcastCampaigns();
        // Start polling for progress
        startProgressPolling(campaignId);
      }
    } catch (error) {
      console.error('Failed to start campaign:', error);
    }
  };

  const startProgressPolling = (campaignId: number) => {
    // Clear existing interval for this campaign to prevent duplicates
    const existingInterval = campaignIntervalsRef.current.get(campaignId);
    if (existingInterval) {
      clearInterval(existingInterval);
      campaignIntervalsRef.current.delete(campaignId);
    }

    const interval = setInterval(async () => {
      try {
        const authHeaders = await orchestratorApi.getAuthHeaders();
        const response = await fetch(`/api/broadcast/campaigns/${campaignId}`, {
          headers: authHeaders
        });
        const data = await response.json();
        if (data.ok) {
          setCampaignProgress(prev => ({
            ...prev,
            [campaignId]: {
              status: data.campaign.status,
              total: data.campaign.total_recipients,
              processed: data.campaign.processed_count,
              success: data.campaign.success_count,
              failed: data.campaign.failed_count,
              percentage: data.campaign.total_recipients > 0
                ? Math.round((data.campaign.processed_count / data.campaign.total_recipients) * 100)
                : 0
            }
          }));

          // Stop polling if campaign is completed, failed, or paused
          if (['completed', 'failed'].includes(data.campaign.status)) {
            clearInterval(interval);
            campaignIntervalsRef.current.delete(campaignId);
            await loadBroadcastCampaigns();
          }
        }
      } catch (error) {
        console.error('Failed to fetch progress:', error);
      }
    }, 2000); // Poll every 2 seconds

    // Store interval reference in ref for proper cleanup
    campaignIntervalsRef.current.set(campaignId, interval);
  };

  const pauseCampaign = async (campaignId: number) => {
    try {
      // Stop polling when pausing
      const interval = campaignIntervalsRef.current.get(campaignId);
      if (interval) {
        clearInterval(interval);
        campaignIntervalsRef.current.delete(campaignId);
      }
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/broadcast/campaigns/${campaignId}/pause`, {
        method: 'POST',
        headers: authHeaders
      });
      if (response.ok) {
        await loadBroadcastCampaigns();
      }
    } catch (error) {
      console.error('Failed to pause campaign:', error);
    }
  };

  const resumeCampaign = async (campaignId: number) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/broadcast/campaigns/${campaignId}/resume`, {
        method: 'POST',
        headers: authHeaders
      });
      if (response.ok) {
        await loadBroadcastCampaigns();
        // Resume polling when resuming
        startProgressPolling(campaignId);
      }
    } catch (error) {
      console.error('Failed to resume campaign:', error);
    }
  };

  // Cleanup all campaign intervals on unmount
  useEffect(() => {
    return () => {
      // Clear all campaign polling intervals
      campaignIntervalsRef.current.forEach((interval) => {
        clearInterval(interval);
      });
      campaignIntervalsRef.current.clear();
    };
  }, []);

  // Find selected entity
  const selectedEntity = entities.find((e) => e.id === selectedEntityId);

  // Load campaigns when component mounts or entity changes
  useEffect(() => {
    if (selectedEntityId) {
      loadBroadcastCampaigns();
    }
  }, [selectedEntityId]);

  const renderWhatsAppConnection = () => {
    return (
      <div className="whatsapp-connection-section">
        <h2>WhatsApp Connection</h2>
        <div className="connection-status-wrapper">
          <p>
            Status: <strong>{whatsAppStatus}</strong>
          </p>
          <div
            className={`status-indicator ${whatsAppStatus.toLowerCase()}`}
          ></div>
        </div>

        {isWhatsAppLoading && (
          <div className="loading-state">Processing...</div>
        )}

        {/* Emergency Cleanup History */}
        {emergencyCleanupEvents.length > 0 && (
          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '6px'
          }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', color: '#856404' }}>
              ⚠️ Emergency Cleanup History
            </h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: '#856404' }}>
              The following WhatsApp sessions were automatically disconnected due to memory emergencies.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #ffeaa7' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Entity</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Date/Time</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Memory</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Reason</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {emergencyCleanupEvents.map((event) => (
                    <tr key={event.id} style={{ borderBottom: '1px solid #ffeaa7' }}>
                      <td style={{ padding: '8px' }}>{event.entity_id}</td>
                      <td style={{ padding: '8px' }}>
                        {new Date(event.cleanup_timestamp).toLocaleString()}
                      </td>
                      <td style={{ padding: '8px' }}>
                        {event.memory_percentage}% ({event.memory_used_mb}MB)
                      </td>
                      <td style={{ padding: '8px' }}>
                        {event.reason}
                      </td>
                      <td style={{ padding: '8px' }}>
                        {event.recovered ? (
                          <span style={{ color: '#28a745' }}>✅ Recovered</span>
                        ) : (
                          <span style={{ color: '#dc3545' }}>❌ Disconnected</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={fetchEmergencyCleanupEvents}
              disabled={cleanupEventsLoading}
              style={{
                marginTop: '10px',
                padding: '5px 10px',
                fontSize: '0.8rem',
                cursor: cleanupEventsLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {cleanupEventsLoading ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
          </div>
        )}

        {(whatsAppStatus === "DISCONNECTED" ||
          whatsAppStatus === "AUTHENTICATING") && (
            <div className="disconnected-actions">
              <p className="status-message">
                {(() => {
                  const selectedEntity = entities.find((e) => e.id === selectedEntityId);
                  const isPlaceholderEntity = selectedEntity &&
                    (selectedEntity.id.includes("placeholder") || !selectedEntity.is_active);

                  if (isPlaceholderEntity) {
                    return `This is a placeholder entity for setting up a new WhatsApp Business account. To activate ${selectedEntity.name}, you'll need to configure a new WhatsApp Web worker service. Click "Refresh QR Code" to test the setup process.`;
                  }

                  return whatsAppStatus === "DISCONNECTED"
                    ? 'WhatsApp Web is not connected. Click "Refresh QR Code" to get a QR code to scan.'
                    : 'WhatsApp Web is trying to authenticate. Click "Refresh QR Code" to get a fresh QR code.';
                })()}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  onClick={handleRefreshQrCode}
                  disabled={isWhatsAppLoading || qrRateLimit.limited}
                  className="refresh-qr-button primary"
                >
                  {isWhatsAppLoading
                    ? "🔄 Generating QR Code..."
                    : "📱 Refresh QR Code"
                  }
                </button>
                {qrRateLimit.limited && (
                  <>
                    <span className="rate-limit-timer" style={{
                      fontWeight: 'bold',
                      color: '#dc3545',
                      fontSize: '0.9rem'
                    }}>
                      ⏱ Rate Limited ({countdown})
                    </span>
                    <div className="rate-limit-message" style={{
                      padding: '8px 12px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      color: '#856404',
                      whiteSpace: 'nowrap'
                    }}>
                      <strong>⚠️ Rate Limit Active</strong><br />
                      Too many QR requests. Please wait
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

        {qrCodeDataUrl &&
          (whatsAppStatus === "NEEDS_QR_CODE" ||
            whatsAppStatus === "AUTHENTICATING" ||
            whatsAppStatus === "DISCONNECTED") && (
            <div className="qr-code-container">
              <h3>Scan QR Code with WhatsApp Mobile</h3>
              <img
                src={qrCodeDataUrl}
                alt="WhatsApp QR Code"
                key={qrCodeImageKey} // Force image refresh with key change
              />

              {/* QR Code comparison information */}
              {isDecodingQr && (
                <div className="qr-decoding-status">
                  <p>🔍 Decoding QR code content...</p>
                </div>
              )}

              {/* QR Code Validation Results */}
              {qrValidationResult && (
                <div className="qr-validation-results">
                  <h4>🔍 QR Code Validation Test Results</h4>

                  <div className="validation-test-item">
                    <span className="test-name">QR Code Format</span>
                    <div className="test-result">
                      <span className={`test-status ${qrValidationResult.formatValid ? 'pass' : 'fail'}`}>
                        {qrValidationResult.formatValid ? '✅ PASS' : '❌ FAIL'}
                      </span>
                    </div>
                  </div>

                  <div className="validation-test-item">
                    <span className="test-name">QR Code Readable</span>
                    <div className="test-result">
                      <span className={`test-status ${qrValidationResult.isReadable ? 'pass' : 'fail'}`}>
                        {qrValidationResult.isReadable ? '✅ PASS' : '❌ FAIL'}
                      </span>
                    </div>
                  </div>

                  <div className="validation-test-item">
                    <span className="test-name">QR Code Valid</span>
                    <div className="test-result">
                      <span className={`test-status ${qrValidationResult.isValid ? 'pass' : 'fail'}`}>
                        {qrValidationResult.isValid ? '✅ PASS' : '❌ FAIL'}
                      </span>
                    </div>
                  </div>

                  <div className="validation-test-item">
                    <span className="test-name">Code Freshness</span>
                    <div className="test-result">
                      <span className={`freshness-indicator ${qrValidationResult.freshness || 'unknown'}`}>
                        {qrValidationResult.freshness?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </div>
                  </div>

                  {qrValidationResult.content && (
                    <div className="validation-test-item">
                      <span className="test-name">Content Changed</span>
                      <div className="test-result">
                        <span className={`test-status ${qrValidationResult.content !== previousQrCodeContent ? 'pass' : 'warning'}`}>
                          {qrValidationResult.content !== previousQrCodeContent ? '✅ NEW CODE' : '⚠️ SAME CODE'}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className={`validation-summary ${qrValidationResult.isValid && qrValidationResult.isReadable && qrValidationResult.formatValid
                    ? 'pass'
                    : 'fail'
                    }`}>
                    {qrValidationResult.isValid && qrValidationResult.isReadable && qrValidationResult.formatValid
                      ? '🎉 QR CODE VALIDATION PASSED - Fresh code detected!'
                      : '⚠️ QR CODE VALIDATION FAILED - Please refresh to get a new code'
                    }
                  </div>

                  {qrValidationResult.recommendations && qrValidationResult.recommendations.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <strong>Recommendations:</strong>
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        {qrValidationResult.recommendations.map((rec, index) => (
                          <li key={index} style={{ margin: '4px 0', color: '#4a5568' }}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {qrCodeContent && (
                <div className="qr-content-comparison">
                  <h4>QR Code Content Analysis</h4>
                  <div className="qr-current-content">
                    <strong>Current QR Code:</strong>
                    <code className="qr-code-text">{qrCodeContent}</code>
                  </div>

                  {previousQrCodeContent && (
                    <div className="qr-previous-content">
                      <strong>Previous QR Code:</strong>
                      <code className="qr-code-text">
                        {previousQrCodeContent}
                      </code>
                    </div>
                  )}

                  <div className="qr-comparison-result">
                    <strong>Status:</strong>
                    <span className={`qr-status ${getQrRefreshStatus()}`}>
                      {getQrRefreshStatusText()}
                    </span>
                  </div>
                </div>
              )}

              {/* QR Code timestamp information */}
              {qrTimestamp && (
                <div className="qr-timestamp-info">
                  <p className="qr-timestamp">
                    {getAgeStatusEmoji(qrAgeStatus)}
                    <strong>Generated:</strong>{" "}
                    {formatTimestampSGT(qrTimestamp)} (SGT)
                  </p>
                  <p className="qr-age">
                    <strong>Age:</strong> {qrAgeMinutes} minute
                    {qrAgeMinutes !== 1 ? "s" : ""} ago
                  </p>

                  {/* QR Code Comparison Status - Adjacent to Age */}
                  {qrCodeContent && (
                    <div className="qr-comparison-status">
                      <p className="qr-comparison-header">
                        <strong>QR Code Status:</strong>
                      </p>
                      <div className={`qr-comparison-badge ${getQrRefreshStatus()}`}>
                        {getQrRefreshStatus() === "changed" ? (
                          <span>
                            {!previousQrCodeContent ? (
                              <>✅ <strong>FRESH QR CODE</strong> - First scan or content changed</>
                            ) : (
                              <>✅ <strong>NEW QR CODE</strong> - Content has changed from previous</>
                            )}
                          </span>
                        ) : (
                          <span>
                            ⚠️ <strong>STALE QR CODE</strong> - Same content as previous scan
                          </span>
                        )}
                      </div>
                      {previousQrCodeContent && (
                        <div className="qr-content-preview">
                          <small>
                            <strong>Current:</strong> {qrCodeContent.substring(0, 30)}...
                            <br />
                            <strong>Previous:</strong> {previousQrCodeContent.substring(0, 30)}...
                            <br />
                            <strong>Match:</strong> {qrCodeContent === previousQrCodeContent ? "❌ Same content" : "✅ Different content"}
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="qr-refresh-hint">
                    💡 <strong>Tip:</strong> The QR code image URL includes a
                    timestamp parameter to prevent browser caching
                  </p>
                </div>
              )}

              <div className="qr-actions">
                <button
                  onClick={handleLogout}
                  disabled={isWhatsAppLoading}
                  className="logout-button"
                >
                  Cancel / Logout
                </button>
              </div>
            </div>
          )}

        {whatsAppStatus === "CONNECTED" && (
          <div className="connection-success">
            <h3>✅ Connected</h3>
            <p>WhatsApp client is connected and ready.</p>

            <div className="whatsapp-actions">
              <button
                onClick={handleSyncWhatsAppChats}
                disabled={syncingChats || isWhatsAppLoading}
                className="sync-chats-button"
              >
                {syncingChats ? "🔄 Syncing..." : "📥 Sync WhatsApp Chats"}
              </button>

              <div className="message-sync-section">
                <p className="sync-note">
                  💬 <strong>Message History Sync:</strong> Use the "💬 Sync
                  Messages" button on the main Chat Management page to sync
                  message history for your most recent chats using
                  fetchMessages().
                </p>
              </div>

              <button
                onClick={handleLogout}
                disabled={isWhatsAppLoading}
                className="logout-button"
              >
                Logout
              </button>
            </div>

            {lastSyncStats && (
              <div className="sync-stats">
                <h4>Last Chat Sync Status:</h4>
                {lastSyncStats.status === "completed" ? (
                  <p>
                    ✅ <strong>{lastSyncStats.synced}</strong> chats synced out
                    of <strong>{lastSyncStats.total}</strong>
                    {typeof lastSyncStats.totalMessages === "number" && (
                      <> (📨 {lastSyncStats.totalMessages} messages)</>
                    )}
                    {lastSyncStats.errors > 0 && (
                      <>
                        {" "}
                        | ⚠️ <strong>{lastSyncStats.errors}</strong> errors
                      </>
                    )}
                  </p>
                ) : lastSyncStats.status === "failed" ? (
                  <p>
                    ❌ Sync failed: {lastSyncStats.message || "Unknown error"}
                  </p>
                ) : (
                  <p>
                    ⏳ {lastSyncStats.message || "Sync in progress..."} ({" "}
                    {lastSyncStats.synced}/{lastSyncStats.total} chats synced)
                  </p>
                )}
                <small>
                  Chats are added to the "Unqualified" list in the Chat Board
                </small>
              </div>
            )}
          </div>
        )}

        {(whatsAppStatus === "INITIALIZING" ||
          whatsAppStatus === "AUTH_FAILURE") && (
            <button
              onClick={handleLogout}
              disabled={isWhatsAppLoading}
              className="logout-button"
            >
              Cancel / Logout
            </button>
          )}
      </div>
    );
  };

  const renderKnowledgeBase = () => {
    // Filter documents based on search
    const filteredDocs = kbDocuments.filter(doc => {
      const matchesSearch = !kbSearchQuery ||
        doc.original_filename?.toLowerCase().includes(kbSearchQuery.toLowerCase()) ||
        doc.description?.toLowerCase().includes(kbSearchQuery.toLowerCase()) ||
        doc.source?.toLowerCase().includes(kbSearchQuery.toLowerCase());
      return matchesSearch;
    });

    // Format file size
    const formatFileSize = (bytes: number) => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    // Format date
    const formatDate = (dateString: string) => {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
      <div className="knowledge-base-section">
        <div className="section-header">
          <h2>📚 Knowledge Base Management</h2>
          <p>Upload and manage documents for AI-powered responses</p>
        </div>

        {kbLoading ? (
          <div className="loading">Loading knowledge base...</div>
        ) : (
          <div className="knowledge-base-content">
            {/* Upload Form Section */}
            <div className="upload-form-section">
              <h3>📄 Upload New Document</h3>
              <div className="upload-form-grid">
                <div className="upload-file-area">
                  <input
                    type="file"
                    id="kb-file-upload"
                    accept=".txt,.md,.pdf,.docx,.html,.doc,.xls,.xlsx"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    disabled={kbUploading}
                  />
                  <label htmlFor="kb-file-upload" className="upload-button">
                    📁 Choose File
                  </label>
                  {selectedFile && (
                    <div className="selected-file-info">
                      <p>✅ {selectedFile.name} ({formatFileSize(selectedFile.size)})</p>
                    </div>
                  )}
                </div>

                <div className="upload-metadata-fields">
                  <div className="metadata-field">
                    <label className="metadata-label">Description:</label>
                    <textarea
                      placeholder="Brief description of the document content"
                      value={uploadMetadata.description}
                      onChange={(e) => setUploadMetadata({ ...uploadMetadata, description: e.target.value })}
                      disabled={kbUploading}
                      className="metadata-textarea"
                      rows={3}
                      maxLength={1000}
                    />
                  </div>
                  <div className="metadata-field">
                    <label className="metadata-label">Source:</label>
                    <input
                      type="text"
                      value={uploadMetadata.source}
                      onChange={(e) => setUploadMetadata({ ...uploadMetadata, source: e.target.value })}
                      disabled={kbUploading}
                      placeholder="e.g., ICA, MOM, Internal, URL"
                      className="metadata-input"
                      maxLength={200}
                    />
                  </div>
                  <div className="upload-action">
                    <button
                      className="btn btn-primary upload-submit"
                      onClick={handleUpload}
                      disabled={kbUploading || !selectedFile}
                    >
                      {kbUploading ? '⏳ Uploading...' : '📤 Upload'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="upload-hint">
                Supported formats: TXT, MD, PDF, DOC, DOCX, HTML, XLS, XLSX (max 10MB)
              </div>
            </div>

            {/* Documents Table Section */}
            <div className="documents-table-section">
              <div className="table-header">
                <h3>📋 Document Library ({filteredDocs.length} documents)</h3>
                <input
                  type="text"
                  placeholder="🔍 Search..."
                  className="search-input"
                  value={kbSearchQuery}
                  onChange={(e) => setKbSearchQuery(e.target.value)}
                />
              </div>

              {filteredDocs.length === 0 ? (
                <div className="empty-state">
                  <p>No documents found. Upload a document to get started.</p>
                </div>
              ) : (
                <table className="documents-table">
                  <thead>
                    <tr>
                      <th>File Name</th>
                      <th>Description</th>
                      <th>Source</th>
                      <th>Size</th>
                      <th>Uploaded Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocs.map((doc) => (
                      <tr key={doc.id}>
                        <td className="filename-cell">
                          📄 {doc.original_filename || doc.filename}
                        </td>
                        <td className="description-cell">
                          {doc.description || '-'}
                        </td>
                        <td className="source-cell">
                          <span className="source-badge">{doc.source || 'Unknown'}</span>
                        </td>
                        <td className="size-cell">
                          {formatFileSize(doc.file_size)}
                        </td>
                        <td className="date-cell">
                          {formatDate(doc.upload_date || doc.created_at)}
                        </td>
                        <td className="actions-cell">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleViewDocument(doc.id, doc.original_filename || doc.filename)}
                            title="View document content"
                          >
                            👁️ View
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEditDocumentById(doc.id, doc.original_filename || doc.filename)}
                            title="Edit document"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteDocument(doc.id, doc.original_filename || doc.filename)}
                            title="Delete document"
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Statistics Section */}
            <div className="statistics-section">
              <h3>📊 Knowledge Base Statistics</h3>
              {kbStats ? (
                <div className="stats-grid">
                  <div className="stat-card">
                    <h4>{kbStats.total_documents || 0}</h4>
                    <p>Total Documents</p>
                  </div>
                  <div className="stat-card">
                    <h4>{kbStats.total_chunks || 0}</h4>
                    <p>Text Chunks</p>
                  </div>
                  <div className="stat-card">
                    <h4>{kbStats.total_searches || 0}</h4>
                    <p>Total Searches</p>
                  </div>
                  <div className="stat-card">
                    <h4>{(kbDocuments.reduce((sum, doc) => sum + (doc.file_size || 0), 0) / (1024 * 1024)).toFixed(2)} MB</h4>
                    <p>Storage Used</p>
                  </div>
                </div>
              ) : (
                <div className="stats-grid">
                  <div className="stat-card">
                    <h4>{kbDocuments.length}</h4>
                    <p>Total Documents</p>
                  </div>
                  <div className="stat-card">
                    <h4>{kbDocuments.reduce((sum, doc) => sum + (doc.chunk_count || 0), 0)}</h4>
                    <p>Text Chunks</p>
                  </div>
                  <div className="stat-card">
                    <h4>{(kbDocuments.reduce((sum, doc) => sum + (doc.file_size || 0), 0) / (1024 * 1024)).toFixed(2)} MB</h4>
                    <p>Storage Used</p>
                  </div>
                </div>
              )}
            </div>

            {/* Document Viewer Modal */}
            {viewingDocument && (
              <div className="modal-overlay" onClick={() => isEditingDocument ? undefined : setViewingDocument(null)} style={isEditingDocument ? { pointerEvents: 'none' } : {}}>
                <div className="modal-content document-viewer-modal" onClick={(e) => e.stopPropagation()} style={isEditingDocument ? { pointerEvents: 'auto' } : {}}>
                  <div className="modal-header">
                    <h3>📄 {viewingDocument.filename}</h3>
                    <div className="modal-header-actions">
                      {!isEditingDocument ? (
                        <>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={handleEditDocument}
                            style={{ marginRight: '10px' }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => setViewingDocument(null)}
                          >
                            ✕ Close
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={handleSaveDocument}
                            style={{ marginRight: '10px' }}
                          >
                            💾 Save
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={handleCancelEdit}
                            style={{ marginRight: '10px' }}
                          >
                            ✕ Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="modal-body">
                    {isEditingDocument ? (
                      <>
                        <div className="edit-metadata-section" style={{ marginBottom: '15px' }}>
                          <div className="form-group" style={{ marginBottom: '10px' }}>
                            <label>Description:</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedDescription}
                              onChange={(e) => setEditedDescription(e.target.value)}
                              placeholder="Enter description"
                              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                          </div>
                          <div className="form-group">
                            <label>Source:</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedSource}
                              onChange={(e) => setEditedSource(e.target.value)}
                              placeholder="Enter source"
                              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                            />
                          </div>
                        </div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Content:</label>
                        <div className="document-editor-wrapper">
                          <ReactQuill
                            theme="snow"
                            value={editedContent}
                            onChange={setEditedContent}
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                [{ 'indent': '-1' }, { 'indent': '+1' }],
                                [{ 'align': [] }],
                                ['clean'],
                              ],
                              clipboard: {
                                matchVisual: false,
                              }
                            }}
                            formats={[
                              'header', 'font', 'size',
                              'bold', 'italic', 'underline', 'strike', 'blockquote',
                              'list', 'bullet', 'indent',
                              'link', 'image',
                              'align'
                            ]}
                            style={{
                              marginBottom: '10px'
                            }}
                            placeholder="Paste your content here. Word bullet points and formatting are automatically preserved..."
                          />
                        </div>
                        <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                          💡 Tip: Paste from Word - bullet points, numbered lists, and formatting are automatically preserved
                        </div>
                      </>
                    ) : (
                      <>
                        {(viewingDocument.description || viewingDocument.source) && (
                          <div className="document-metadata" style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                            {viewingDocument.description && (
                              <div style={{ marginBottom: '5px' }}>
                                <strong>Description:</strong> {viewingDocument.description}
                              </div>
                            )}
                            {viewingDocument.source && (
                              <div>
                                <strong>Source:</strong> {viewingDocument.source}
                              </div>
                            )}
                          </div>
                        )}
                        <div
                          className="document-content-viewer"
                          dangerouslySetInnerHTML={{ __html: viewingDocument.content }}
                          style={{
                            padding: '15px',
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            lineHeight: '1.6',
                            minHeight: '100px'
                          }}
                        />
                        <style>{`
                          .document-content-viewer ul {
                            list-style-type: disc;
                            padding-left: 20px;
                            margin: 10px 0;
                          }
                          .document-content-viewer ol {
                            list-style-type: decimal;
                            padding-left: 20px;
                            margin: 10px 0;
                          }
                          .document-content-viewer li {
                            margin: 5px 0;
                          }
                        `}</style>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderLearningSystem = () => {
    return (
      <div className="learning-system-section">
        <div className="section-header">
          <h2>🧠 Continuous Learning System</h2>
          <p>Automatically extract Q&A pairs from chat history to improve the Lead Qualifying Agent's knowledge base</p>
        </div>

        {/* System Status Overview */}
        <div className="learning-status-card">
          <h3>📊 Database Statistics</h3>
          {loadingLearning ? (
            <div className="loading-state">Loading learning system status...</div>
          ) : learningStatistics ? (
            <div className="status-grid">
              <div className="status-section">
                <h4>💬 Chat Messages</h4>
                <div className="status-item">
                  <span className="status-label">Total Messages Available:</span>
                  <span className="status-value">{learningStatistics.messages?.total || 0}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Messages Processed:</span>
                  <span className="status-value">{learningStatistics.messages?.processed || 0}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Messages Remaining:</span>
                  <span className="status-value warning">{learningStatistics.messages?.remaining || 0}</span>
                </div>
              </div>

              <div className="status-section">
                <h4>💬 Chats</h4>
                <div className="status-item">
                  <span className="status-label">Total Chats:</span>
                  <span className="status-value">{learningStatistics.chats?.total || 0}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Chats Processed:</span>
                  <span className="status-value">{learningStatistics.chats?.processed || 0}</span>
                </div>
              </div>

              <div className="status-section">
                <h4>🤖 Q&A Pairs Generated</h4>
                <div className="status-item">
                  <span className="status-label">Total Q&A Pairs:</span>
                  <span className="status-value">{learningStatistics.qaPairs?.total || 0}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Auto-Approved:</span>
                  <span className="status-value success">{learningStatistics.qaPairs?.autoApproved || 0}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Needs Review:</span>
                  <span className="status-value warning">{learningStatistics.qaPairs?.needsReview || 0}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Rejected:</span>
                  <span className="status-value error">{learningStatistics.qaPairs?.rejected || 0}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Last Processing:</span>
                  <span className="status-value">
                    {learningStatistics.last_processing_date
                      ? new Date(learningStatistics.last_processing_date).toLocaleString('en-US', {
                        timeZone: 'Asia/Singapore',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })
                      : 'Never'
                    }
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-data">No learning data available yet. Run your first learning process to see statistics.</div>
          )}
        </div>

        {/* Manual Processing Controls */}
        <div className="learning-controls-card">
          <h3>🚀 Processing Controls</h3>
          <div className="controls-section">
            <button
              className="btn btn-primary"
              onClick={startManualLearningRun}
              disabled={runningLearning}
            >
              {runningLearning ? '⏳ Processing...' : '▶️ Start Manual Learning Run'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={loadLearningData}
              disabled={loadingLearning}
            >
              🔄 Refresh Status
            </button>
            <button
              className="btn btn-info"
              onClick={() => {
                setShowQAPairs(!showQAPairs);
                if (!showQAPairs && qaPairs.length === 0) {
                  loadQAPairs(qaPairFilter);
                }
              }}
            >
              {showQAPairs ? '🔽 Hide Q&A Pairs' : '👁️ View Generated Q&A Pairs'}
            </button>
          </div>
          <div className="processing-info">
            <p><strong>What this does:</strong></p>
            <ul>
              <li>✅ Extracts Q&A pairs from recent chat conversations</li>
              <li>🌐 Translates non-English content to English</li>
              <li>🤖 Uses {activeAiModel ? `${activeAiModel.model_name} (${activeAiModel.model_provider})` : 'AI Model'} to validate immigration relevance</li>
              <li>📚 Adds approved Q&As to the knowledge base automatically</li>
              <li>👥 Queues uncertain cases for manual review</li>
            </ul>
          </div>
        </div>

        {/* AI Configuration Section */}
        <div className="ai-config-card">
          <div className="section-header">
            <h3>🤖 AI Model & Prompt Configuration</h3>
            <p>Configure AI models and prompts for Q&A extraction and relevance validation</p>
          </div>

          <div className="ai-config-controls">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowAiConfig(!showAiConfig);
                if (!showAiConfig) {
                  loadAiConfiguration();
                }
              }}
              disabled={loadingAiConfig}
            >
              {loadingAiConfig ? '⏳ Loading...' : (showAiConfig ? '🔽 Hide AI Configuration' : '⚙️ Show AI Configuration')}
            </button>
          </div>

          {/* Current Active Configuration Display */}
          {activeAiModel && (
            <div className="current-config-display">
              <h4>📊 Current Active Configuration</h4>
              <div className="config-info">
                <div className="config-item">
                  <span className="config-label">Active Model:</span>
                  <span className="config-value">{activeAiModel.model_name}</span>
                </div>
                <div className="config-item">
                  <span className="config-label">Provider:</span>
                  <span className="config-value">{activeAiModel.model_provider}</span>
                </div>
                <div className="config-item">
                  <span className="config-label">Temperature:</span>
                  <span className="config-value">{activeAiModel.temperature}</span>
                </div>
                <div className="config-item">
                  <span className="config-label">Max Tokens:</span>
                  <span className="config-value">{activeAiModel.max_tokens}</span>
                </div>
              </div>
            </div>
          )}

          {/* AI Configuration Panel */}
          {showAiConfig && (
            <div className="ai-config-panel">

              {/* Model Selection Section */}
              <div className="model-section">
                <h4>🔧 Available AI Models</h4>
                <div className="models-list">
                  {aiModels.map((model) => (
                    <div key={model.id} className={`model-item ${model.is_active ? 'active' : ''}`}>
                      <div className="model-info">
                        <div className="model-name">
                          {model.config_name}
                          {model.is_active && <span className="active-badge">ACTIVE</span>}
                        </div>
                        <div className="model-details">
                          <span>{model.model_name} ({model.model_provider})</span>
                          <span>Temp: {model.temperature}</span>
                          <span>Tokens: {model.max_tokens}</span>
                        </div>
                        {model.description && (
                          <div className="model-description">{model.description}</div>
                        )}
                      </div>
                      <div className="model-actions">
                        {!model.is_active && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => activateModel(model.id)}
                          >
                            ✅ Activate
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => deleteModel(model.id)}
                          disabled={model.is_active}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add New Model Section */}
              <div className="add-model-section">
                <h4>➕ Add New AI Model</h4>
                <div className="new-model-form">
                  <div className="form-row">
                    <div className="form-field">
                      <label>Configuration Name:</label>
                      <input
                        type="text"
                        value={newModelForm.config_name}
                        onChange={(e) => setNewModelForm({ ...newModelForm, config_name: e.target.value })}
                        placeholder="e.g., my-custom-model"
                      />
                    </div>
                    <div className="form-field">
                      <label>Provider:</label>
                      <select
                        value={newModelForm.model_provider}
                        onChange={(e) => setNewModelForm({ ...newModelForm, model_provider: e.target.value })}
                      >
                        <option value="gemini">Google Gemini</option>
                        <option value="openai">OpenAI</option>
                        <option value="openrouter">OpenRouter</option>
                        <option value="custom">Custom Provider</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label>Model Name:</label>
                      <input
                        type="text"
                        value={newModelForm.model_name}
                        onChange={(e) => setNewModelForm({ ...newModelForm, model_name: e.target.value })}
                        placeholder="e.g., gemini-1.5-pro or gpt-4o"
                      />
                    </div>
                    <div className="form-field">
                      <label>Temperature:</label>
                      <input
                        type="number"
                        min="0"
                        max="2"
                        step="0.1"
                        value={newModelForm.temperature}
                        onChange={(e) => setNewModelForm({ ...newModelForm, temperature: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label>API Endpoint:</label>
                    <input
                      type="url"
                      value={newModelForm.api_endpoint}
                      onChange={(e) => setNewModelForm({ ...newModelForm, api_endpoint: e.target.value })}
                      placeholder="https://api.example.com/v1/models/model-name:generateContent"
                    />
                  </div>

                  <div className="form-field">
                    <label>Description:</label>
                    <textarea
                      value={newModelForm.description}
                      onChange={(e) => setNewModelForm({ ...newModelForm, description: e.target.value })}
                      placeholder="Brief description of this model configuration"
                      rows={2}
                    />
                  </div>

                  <button
                    className="btn btn-primary"
                    onClick={createNewModel}
                    disabled={!newModelForm.config_name || !newModelForm.model_name || !newModelForm.api_endpoint}
                  >
                    💾 Save New Model
                  </button>
                </div>
              </div>

              {/* Prompt Templates Section */}
              <div className="prompts-section">
                <h4>📝 Active Prompt Templates</h4>
                <div className="prompts-list">
                  {Object.entries(activePromptTemplates).map(([type, prompt]: [string, any]) => (
                    <div key={type} className="prompt-item">
                      <div className="prompt-header">
                        <span className="prompt-type">{type.replace('_', ' ').toUpperCase()}</span>
                        <span className="prompt-version">v{prompt?.version_number || 1}</span>
                      </div>
                      <div className="prompt-name">{prompt?.prompt_name || 'Default Template'}</div>
                      <div className="prompt-stats">
                        <span>Used: {prompt?.usage_count || 0} times</span>
                        <span>Success: {Math.round((prompt?.success_count / Math.max(prompt?.usage_count, 1)) * 100) || 0}%</span>
                      </div>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openPromptEditor(prompt, type)}
                      >
                        ✏️ Edit/View
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Q&A Pairs Display */}
        {showQAPairs && (
          <div className="learning-qa-pairs-card">
            <div className="qa-pairs-header">
              <h3>🤖 Generated Q&A Pairs</h3>
              <div className="qa-pairs-filters">
                <label>Filter by Status:</label>
                <select
                  value={qaPairFilter}
                  onChange={(e) => {
                    const newFilter = e.target.value as 'all' | 'auto_approved' | 'needs_review' | 'rejected';
                    setQaPairFilter(newFilter);
                    loadQAPairs(newFilter);
                  }}
                  className="filter-select"
                >
                  <option value="all">🔍 All Q&A Pairs</option>
                  <option value="auto_approved">✅ Auto-Approved</option>
                  <option value="needs_review">⏳ Needs Review</option>
                  <option value="rejected">❌ Rejected</option>
                </select>
              </div>
            </div>
            {qaPairs.length > 0 ? (
              <div className="qa-pairs-list">
                {qaPairs.map((qaPair) => (
                  <div key={qaPair.id} className="qa-pair-item">
                    <div className="qa-pair-header">
                      <span className="qa-pair-id">#{qaPair.id}</span>
                      <span className={`qa-pair-status ${qaPair.validation_status}`}>
                        {qaPair.validation_status === 'auto_approved' && '✅ Auto-Approved'}
                        {qaPair.validation_status === 'needs_review' && '⏳ Needs Review'}
                        {qaPair.validation_status === 'rejected' && '❌ Rejected'}
                      </span>
                      <span className="qa-pair-confidence">
                        {Math.round((qaPair.relevance_confidence || 0) * 100)}% confidence
                      </span>
                    </div>
                    <div className="qa-pair-content">
                      <div className="question">
                        <strong>❓ Question:</strong>
                        <p>{qaPair.question}</p>
                      </div>
                      <div className="answer">
                        <strong>💬 Answer:</strong>
                        <p>{qaPair.answer}</p>
                      </div>
                      {qaPair.original_language && qaPair.was_translated && (
                        <div className="translation-info">
                          <small>🌐 Translated from {qaPair.original_language}</small>
                        </div>
                      )}
                      {qaPair.immigration_categories && qaPair.immigration_categories.length > 0 && (
                        <div className="categories">
                          <strong>🏷️ Categories:</strong>
                          <div className="category-tags">
                            {qaPair.immigration_categories.map((cat: string) => (
                              <span key={cat} className="category-tag">{cat}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="qa-pair-meta">
                        <small>📅 Created: {new Date(qaPair.created_at).toLocaleString()}</small>
                        {qaPair.run_type && <small> | 🔄 Run Type: {qaPair.run_type}</small>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-qa-pairs">
                No Q&A pairs generated yet. Run the learning process to extract Q&A pairs from your chat history.
              </div>
            )}
          </div>
        )}

        {/* Manual Review Section for Q&A Pairs Needing Review */}
        <div className="manual-review-card">
          <div className="section-header">
            <h3>⏳ Q&A Pairs Needing Review</h3>
            <p>Review Q&A pairs that the AI marked as uncertain and approve or reject them for the knowledge base</p>
          </div>

          <div className="review-controls">
            <button
              className="btn btn-secondary"
              onClick={loadNeedsReviewQAPairs}
              disabled={loadingNeedsReview}
            >
              {loadingNeedsReview ? '⏳ Loading...' : '🔄 Load Q&A Pairs Needing Review'}
            </button>

            {selectedNeedsReviewPairs.size > 0 && (
              <>
                <button
                  className="btn btn-success"
                  onClick={bulkApproveNeedsReviewSelected}
                  disabled={bulkProcessingNeedsReview || !reviewerName.trim()}
                >
                  {bulkProcessingNeedsReview ? '⏳ Processing...' : `✅ Approve Selected (${selectedNeedsReviewPairs.size})`}
                </button>
              </>
            )}
          </div>

          {/* Instructions and Reviewer Input */}
          <div className="review-instructions">
            <div className="instructions-content">
              <h4>📋 Review Instructions</h4>
              <ol>
                <li><strong>Load Data:</strong> Click "Load Q&A Pairs Needing Review" to fetch pairs that need manual review</li>
                <li><strong>Enter Reviewer Name:</strong> Your name is required for audit trails and feedback learning</li>
                <li><strong>Review Individual Pairs:</strong> Read each Q&A pair and check service area classification</li>
                <li><strong>Make Decision:</strong> Click ✅ Approve to include in knowledge base or ❌ Reject to exclude</li>
                <li><strong>Bulk Operations:</strong> Select multiple pairs and use bulk approve for efficiency</li>
              </ol>
            </div>

            <div className="reviewer-input-section">
              <div className="reviewer-input">
                <label htmlFor="reviewerName">👤 Reviewer Name (Required):</label>
                <input
                  id="reviewerName"
                  type="text"
                  placeholder="Enter your name for audit trail..."
                  value={reviewerName}
                  onChange={(e) => setReviewerName(e.target.value)}
                  className={`reviewer-name-input ${!reviewerName.trim() ? 'invalid' : 'valid'}`}
                />
                {!reviewerName.trim() && (
                  <small className="input-error">⚠️ Reviewer name is required for all approval/rejection actions</small>
                )}
              </div>
            </div>
          </div>

          {/* Q&A Pairs Needing Review List */}
          {needsReviewQAPairs.length > 0 ? (
            <div>
              <div className="list-header">
                <div className="bulk-select">
                  <input
                    type="checkbox"
                    checked={selectedNeedsReviewPairs.size === needsReviewQAPairs.length && needsReviewQAPairs.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedNeedsReviewPairs(new Set(needsReviewQAPairs.map(qa => qa.id)));
                      } else {
                        setSelectedNeedsReviewPairs(new Set());
                      }
                    }}
                    disabled={bulkProcessingNeedsReview}
                  />
                  <label>Select All ({needsReviewQAPairs.length} items needing review)</label>
                </div>
              </div>

              <div className="needs-review-qa-list">
                {needsReviewQAPairs.map((qaPair) => (
                  <div key={qaPair.id} className={`needs-review-qa-item ${selectedNeedsReviewPairs.has(qaPair.id) ? 'selected' : ''}`}>
                    <div className="qa-item-header">
                      <div className="selection-controls">
                        <input
                          type="checkbox"
                          checked={selectedNeedsReviewPairs.has(qaPair.id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedNeedsReviewPairs);
                            if (e.target.checked) {
                              newSelected.add(qaPair.id);
                            } else {
                              newSelected.delete(qaPair.id);
                            }
                            setSelectedNeedsReviewPairs(newSelected);
                          }}
                          disabled={bulkProcessingNeedsReview}
                        />
                        <span className="qa-id">#{qaPair.id}</span>
                      </div>
                      <span className="review-info">
                        ⏳ Needs Review ({Math.round((qaPair.relevance_confidence || 0) * 100)}% confidence)
                      </span>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-success individual-approve"
                          onClick={() => approveNeedsReviewQAPair(qaPair.id)}
                          disabled={bulkProcessingNeedsReview || !reviewerName.trim()}
                        >
                          ✅ Approve
                        </button>
                        <button
                          className="btn btn-sm btn-danger individual-reject"
                          onClick={() => rejectNeedsReviewQAPair(qaPair.id)}
                          disabled={bulkProcessingNeedsReview || !reviewerName.trim()}
                        >
                          ❌ Reject
                        </button>
                      </div>
                    </div>

                    {/* Service Area Classification */}
                    <div className="service-area-selection">
                      <strong>🏢 Service Area Classification:</strong>
                      <div className="checkbox-group">
                        <label className="service-checkbox">
                          <input
                            type="checkbox"
                            checked={needsReviewServiceAreaSelections[qaPair.id]?.immigration || false}
                            onChange={(e) => {
                              setNeedsReviewServiceAreaSelections(prev => ({
                                ...prev,
                                [qaPair.id]: {
                                  ...prev[qaPair.id],
                                  immigration: e.target.checked,
                                  workPass: prev[qaPair.id]?.workPass || false
                                }
                              }));
                            }}
                            disabled={bulkProcessingNeedsReview}
                          />
                          🛂 Immigration/ICA
                        </label>
                        <label className="service-checkbox">
                          <input
                            type="checkbox"
                            checked={needsReviewServiceAreaSelections[qaPair.id]?.workPass || false}
                            onChange={(e) => {
                              setNeedsReviewServiceAreaSelections(prev => ({
                                ...prev,
                                [qaPair.id]: {
                                  immigration: prev[qaPair.id]?.immigration || false,
                                  workPass: e.target.checked
                                }
                              }));
                            }}
                            disabled={bulkProcessingNeedsReview}
                          />
                          💼 Work Passes/MOM
                        </label>
                      </div>
                    </div>

                    <div className="qa-content">
                      <div className="question-section">
                        <strong>❓ Question:</strong>
                        <p>{qaPair.question}</p>
                      </div>
                      <div className="answer-section">
                        <strong>💬 Answer:</strong>
                        <p>{qaPair.answer}</p>
                      </div>
                      {qaPair.relevance_explanation && (
                        <div className="ai-explanation">
                          <strong>🤖 AI Analysis:</strong>
                          <p className="explanation-text">{qaPair.relevance_explanation}</p>
                        </div>
                      )}
                      <div className="qa-metadata">
                        <small>📅 Created: {new Date(qaPair.created_at).toLocaleString()}</small>
                        <small>📍 Chat: {qaPair.source_chat_id}</small>
                        {qaPair.manual_reviewer && (
                          <small>👤 Last Reviewer: {qaPair.manual_reviewer}</small>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-needs-review-pairs">
              {loadingNeedsReview ? (
                <div className="loading-state">Loading Q&A pairs needing review...</div>
              ) : (
                <div className="empty-state">
                  🎉 No Q&A pairs need review. All pairs have clear AI confidence scores.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Manual Review Section for Rejected Q&A Pairs */}
        <div className="manual-review-card">
          <div className="section-header">
            <h3>👥 Manual Review & Approval</h3>
            <p>Review rejected Q&A pairs and approve those that should be included in the knowledge base</p>
          </div>

          <div className="review-controls">
            <button
              className="btn btn-secondary"
              onClick={loadRejectedQAPairs}
              disabled={loadingRejected}
            >
              {loadingRejected ? '⏳ Loading...' : '🔄 Load Rejected Q&A Pairs'}
            </button>

            {selectedQAPairs.size > 0 && (
              <button
                className="btn btn-success"
                onClick={bulkApproveSelected}
                disabled={bulkApproving || !reviewerName.trim()}
              >
                {bulkApproving ? '⏳ Approving...' : `✅ Approve Selected (${selectedQAPairs.size})`}
              </button>
            )}
          </div>

          {/* Instructions and Reviewer Input */}
          <div className="review-instructions">
            <div className="instruction-box">
              <h4>📋 How to Review & Approve Q&A Pairs:</h4>
              <ol>
                <li><strong>Step 1:</strong> Enter your reviewer name below (required for all approvals)</li>
                <li><strong>Step 2:</strong> Click "Load Rejected Q&A Pairs" to fetch items that need review</li>
                <li><strong>Step 3:</strong> Review each Q&A pair and select those you want to approve</li>
                <li><strong>Step 4:</strong> Use "Approve" buttons to approve individual items or multiple at once</li>
              </ol>
            </div>
          </div>

          <div className="reviewer-input-section">
            <label className="reviewer-label">
              👤 <strong>Your Reviewer Name (Required for All Approvals)</strong>
            </label>
            <input
              type="text"
              value={reviewerName}
              onChange={(e) => setReviewerName(e.target.value)}
              className="reviewer-name-input"
              placeholder="Enter your full name (e.g., John Smith)"
              disabled={bulkApproving}
            />
            {!reviewerName.trim() && (
              <div className="reviewer-warning">
                ⚠️ <strong>Required:</strong> Please enter your name to enable approval buttons
              </div>
            )}
            {reviewerName.trim() && (
              <div className="reviewer-success">
                ✅ Ready to approve as: <strong>{reviewerName}</strong>
              </div>
            )}
          </div>

          {/* Manual Review Statistics */}
          {manualReviewStats && (
            <div className="review-stats">
              <h4>📊 Manual Review Statistics</h4>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Pending Reviews:</span>
                  <span className="stat-value warning">{manualReviewStats.pending_reviews || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Manual Approvals:</span>
                  <span className="stat-value success">{manualReviewStats.manual_approvals || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">AI Overrides:</span>
                  <span className="stat-value info">{manualReviewStats.ai_overrides || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Active Reviewers:</span>
                  <span className="stat-value">{manualReviewStats.active_reviewers || 0}</span>
                </div>
              </div>
            </div>
          )}

          {/* Rejected Q&A Pairs List */}
          {rejectedQAPairs.length > 0 ? (
            <div>
              <div className="list-header">
                <div className="bulk-select">
                  <input
                    type="checkbox"
                    checked={selectedQAPairs.size === rejectedQAPairs.length && rejectedQAPairs.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedQAPairs(new Set(rejectedQAPairs.map(qa => qa.id)));
                      } else {
                        setSelectedQAPairs(new Set());
                      }
                    }}
                    disabled={bulkApproving}
                  />
                  <label>Select All ({rejectedQAPairs.length} items)</label>
                </div>
              </div>

              <div className="rejected-qa-list">
                {rejectedQAPairs.map((qaPair) => (
                  <div key={qaPair.id} className={`rejected-qa-item ${selectedQAPairs.has(qaPair.id) ? 'selected' : ''}`}>
                    <div className="qa-item-header">
                      <div className="selection-controls">
                        <input
                          type="checkbox"
                          checked={selectedQAPairs.has(qaPair.id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedQAPairs);
                            if (e.target.checked) {
                              newSelected.add(qaPair.id);
                            } else {
                              newSelected.delete(qaPair.id);
                            }
                            setSelectedQAPairs(newSelected);
                          }}
                          disabled={bulkApproving}
                        />
                        <span className="qa-id">#{qaPair.id}</span>
                      </div>
                      <span className="rejection-info">
                        ❌ Rejected by AI ({Math.round((qaPair.relevance_confidence || 0) * 100)}% confidence)
                      </span>
                      <button
                        className="btn btn-sm btn-success individual-approve"
                        onClick={() => approveIndividualQAPair(qaPair.id)}
                        disabled={bulkApproving || !reviewerName.trim()}
                      >
                        ✅ Approve
                      </button>
                    </div>

                    {/* Service Area Classification - Moved to top for better space usage */}
                    <div className="service-area-selection">
                      <strong>🏢 Service Area Classification:</strong>
                      <div className="checkbox-group">
                        <label className="service-checkbox">
                          <input
                            type="checkbox"
                            checked={serviceAreaSelections[qaPair.id]?.immigration || false}
                            onChange={(e) => {
                              setServiceAreaSelections(prev => ({
                                ...prev,
                                [qaPair.id]: {
                                  ...prev[qaPair.id],
                                  immigration: e.target.checked,
                                  workPass: prev[qaPair.id]?.workPass || false
                                }
                              }));
                            }}
                            disabled={bulkApproving}
                          />
                          🛂 Immigration/ICA
                        </label>
                        <label className="service-checkbox">
                          <input
                            type="checkbox"
                            checked={serviceAreaSelections[qaPair.id]?.workPass || false}
                            onChange={(e) => {
                              setServiceAreaSelections(prev => ({
                                ...prev,
                                [qaPair.id]: {
                                  immigration: prev[qaPair.id]?.immigration || false,
                                  workPass: e.target.checked
                                }
                              }));
                            }}
                            disabled={bulkApproving}
                          />
                          💼 Work Passes/MOM
                        </label>
                      </div>
                    </div>

                    <div className="qa-content">
                      <div className="question-section">
                        <strong>❓ Question:</strong>
                        <p>{qaPair.question}</p>
                      </div>
                      <div className="answer-section">
                        <strong>💬 Answer:</strong>
                        <p>{qaPair.answer}</p>
                      </div>
                      {qaPair.relevance_explanation && (
                        <div className="ai-explanation">
                          <strong>🤖 AI Rejection Reason:</strong>
                          <p className="explanation-text">{qaPair.relevance_explanation}</p>
                        </div>
                      )}
                      <div className="qa-metadata">
                        <small>📅 Created: {new Date(qaPair.created_at).toLocaleString()}</small>
                        <small>📍 Chat: {qaPair.source_chat_id}</small>
                        {qaPair.manual_reviewer && (
                          <small>👤 Last Reviewer: {qaPair.manual_reviewer}</small>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-rejected-pairs">
              {loadingRejected ? (
                <div className="loading-state">Loading rejected Q&A pairs...</div>
              ) : (
                <div className="empty-state">
                  🎉 No rejected Q&A pairs found. All AI decisions are accurate or no data available yet.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent Learning Runs */}
        <div className="learning-runs-card">
          <h3>📈 Recent Learning Runs</h3>
          {learningRuns.length > 0 ? (
            <div className="runs-list">
              {learningRuns.slice(0, 5).map((run) => (
                <div key={run.id} className="run-item">
                  <div className="run-header">
                    <span className="run-id">Run #{run.id}</span>
                    <span className={`run-status status-${run.status}`}>
                      {run.status.toUpperCase()}
                    </span>
                    <span className="run-date">
                      {new Date(run.started_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="run-stats">
                    <span>Messages: {run.messages_processed}</span>
                    <span>Q&As: {run.qa_pairs_extracted}</span>
                    <span>Approved: {run.qa_pairs_auto_approved}</span>
                    <span>Review: {run.qa_pairs_needs_review}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-runs">
              No learning runs completed yet. Start your first run to begin continuous learning!
            </div>
          )}
        </div>

        {/* Configuration */}
        <div className="learning-config-card">
          <h3>⚙️ Learning Configuration</h3>
          {learningConfig ? (
            <div className="config-grid">
              <div className="config-item">
                <label>Auto-Approval Threshold:</label>
                <span>{Math.round((learningConfig.auto_approval_threshold || 0.85) * 100)}%</span>
              </div>
              <div className="config-item">
                <label>Review Threshold (Min):</label>
                <span>{Math.round((learningConfig.review_threshold_min || 0.60) * 100)}%</span>
              </div>
              <div className="config-item">
                <label>Max Messages Per Run:</label>
                <span>{learningConfig.max_messages_per_run || 1000}</span>
              </div>
              <div className="config-item">
                <label>Weekly Schedule:</label>
                <span className={`schedule-status ${learningConfig.schedule_enabled ? 'enabled' : 'disabled'}`}>
                  {learningConfig.schedule_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="config-item">
                <label>Schedule Time:</label>
                <span>{learningConfig.schedule_cron || 'Monday 2:00 AM'}</span>
              </div>
              <div className="config-item">
                <label>Gemini Model:</label>
                <span>{learningConfig.gemini_model || 'gemini-2.5-pro'}</span>
              </div>
            </div>
          ) : (
            <div className="loading-state">Loading configuration...</div>
          )}
        </div>
      </div>
    );
  };

  // URL Management Functions
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const loadUrlsData = async () => {
    setUrlsLoading(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/scraped-urls', { headers: authHeaders });
      const urlsData = await response.json();
      // Extract URLs array from API response
      setScrapedUrls(urlsData.urls || []);

      const statsResponse = await fetch('/api/scraping-stats', { headers: authHeaders });
      const statsData = await statsResponse.json();

      // Transform API response to match ScrapingStats interface
      if (statsData.ok && statsData.stats) {
        const transformedStats = {
          totalUrls: parseInt(statsData.stats.urls.total_urls) || 0,
          activeUrls: parseInt(statsData.stats.urls.scraped_urls) || 0,
          failedUrls: parseInt(statsData.stats.urls.pending_urls) || 0,
          changesDetected: 0, // TODO: Add this to API response
          lastRunTime: statsData.stats.urls.last_scrape_time || undefined
        };
        setScrapingStats(transformedStats);
      }
    } catch (error) {
      console.error('Failed to load URLs data:', error);
      alert('Failed to load URLs data. Please try again.');
    } finally {
      setUrlsLoading(false);
    }
  };

  const handleManualScrapingUpdate = async () => {
    setManualScrapingRunning(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/manual-rag-update', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Manual scraping update started successfully! This may take a few minutes.');
        // Refresh data after a delay
        setTimeout(loadUrlsData, 5000);
      } else {
        throw new Error('Failed to start manual scraping');
      }
    } catch (error) {
      console.error('Failed to start manual scraping:', error);
      alert('Failed to start manual scraping. Please try again.');
    } finally {
      setManualScrapingRunning(false);
    }
  };

  const handleUrlOnlyScraping = async () => {
    setUrlOnlyScrapingRunning(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch('/api/scrape-urls-only', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('URL scraping started successfully! Content will be fetched without RAG processing.');
        // Refresh data after a delay
        setTimeout(loadUrlsData, 5000);
      } else {
        throw new Error('Failed to start URL scraping');
      }
    } catch (error) {
      console.error('Failed to start URL scraping:', error);
      alert('Failed to start URL scraping. Please try again.');
    } finally {
      setUrlOnlyScrapingRunning(false);
    }
  };

  const handleViewContent = async (urlId: number) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/scraped-content/${urlId}`, {
        headers: authHeaders
      });
      const data = await response.json();

      if (data.ok) {
        setSelectedUrlData(data);
        setShowContentModal(true);
      } else {
        alert('Failed to fetch content: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
      alert('Failed to fetch content. Please try again.');
    }
  };

  const handleProcessChunks = async (urlId: number) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/process-url-chunks/${urlId}`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.ok) {
        alert('Chunk processing started successfully! Content will be processed using Gemini model.');
        // Refresh the content view
        setTimeout(() => handleViewContent(urlId), 3000);
      } else {
        alert('Failed to process chunks: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to process chunks:', error);
      alert('Failed to process chunks. Please try again.');
    }
  };

  const handleAddUrl = async () => {
    if (!newUrl.url.trim()) return;

    setUrlsLoading(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      // Step 1: Add URL to database
      const response = await fetch('/api/scraped-urls', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUrl)
      });

      if (!response.ok) {
        throw new Error('Failed to add URL');
      }

      const data = await response.json();
      const newUrlId = data.url.id;

      // Step 2: Automatically trigger scraping of the new URL
      // console.log('[URL Management] Automatically scraping new URL:', { urlId: newUrlId, url: newUrl.url });

      const scrapeResponse = await fetch('/api/scrape-single-url', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ urlId: newUrlId })
      });

      if (scrapeResponse.ok) {
        const scrapeData = await scrapeResponse.json();
        alert(`URL added and automatically scraped!\n\nURL: ${newUrl.url}\nStatus: ${scrapeData.status}\nChunks: ${scrapeData.chunkCount || 0}\nChanged: ${scrapeData.changed ? 'Yes' : 'No'}`);
      } else {
        console.warn('[URL Management] Automatic scraping failed, but URL was added');
        alert('URL added successfully, but automatic scraping failed. You can trigger it manually.');
      }

      // Step 3: Reset form and reload data
      setNewUrl({ url: '', title: '', description: '', category: 'citizenship', source: 'ica', priority: 1 });
      await loadUrlsData();

    } catch (error) {
      console.error('Failed to add URL:', error);
      alert('Failed to add URL. Please try again.');
    } finally {
      setUrlsLoading(false);
    }
  };

  const handleDeleteUrl = async (urlId: number) => {
    if (!confirm('Are you sure you want to delete this URL? This action cannot be undone.')) {
      return;
    }

    setUrlsLoading(true);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/scraped-urls/${urlId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        await loadUrlsData();
        alert('URL deleted successfully!');
      } else {
        throw new Error('Failed to delete URL');
      }
    } catch (error) {
      console.error('Failed to delete URL:', error);
      alert('Failed to delete URL. Please try again.');
    } finally {
      setUrlsLoading(false);
    }
  };

  // Scraping schedule functions (legacy - replaced by loadScheduleSettings)
  /*
  const loadScrapingSchedule = async () => {
    try {
      const response = await authFetch('/api/scraping-schedule');
      const data = await response.json();
      if (data.ok && data.schedule) {
        setScrapingSchedule(data.schedule);
      } else {
        console.warn('Failed to load scraping schedule');
        setScrapingSchedule(null);
      }
    } catch (error) {
      console.error('Error loading scraping schedule:', error);
      setScrapingSchedule(null);
    }
  };
  */

  // Load schedule settings (new user-friendly UI)
  const loadScheduleSettings = async () => {
    try {
      const response = await authFetch('/api/scraping-schedule');
      const data = await response.json();
      if (data.ok && data.schedule) {
        setScheduleSettings({
          frequency: data.schedule.frequency || 'daily',
          time_of_day: data.schedule.time_of_day ? data.schedule.time_of_day.substring(0, 5) : '03:00',
          day_of_week: data.schedule.day_of_week || 1,
          day_of_month: data.schedule.day_of_month || 1,
          notification_email: data.schedule.notification_email || 'tien.ho@eh-immigration.com',
          notify_on_changes: data.schedule.notify_on_changes !== false,
          notify_on_failures: data.schedule.notify_on_failures !== false,
          next_run_at: data.schedule.next_run_at || null
        });
      }
    } catch (error) {
      console.error('Failed to load schedule settings:', error);
    }
  };

  // Save schedule settings (new user-friendly UI)
  const saveScheduleSettings = async () => {
    try {
      const response = await authFetch('/api/scraping-schedule', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          frequency: scheduleSettings.frequency,
          time_of_day: scheduleSettings.time_of_day + ':00', // Add seconds
          day_of_week: scheduleSettings.frequency === 'weekly' ? scheduleSettings.day_of_week : null,
          day_of_month: scheduleSettings.frequency === 'monthly' ? scheduleSettings.day_of_month : null,
          notification_email: scheduleSettings.notification_email,
          notify_on_changes: scheduleSettings.notify_on_changes,
          notify_on_failures: scheduleSettings.notify_on_failures
        })
      });

      const data = await response.json();
      if (data.ok) {
        const nextRunDate = new Date(data.schedule.next_run_at);
        alert('Schedule updated successfully!\n\nNext run: ' +
          nextRunDate.toLocaleString('en-SG', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
          }));
        await loadScheduleSettings();
      } else {
        throw new Error(data.error || 'Failed to save schedule');
      }
    } catch (error) {
      console.error('Failed to save schedule:', error);
      alert('Failed to save schedule. Please try again.');
    }
  };


  // Load URLs data when component mounts or when urls tab is selected
  React.useEffect(() => {
    // console.log('[Tab Effect] activeTab changed to:', activeTab);
    if (activeTab === 'urls') {
      // console.log('[Tab Effect] Loading URLs data...');
      loadUrlsData();
      // loadScrapingSchedule(); // Legacy - replaced by loadScheduleSettings
      loadScheduleSettings(); // Load user-friendly schedule settings
    }
    if (activeTab === 'knowledge') {
      // console.log('[Tab Effect] Loading Knowledge Base data...');
      loadKnowledgeBaseData();
    }
  }, [activeTab]);

  // ==================== KNOWLEDGE BASE FUNCTIONS ====================

  const loadKnowledgeBaseData = async () => {
    // console.log('[KB] loadKnowledgeBaseData called');
    setKbLoading(true);
    try {
      // console.log('[KB] Fetching documents and stats...');
      const [docsResult, statsResult] = await Promise.all([
        orchestratorApi.getKnowledgeBaseDocuments(),
        orchestratorApi.getKnowledgeBaseStats()
      ]);

      // console.log('[KB] Docs result:', docsResult);
      // console.log('[KB] Stats result:', statsResult);

      // Check for ok property (not success) to match API response
      if (docsResult.ok && docsResult.documents) {
        // console.log('[KB] Setting', docsResult.documents.length, 'documents');
        setKbDocuments(docsResult.documents);
      } else {
        console.error('[KB] Failed to load documents:', docsResult);
      }

      if (statsResult.ok && statsResult.stats) {
        // console.log('[KB] Setting stats');
        setKbStats(statsResult.stats);
      }
    } catch (error) {
      console.error('[KB] Failed to load data:', error);
    } finally {
      setKbLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size exceeds 10MB limit. Please choose a smaller file.');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload.');
      return;
    }

    setKbUploading(true);
    try {
      const result = await orchestratorApi.uploadKnowledgeBaseDocument(
        selectedFile,
        uploadMetadata
      );

      // Backend returns 'ok' not 'success'
      if (result.ok || result.success) {
        alert(`Document uploaded successfully! ${result.message || ''}`);
        setSelectedFile(null);
        // Reset to default metadata
        setUploadMetadata({
          description: '',
          source: ''
        });
        // Reload documents list
        await loadKnowledgeBaseData();
      } else {
        alert(`Upload failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('[KB] Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setKbUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: number, filename: string) => {
    if (!confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const result = await orchestratorApi.deleteKnowledgeBaseDocument(documentId);
      if (result.success) {
        alert('Document deleted successfully.');
        await loadKnowledgeBaseData();
      } else {
        alert(`Delete failed: ${result.error}`);
      }
    } catch (error) {
      console.error('[KB] Delete error:', error);
      alert('Delete failed. Please try again.');
    }
  };

  const handleViewDocument = async (documentId: number, filename: string) => {
    try {
      const result = await orchestratorApi.getKnowledgeBaseDocumentContent(documentId);
      if (result.ok && result.content) {
        // Get document metadata from the documents list
        const docMetadata = kbDocuments.find(d => d.id === documentId);
        setViewingDocument({
          id: documentId,
          filename: filename,
          content: result.content,
          mime_type: result.mime_type || 'text/plain',
          description: docMetadata?.description,
          source: docMetadata?.source
        });
        // Reset edit mode when viewing a new document
        setIsEditingDocument(false);
        setEditedContent(result.content);
        setEditedDescription(docMetadata?.description || '');
        setEditedSource(docMetadata?.source || '');
      } else {
        alert(`Failed to load document: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('[KB] View error:', error);
      alert('Failed to load document. Please try again.');
    }
  };

  const handleEditDocumentById = async (documentId: number, filename: string) => {
    await handleViewDocument(documentId, filename);
    setIsEditingDocument(true);
  };

  const handleEditDocument = () => {
    if (viewingDocument) {
      setEditedContent(viewingDocument.content);
      setEditedDescription(viewingDocument.description || '');
      setEditedSource(viewingDocument.source || '');
      setIsEditingDocument(true);
    }
  };

  const handleSaveDocument = async () => {
    if (!viewingDocument) return;

    try {
      const updateData: any = {};
      if (editedContent !== viewingDocument.content) {
        updateData.content_text = editedContent;
      }
      if (editedDescription !== (viewingDocument.description || '')) {
        updateData.description = editedDescription;
      }
      if (editedSource !== (viewingDocument.source || '')) {
        updateData.source = editedSource;
      }

      if (Object.keys(updateData).length === 0) {
        setIsEditingDocument(false);
        return;
      }

      const result = await orchestratorApi.updateKnowledgeBaseDocument(viewingDocument.id, updateData);

      if (result.success) {
        // Update the viewing document with new values
        setViewingDocument({
          ...viewingDocument,
          content: editedContent,
          description: editedDescription,
          source: editedSource
        });
        setIsEditingDocument(false);
        // Reload documents to get updated metadata
        await loadKnowledgeBaseData();
        alert('Document updated successfully!');
      } else {
        alert(`Failed to save: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('[KB] Save error:', error);
      alert('Failed to save document. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    if (viewingDocument) {
      setEditedContent(viewingDocument.content);
      setEditedDescription(viewingDocument.description || '');
      setEditedSource(viewingDocument.source || '');
    }
    setIsEditingDocument(false);
  };

  // ============================================
  // CURATED QA LIBRARY HANDLERS
  // ============================================

  const loadCuratedQAData = async () => {
    setCuratedQALoading(true);
    try {
      // Multi-tenant SaaS: Backend extracts tenant_id from JWT token
      // Frontend should NOT pass tenant_id to prevent tenant spoofing
      const [pairsResult, statsResult, categoriesResult] = await Promise.all([
        orchestratorApi.getCuratedQAPairs(),
        orchestratorApi.getCuratedQAStats(),
        orchestratorApi.getCuratedQACategories()
      ]);

      if (pairsResult.success && pairsResult.pairs) {
        setCuratedQAPairs(pairsResult.pairs);
      }

      if (statsResult.success && statsResult.stats) {
        setCuratedQAStats(statsResult.stats);
      }

      if (categoriesResult.success && categoriesResult.categories) {
        setCuratedQACategories(categoriesResult.categories);
      }
    } catch (error) {
      console.error('[CuratedQA] Load error:', error);
    } finally {
      setCuratedQALoading(false);
    }
  };

  const handleCreateQAPair = async () => {
    if (!curatedQAForm.question || !curatedQAForm.answer) {
      alert('Please provide both question and answer.');
      return;
    }

    setCuratedQASaving(true);
    try {
      const result = await orchestratorApi.createCuratedQAPair({
        question: curatedQAForm.question,
        answer: curatedQAForm.answer,
        category: curatedQAForm.category,
        subcategory: curatedQAForm.subcategory || undefined,
        tags: curatedQAForm.tags,
        keywords: curatedQAForm.keywords,
        priority: curatedQAForm.priority,
        quality_score: curatedQAForm.quality_score,
        language: curatedQAForm.language,
        // Multi-tenant SaaS: tenant_id extracted from JWT by backend
        created_by: 'admin'
      });

      if (result.success) {
        alert('Q&A pair created successfully!');
        setShowCreateQAModal(false);
        setCuratedQAForm({
          question: '',
          answer: '',
          category: 'General',
          subcategory: '',
          tags: [],
          keywords: [],
          priority: 50,
          quality_score: 0.8,
          language: 'english'
        });
        await loadCuratedQAData();
      } else {
        alert(`Create failed: ${result.error}`);
      }
    } catch (error) {
      console.error('[CuratedQA] Create error:', error);
      alert('Create failed. Please try again.');
    } finally {
      setCuratedQASaving(false);
    }
  };

  const handleUpdateQAPair = async () => {
    if (!editingQAPair || !curatedQAForm.question || !curatedQAForm.answer) {
      alert('Please provide both question and answer.');
      return;
    }

    setCuratedQASaving(true);
    try {
      const result = await orchestratorApi.updateCuratedQAPair(editingQAPair.id, {
        question: curatedQAForm.question,
        answer: curatedQAForm.answer,
        category: curatedQAForm.category,
        subcategory: curatedQAForm.subcategory || undefined,
        tags: curatedQAForm.tags,
        keywords: curatedQAForm.keywords,
        priority: curatedQAForm.priority,
        quality_score: curatedQAForm.quality_score,
        language: curatedQAForm.language,
        updated_by: 'admin'
      });

      if (result.success) {
        alert('Q&A pair updated successfully!');
        setShowCreateQAModal(false);
        setEditingQAPair(null);
        setCuratedQAForm({
          question: '',
          answer: '',
          category: 'General',
          subcategory: '',
          tags: [],
          keywords: [],
          priority: 50,
          quality_score: 0.8,
          language: 'english'
        });
        await loadCuratedQAData();
      } else {
        alert(`Update failed: ${result.error}`);
      }
    } catch (error) {
      console.error('[CuratedQA] Update error:', error);
      alert('Update failed. Please try again.');
    } finally {
      setCuratedQASaving(false);
    }
  };

  const handleDeleteQAPair = async (id: number, question: string) => {
    if (!confirm(`Are you sure you want to delete "${question.substring(0, 50)}..."?`)) {
      return;
    }

    try {
      const result = await orchestratorApi.deleteCuratedQAPair(id);
      if (result.success) {
        alert('Q&A pair deleted successfully.');
        await loadCuratedQAData();
      } else {
        alert(`Delete failed: ${result.error}`);
      }
    } catch (error) {
      console.error('[CuratedQA] Delete error:', error);
      alert('Delete failed. Please try again.');
    }
  };

  const handleDeactivateQAPair = async (id: number, question: string) => {
    if (!confirm(`Deactivate "${question.substring(0, 50)}..."? This can be reactivated later.`)) {
      return;
    }

    try {
      const result = await orchestratorApi.deactivateCuratedQAPair(id);
      if (result.success) {
        alert('Q&A pair deactivated successfully.');
        await loadCuratedQAData();
      } else {
        alert(`Deactivate failed: ${result.error}`);
      }
    } catch (error) {
      console.error('[CuratedQA] Deactivate error:', error);
      alert('Deactivate failed. Please try again.');
    }
  };

  const handleSearchCuratedQA = async () => {
    if (!curatedQASearchQuery.trim()) {
      setCuratedQASearchResults(null);
      return;
    }

    try {
      // Multi-tenant SaaS: Backend extracts tenant_id from JWT token
      const result = await orchestratorApi.searchCuratedQA(curatedQASearchQuery, {
        category: curatedQACategoryFilter || undefined,
        limit: 10
      });

      if (result.success && result.results) {
        setCuratedQASearchResults(result.results);
      } else {
        alert(`Search failed: ${result.error}`);
      }
    } catch (error) {
      console.error('[CuratedQA] Search error:', error);
      alert('Search failed. Please try again.');
    }
  };

  const handleEditQAPair = (qa: any) => {
    setEditingQAPair(qa);
    setCuratedQAForm({
      question: qa.question,
      answer: qa.answer,
      category: qa.category,
      subcategory: qa.subcategory || '',
      tags: qa.tags || [],
      keywords: qa.keywords || [],
      priority: qa.priority || 50,
      quality_score: qa.quality_score || 0.8,
      language: qa.language || 'english'
    });
    setShowCreateQAModal(true);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !curatedQAForm.tags.includes(tagInput.trim())) {
      setCuratedQAForm({
        ...curatedQAForm,
        tags: [...curatedQAForm.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setCuratedQAForm({
      ...curatedQAForm,
      tags: curatedQAForm.tags.filter(t => t !== tag)
    });
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !curatedQAForm.keywords.includes(keywordInput.trim())) {
      setCuratedQAForm({
        ...curatedQAForm,
        keywords: [...curatedQAForm.keywords, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setCuratedQAForm({
      ...curatedQAForm,
      keywords: curatedQAForm.keywords.filter(k => k !== keyword)
    });
  };

  // ==========================================
  // TENANT TOPICS MANAGEMENT FUNCTIONS
  // ==========================================

  const loadTenantTopics = async () => {
    setTenantTopicsLoading(true);
    try {
      const response = await fetch('/api/admin/tenant-topics', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.ok) {
        setTenantTopics(data.topics);
      }
    } catch (error) {
      console.error('Failed to load tenant topics:', error);
    } finally {
      setTenantTopicsLoading(false);
    }
  };

  const handleSaveTopic = async () => {
    try {
      const response = await fetch('/api/admin/tenant-topics', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(topicForm)
      });
      const data = await response.json();
      if (data.ok) {
        await loadTenantTopics();
        setShowTopicModal(false);
        setTopicForm({
          topic_name: '',
          topic_patterns: '',
          is_active: true,
          priority: 0
        });
        setEditingTopic(null);
      }
    } catch (error) {
      console.error('Failed to save topic:', error);
    }
  };

  const handleDeleteTopic = async (topicName: string) => {
    if (!confirm(`Are you sure you want to delete topic "${topicName}"?`)) {
      return;
    }
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/admin/tenant-topics/${encodeURIComponent(topicName)}`, {
        method: 'DELETE',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.ok) {
        await loadTenantTopics();
      }
    } catch (error) {
      console.error('Failed to delete topic:', error);
    }
  };

  const handleToggleTopic = async (topicName: string, isActive: boolean) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/admin/tenant-topics/${encodeURIComponent(topicName)}/toggle`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: isActive })
      });
      const data = await response.json();
      if (data.ok) {
        await loadTenantTopics();
      }
    } catch (error) {
      console.error('Failed to toggle topic:', error);
    }
  };

  const handleTestTopic = async () => {
    if (!testMessage.trim()) {
      return;
    }
    try {
      const response = await fetch('/api/admin/tenant-topics/test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: testMessage })
      });
      const data = await response.json();
      setTestResult(data);
    } catch (error) {
      console.error('Failed to test topic:', error);
    }
  };

  const openEditTopic = (topic: any) => {
    setEditingTopic(topic);
    setTopicForm({
      topic_name: topic.topic_name,
      topic_patterns: topic.topic_patterns,
      is_active: topic.is_active,
      priority: topic.priority || 0
    });
    setShowTopicModal(true);
  };

  const renderTenantTopics = () => {
    return (
      <div className="tenant-topics-section">
        <div className="section-header">
          <h2>🏷️ Tenant Topics Management</h2>
          <p>Configure conversation topics for automatic detection and topic locking</p>
        </div>

        {tenantTopicsLoading ? (
          <div className="loading">Loading topics...</div>
        ) : (
          <div className="tenant-topics-content">
            {/* Statistics Section */}
            <div className="statistics-section">
              <h3>📊 Topic Statistics</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <h4>{tenantTopics.length}</h4>
                  <p>Total Topics</p>
                </div>
                <div className="stat-card">
                  <h4>{tenantTopics.filter(t => t.is_active).length}</h4>
                  <p>Active Topics</p>
                </div>
              </div>
            </div>

            {/* Test Topic Detection */}
            <div className="topic-test-section" style={{ marginTop: '24px' }}>
              <h3>🔍 Test Topic Detection</h3>
              <p>Test how messages are matched to topics</p>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Enter a message to test..."
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleTestTopic()}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px'
                  }}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleTestTopic}
                  disabled={!testMessage.trim()}
                >
                  Test
                </button>
              </div>
              {testResult && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: testResult.detected_topic ? '#fef3c7' : '#f3f4f6',
                  borderRadius: '6px',
                  border: `1px solid ${testResult.detected_topic ? '#f59e0b' : '#d1d5db'}`
                }}>
                  {testResult.detected_topic ? (
                    <span>✅ Topic detected: <strong>{testResult.detected_topic}</strong></span>
                  ) : (
                    <span>ℹ️ No topic matched</span>
                  )}
                </div>
              )}
            </div>

            {/* Topics List */}
            <div className="topics-list-section" style={{ marginTop: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>📋 Topics ({tenantTopics.length})</h3>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setEditingTopic(null);
                    setTopicForm({
                      topic_name: '',
                      topic_patterns: '',
                      is_active: true,
                      priority: 0
                    });
                    setShowTopicModal(true);
                  }}
                >
                  ➕ Add Topic
                </button>
              </div>

              {tenantTopics.length === 0 ? (
                <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                  <p>No topics configured yet. Create your first topic to enable automatic topic detection.</p>
                </div>
              ) : (
                <div className="topics-grid" style={{ display: 'grid', gap: '16px', marginTop: '16px' }}>
                  {tenantTopics.map((topic) => (
                    <div key={topic.id} className={`topic-card ${!topic.is_active ? 'inactive' : ''}`} style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: !topic.is_active ? '#f9fafb' : 'white',
                      opacity: !topic.is_active ? 0.7 : 1
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <h4 style={{ margin: 0 }}>{topic.topic_name}</h4>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              backgroundColor: topic.is_active ? '#d1fae5' : '#f3f4f6',
                              color: topic.is_active ? '#065f46' : '#6b7280'
                            }}>
                              {topic.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              backgroundColor: '#e0f2fe',
                              color: '#0369a1'
                            }}>
                              Priority: {topic.priority || 0}
                            </span>
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                            <strong>Patterns:</strong> {topic.topic_patterns}
                          </div>
                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                            Updated: {new Date(topic.updated_at).toLocaleString()}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                            onClick={() => openEditTopic(topic)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                            onClick={() => handleToggleTopic(topic.topic_name, !topic.is_active)}
                          >
                            {topic.is_active ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ padding: '6px 12px', fontSize: '13px', backgroundColor: '#ef4444', color: 'white', border: 'none' }}
                            onClick={() => handleDeleteTopic(topic.topic_name)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Topic Modal */}
        {showTopicModal && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ marginTop: 0 }}>{editingTopic ? 'Edit Topic' : 'Add New Topic'}</h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Topic Name *
                </label>
                <input
                  type="text"
                  value={topicForm.topic_name}
                  onChange={(e) => setTopicForm({ ...topicForm, topic_name: e.target.value })}
                  placeholder="e.g., LTVP, PR, Citizenship"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  disabled={!!editingTopic}
                />
                <small style={{ color: '#6b7280' }}>Canonical name for this topic (cannot be changed after creation)</small>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Topic Patterns *
                </label>
                <input
                  type="text"
                  value={topicForm.topic_patterns}
                  onChange={(e) => setTopicForm({ ...topicForm, topic_patterns: e.target.value })}
                  placeholder="e.g., LTVP|long term visit pass|visit pass"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <small style={{ color: '#6b7280' }}>Pipe-separated patterns (|) for matching messages</small>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Priority
                </label>
                <input
                  type="number"
                  value={topicForm.priority}
                  onChange={(e) => setTopicForm({ ...topicForm, priority: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                  min="0"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <small style={{ color: '#6b7280' }}>Higher priority topics are checked first (default: 0)</small>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={topicForm.is_active}
                    onChange={(e) => setTopicForm({ ...topicForm, is_active: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
                <small style={{ color: '#6b7280' }}>Only active topics are used for detection</small>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowTopicModal(false);
                    setTopicForm({
                      topic_name: '',
                      topic_patterns: '',
                      is_active: true,
                      priority: 0
                    });
                    setEditingTopic(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveTopic}
                  disabled={!topicForm.topic_name || !topicForm.topic_patterns}
                >
                  {editingTopic ? 'Update' : 'Create'} Topic
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ==========================================
  // TENANT CONFIG MANAGEMENT FUNCTIONS
  // ==========================================

  const loadTenantConfig = async () => {
    setConfigLoading(true);
    try {
      const response = await fetch('/api/admin/tenant-config', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      const data = await response.json();
      if (data.ok) {
        setTenantConfig(data.data.config);
        setTenantDomains(data.data.domains || []);
        setChatRoutingRuleTypes(data.data.ruleTypes || []);
      }
    } catch (error) {
      console.error('Failed to load tenant config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const updateTenantConfig = async (updates: any) => {
    setConfigSaving(true);
    try {
      const response = await fetch('/api/admin/tenant-config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.ok) {
        setTenantConfig(data.data);
        alert('Configuration updated successfully!');
      } else {
        alert('Failed to update configuration: ' + data.error);
      }
    } catch (error) {
      console.error('Failed to update config:', error);
      alert('Failed to update configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  const defaultChatRoutingRules = [
    {
      id: 'management-message-default',
      name: 'Messages For Management',
      actionType: 'management_message',
      enabled: true,
      priority: 10,
      responseMessage: 'Thanks for the message, we will get back to you shortly.',
      managementGroupName: 'E&H Consultants',
      triggerPhrases: [
        'partnership',
        'collaboration',
        'collab',
        'synergy',
        'referral',
        'joint venture',
        'insurance broker',
        'financial advisor',
        'financial positioning',
        'vendor',
        'service provider',
        'business proposal'
      ]
    },
    {
      id: 'job-search-default',
      name: 'Job Search Redirect',
      actionType: 'job_search_redirect',
      enabled: true,
      priority: 20,
      responseMessage: 'Thanks for your message. We do not provide job placements in Singapore. What we can help with is a self employed Employment Pass route, which involves incorporating a company in Singapore and having that company issue you an Employment Pass. If you would like, we can arrange a quick call to share more about how that route works.',
      triggerPhrases: [
        'find a job',
        'finding a job',
        'looking for a job',
        'job placement',
        'job placements',
        'job opportunities',
        'employment opportunities',
        'career opportunities',
        'work opportunities',
        'work in singapore',
        'explore opportunities in singapore'
      ]
    }
  ];

  const fallbackChatRoutingRuleTypes = [
    {
      actionType: 'management_message',
      label: 'Reply + Internal Notification',
      description: 'Send a reply to the chat and notify an internal group chat.',
      defaultName: 'Internal Notification Rule',
      defaultResponseMessage: 'Thanks for the message, we will get back to you shortly.',
      requiresManagementGroupName: true,
      defaultManagementGroupName: 'E&H Consultants'
    },
    {
      actionType: 'job_search_redirect',
      label: 'Reply Only Redirect',
      description: 'Send a redirect reply without notifying an internal group chat.',
      defaultName: 'Reply Only Redirect Rule',
      defaultResponseMessage: 'Thanks for your message. We do not provide job placements in Singapore. What we can help with is a self employed Employment Pass route, which involves incorporating a company in Singapore and having that company issue you an Employment Pass. If you would like, we can arrange a quick call to share more about how that route works.',
      requiresManagementGroupName: false
    }
  ];

  const normalizeChatRoutingRules = (rawRules: any) => {
    if (Array.isArray(rawRules)) {
      return rawRules
        .map((rule: any, index: number) => ({
          id: typeof rule?.id === 'string' && rule.id.trim() ? rule.id.trim() : `chat-routing-rule-${index + 1}`,
          name: typeof rule?.name === 'string' && rule.name.trim() ? rule.name.trim() : `Rule ${index + 1}`,
          actionType: rule?.actionType === 'job_search_redirect' ? 'job_search_redirect' : 'management_message',
          enabled: rule?.enabled !== false,
          priority: Number.isFinite(rule?.priority) ? Number(rule.priority) : ((index + 1) * 10),
          responseMessage: typeof rule?.responseMessage === 'string' ? rule.responseMessage : '',
          managementGroupName: typeof rule?.managementGroupName === 'string' ? rule.managementGroupName : '',
          triggerPhrases: Array.isArray(rule?.triggerPhrases)
            ? rule.triggerPhrases.map((trigger: any) => String(trigger).trim()).filter(Boolean)
            : []
        }))
        .sort((a: any, b: any) => a.priority - b.priority);
    }

    if (rawRules && typeof rawRules === 'object') {
      return [
        {
          id: 'management-message-default',
          name: 'Messages For Management',
          actionType: 'management_message',
          enabled: rawRules.managementMessageEnabled !== false,
          priority: 10,
          responseMessage: rawRules.managementMessageAckMessage || defaultChatRoutingRules[0].responseMessage,
          managementGroupName: rawRules.managementMessageGroupName || defaultChatRoutingRules[0].managementGroupName,
          triggerPhrases: Array.isArray(rawRules.managementMessageTriggers) && rawRules.managementMessageTriggers.length > 0
            ? rawRules.managementMessageTriggers.map((trigger: any) => String(trigger).trim()).filter(Boolean)
            : [...defaultChatRoutingRules[0].triggerPhrases]
        },
        {
          id: 'job-search-default',
          name: 'Job Search Redirect',
          actionType: 'job_search_redirect',
          enabled: rawRules.jobSearchRedirectEnabled !== false,
          priority: 20,
          responseMessage: rawRules.jobSearchRedirectMessage || defaultChatRoutingRules[1].responseMessage,
          managementGroupName: '',
          triggerPhrases: Array.isArray(rawRules.jobSearchTriggers) && rawRules.jobSearchTriggers.length > 0
            ? rawRules.jobSearchTriggers.map((trigger: any) => String(trigger).trim()).filter(Boolean)
            : [...defaultChatRoutingRules[1].triggerPhrases]
        }
      ];
    }

    return JSON.parse(JSON.stringify(defaultChatRoutingRules));
  };

  const getChatRoutingRules = () => normalizeChatRoutingRules(tenantConfig?.additional_settings?.chat_routing_rules);

  const saveChatRoutingRules = async (rules: any[]) => {
    const normalizedRules = [...rules].sort((a, b) => a.priority - b.priority);
    const mergedAdditionalSettings = {
      ...(tenantConfig?.additional_settings || {}),
      chat_routing_rules: normalizedRules
    };

    setTenantConfig((prev: any) => ({
      ...(prev || {}),
      additional_settings: mergedAdditionalSettings
    }));

    await updateTenantConfig({
      additional_settings: mergedAdditionalSettings
    });
  };

  const createEmptyChatRoutingRule = (actionType: 'management_message' | 'job_search_redirect' = ((getAvailableChatRoutingRuleTypes()[0]?.actionType || 'management_message') as 'management_message' | 'job_search_redirect')) => {
    const definition = getChatRoutingRuleTypeDefinition(actionType);
    return {
      id: `chat-routing-rule-${Date.now()}`,
      name: definition?.defaultName || 'New Chat Routing Rule',
      actionType,
      enabled: true,
      priority: (getChatRoutingRules().length + 1) * 10,
      responseMessage: definition?.defaultResponseMessage || '',
      managementGroupName: definition?.defaultManagementGroupName || '',
      triggerPhrases: []
    };
  };

  const openCreateChatRoutingRule = () => {
    const newRule = createEmptyChatRoutingRule();
    setEditingChatRoutingRuleId(null);
    setChatRoutingRuleForm({
      ...newRule,
      triggerText: ''
    });
    setShowChatRoutingRuleModal(true);
  };

  const openEditChatRoutingRule = (rule: any) => {
    setEditingChatRoutingRuleId(rule.id);
    setChatRoutingRuleForm({
      ...rule,
      triggerText: stringifyRuleLines(rule.triggerPhrases || [])
    });
    setShowChatRoutingRuleModal(true);
  };

  const closeChatRoutingRuleModal = () => {
    setShowChatRoutingRuleModal(false);
    setEditingChatRoutingRuleId(null);
    setChatRoutingRuleForm({
      id: '',
      name: '',
      actionType: 'management_message',
      enabled: true,
      priority: 10,
      responseMessage: '',
      managementGroupName: '',
      triggerText: ''
    });
  };

  const handleSaveChatRoutingRule = async () => {
    const rule = {
      id: chatRoutingRuleForm.id || `chat-routing-rule-${Date.now()}`,
      name: chatRoutingRuleForm.name.trim(),
      actionType: chatRoutingRuleForm.actionType,
      enabled: chatRoutingRuleForm.enabled,
      priority: Number(chatRoutingRuleForm.priority) || 10,
      responseMessage: chatRoutingRuleForm.responseMessage.trim(),
      managementGroupName: chatRoutingRuleForm.actionType === 'management_message'
        ? chatRoutingRuleForm.managementGroupName.trim()
        : '',
      triggerPhrases: parseRuleLines(chatRoutingRuleForm.triggerText)
    };

    if (!rule.name || !rule.responseMessage || rule.triggerPhrases.length === 0) {
      alert('Rule name, response message, and at least one trigger phrase are required.');
      return;
    }

    const typeDefinition = getChatRoutingRuleTypeDefinition(rule.actionType);
    if (typeDefinition?.requiresManagementGroupName && !rule.managementGroupName) {
      alert('Internal group chat name is required for this action.');
      return;
    }

    const currentRules = getChatRoutingRules();
    const updatedRules = editingChatRoutingRuleId
      ? currentRules.map((existingRule: any) => existingRule.id === editingChatRoutingRuleId ? rule : existingRule)
      : [...currentRules, rule];

    await saveChatRoutingRules(updatedRules);
    closeChatRoutingRuleModal();
  };

  const handleDeleteChatRoutingRule = async (ruleId: string) => {
    if (!confirm('Delete this chat routing rule?')) {
      return;
    }

    const currentRules = getChatRoutingRules();
    await saveChatRoutingRules(currentRules.filter((rule: any) => rule.id !== ruleId));
  };

  const handleMoveChatRoutingRule = async (ruleId: string, direction: 'up' | 'down') => {
    const currentRules = [...getChatRoutingRules()].sort((a: any, b: any) => a.priority - b.priority);
    const index = currentRules.findIndex((rule: any) => rule.id === ruleId);
    if (index === -1) return;

    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= currentRules.length) return;

    const swappedRules = [...currentRules];
    const currentPriority = swappedRules[index].priority;
    swappedRules[index].priority = swappedRules[swapIndex].priority;
    swappedRules[swapIndex].priority = currentPriority;

    await saveChatRoutingRules(swappedRules);
  };

  const stringifyRuleLines = (values: string[] = []) => values.join('\n');

  const parseRuleLines = (value: string) =>
    value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

  const getAvailableChatRoutingRuleTypes = () =>
    chatRoutingRuleTypes.length > 0 ? chatRoutingRuleTypes : fallbackChatRoutingRuleTypes;

  const getChatRoutingRuleTypeDefinition = (actionType: string) =>
    getAvailableChatRoutingRuleTypes().find((ruleType) => ruleType.actionType === actionType) || null;

  const openAddDomain = () => {
    setEditingDomain(null);
    setDomainForm({
      domain_key: '',
      domain_name: '',
      description: '',
      confidence_threshold: 0.70,
      priority: 0,
      system_prompt_addition: '',
      is_active: true
    });
    setShowDomainModal(true);
  };

  const openEditDomain = (domain: any) => {
    setEditingDomain(domain);
    setDomainForm({
      domain_key: domain.domain_key,
      domain_name: domain.domain_name,
      description: domain.description || '',
      confidence_threshold: domain.confidence_threshold || 0.70,
      priority: domain.priority || 0,
      system_prompt_addition: domain.system_prompt_addition || '',
      is_active: domain.is_active !== false
    });
    setShowDomainModal(true);
  };

  const handleSaveDomain = async () => {
    try {
      const url = editingDomain
        ? `/api/admin/tenant-domains/${editingDomain.domain_key}`
        : '/api/admin/tenant-domains';

      const response = await fetch(url, {
        method: editingDomain ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(domainForm)
      });
      const data = await response.json();
      if (data.ok) {
        setShowDomainModal(false);
        loadTenantConfig();
      } else {
        alert('Failed to save domain: ' + data.error);
      }
    } catch (error) {
      console.error('Failed to save domain:', error);
      alert('Failed to save domain');
    }
  };

  const handleDeleteDomain = async (domainKey: string) => {
    if (!confirm(`Are you sure you want to delete domain "${domainKey}"?`)) {
      return;
    }
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/admin/tenant-domains/${domainKey}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      const data = await response.json();
      if (data.ok) {
        loadTenantConfig();
      } else {
        alert('Failed to delete domain: ' + data.error);
      }
    } catch (error) {
      console.error('Failed to delete domain:', error);
      alert('Failed to delete domain');
    }
  };

  const handleTestDomain = async () => {
    if (!domainTestQuery.trim()) return;
    try {
      const response = await fetch('/api/admin/tenant-domains/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ query: domainTestQuery })
      });
      const data = await response.json();
      if (data.ok) {
        setDomainTestResult(data.data);
      } else {
        alert('Failed to test domain: ' + data.error);
      }
    } catch (error) {
      console.error('Failed to test domain:', error);
      alert('Failed to test domain');
    }
  };

  const renderTenantConfig = () => {
    return (
      <div className="tenant-config-section">
        <div className="section-header">
          <h2>⚙️ Tenant Configuration</h2>
          <p>Configure AI models, RAG settings, and domain classifications for your tenant</p>
        </div>

        {configLoading ? (
          <div className="loading">Loading configuration...</div>
        ) : (
          <div className="tenant-config-content">
            {/* AI Models Configuration */}
            <div className="config-section" style={{ marginBottom: '32px' }}>
              <h3>🤖 AI Models</h3>
              <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Primary Model
                  </label>
                  <select
                    value={tenantConfig?.primary_model || 'google/gemini-3-flash-preview'}
                    onChange={(e) => updateTenantConfig({ primary_model: e.target.value })}
                    disabled={configSaving}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                  >
                    <option value="google/gemini-3-flash-preview">Gemini 3 Flash Preview</option>
                    <option value="google/gemini-2-flash-thinking">Gemini 2 Flash Thinking</option>
                    <option value="anthropic/claude-3.5-sonnet">Claude 3.5 Sonnet</option>
                    <option value="anthropic/claude-3.5-haiku">Claude 3.5 Haiku</option>
                    <option value="openai/gpt-4o">GPT-4o</option>
                    <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Backup Model
                  </label>
                  <select
                    value={tenantConfig?.backup_model || 'anthropic/claude-3.5-sonnet'}
                    onChange={(e) => updateTenantConfig({ backup_model: e.target.value })}
                    disabled={configSaving}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                  >
                    <option value="anthropic/claude-3.5-sonnet">Claude 3.5 Sonnet</option>
                    <option value="google/gemini-3-flash-preview">Gemini 3 Flash Preview</option>
                    <option value="google/gemini-2-flash-thinking">Gemini 2 Flash Thinking</option>
                    <option value="anthropic/claude-3.5-haiku">Claude 3.5 Haiku</option>
                    <option value="openai/gpt-4o">GPT-4o</option>
                    <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
                  </select>
                </div>
              </div>
            </div>

            {/* RAG Thresholds */}
            <div className="config-section" style={{ marginBottom: '32px' }}>
              <h3>📊 RAG Confidence Thresholds</h3>
              <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    General Confidence: {(tenantConfig?.rag_confidence_general || 0.70).toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={tenantConfig?.rag_confidence_general || 0.70}
                    onChange={(e) => updateTenantConfig({ rag_confidence_general: parseFloat(e.target.value) })}
                    disabled={configSaving}
                    style={{ width: '100%' }}
                  />
                  <small style={{ color: '#6b7280' }}>Minimum confidence for general queries</small>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Specialized Confidence: {(tenantConfig?.rag_confidence_specialized || 0.80).toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={tenantConfig?.rag_confidence_specialized || 0.80}
                    onChange={(e) => updateTenantConfig({ rag_confidence_specialized: parseFloat(e.target.value) })}
                    disabled={configSaving}
                    style={{ width: '100%' }}
                  />
                  <small style={{ color: '#6b7280' }}>Minimum confidence for specialized queries</small>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Hybrid Score: {(tenantConfig?.rag_min_hybrid_score || 0.45).toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={tenantConfig?.rag_min_hybrid_score || 0.45}
                    onChange={(e) => updateTenantConfig({ rag_min_hybrid_score: parseFloat(e.target.value) })}
                    disabled={configSaving}
                    style={{ width: '100%' }}
                  />
                  <small style={{ color: '#6b7280' }}>Minimum hybrid RAG score</small>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Rerank Score: {(tenantConfig?.rag_min_rerank_score || 0.35).toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={tenantConfig?.rag_min_rerank_score || 0.35}
                    onChange={(e) => updateTenantConfig({ rag_min_rerank_score: parseFloat(e.target.value) })}
                    disabled={configSaving}
                    style={{ width: '100%' }}
                  />
                  <small style={{ color: '#6b7280' }}>Minimum reranker score</small>
                </div>
              </div>
            </div>

            {/* Feature Flags */}
            <div className="config-section" style={{ marginBottom: '32px' }}>
              <h3>🚦 Feature Flags</h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={tenantConfig?.enable_topic_locking !== false}
                    onChange={(e) => updateTenantConfig({ enable_topic_locking: e.target.checked })}
                    disabled={configSaving}
                  />
                  <span>Enable Topic Locking</span>
                  <small style={{ color: '#6b7280', marginLeft: 'auto' }}>Keep conversations focused on detected topics</small>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={tenantConfig?.enable_domain_classification !== false}
                    onChange={(e) => updateTenantConfig({ enable_domain_classification: e.target.checked })}
                    disabled={configSaving}
                  />
                  <span>Enable Domain Classification</span>
                  <small style={{ color: '#6b7280', marginLeft: 'auto' }}>Classify queries into domains for routing</small>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={tenantConfig?.enable_low_signal_detection !== false}
                    onChange={(e) => updateTenantConfig({ enable_low_signal_detection: e.target.checked })}
                    disabled={configSaving}
                  />
                  <span>Enable Low Signal Detection</span>
                  <small style={{ color: '#6b7280', marginLeft: 'auto' }}>Detect and handle low-quality queries</small>
                </label>
              </div>
            </div>

            {/* Specialist Message */}
            <div className="config-section" style={{ marginBottom: '32px' }}>
              <h3>💬 Specialist Message</h3>
              <textarea
                value={tenantConfig?.specialist_message || "We'll consult with our specialist first and get back to you shortly with the most accurate information for your case."}
                onChange={(e) => updateTenantConfig({ specialist_message: e.target.value })}
                disabled={configSaving}
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  fontFamily: 'monospace'
                }}
              />
              <small style={{ color: '#6b7280' }}>Message shown when deferring to a specialist</small>
            </div>

            {/* System Prompt */}
            <div className="config-section" style={{ marginBottom: '32px' }}>
              <h3>🎯 System Prompt</h3>
              <textarea
                value={tenantConfig?.system_prompt || "You are a helpful AI assistant. Answer naturally using ONLY the context provided. Do not use outside knowledge. Keep your response helpful and professional."}
                onChange={(e) => updateTenantConfig({ system_prompt: e.target.value })}
                disabled={configSaving}
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  fontFamily: 'monospace'
                }}
              />
              <small style={{ color: '#6b7280' }}>Base system prompt for AI responses</small>
            </div>

            {/* Client Context Instructions */}
            <div className="config-section" style={{ marginBottom: '32px' }}>
              <h3>👥 Client Context Instructions</h3>
              <textarea
                value={tenantConfig?.client_context_instructions || `========================================
🧠 CRITICAL: RESPECT CLIENT INFORMATION
========================================

If the conversation history contains client information (like relationship to customer, account details, preferences, etc.),
you MUST use that information in your response. Do NOT ignore it or switch to a different topic.

For example:
- If client mentioned a specific product or service, stay on that topic
- If client provided facts (requirements, timeline, budget, etc.), incorporate them naturally
- Maintain context of the ongoing conversation

Retrieved context chunks are supplementary. Client information from conversation history takes priority.
========================================`}
                onChange={(e) => updateTenantConfig({ client_context_instructions: e.target.value })}
                disabled={configSaving}
                rows={12}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  fontFamily: 'monospace'
                }}
              />
              <small style={{ color: '#6b7280' }}>Instructions for respecting client information in conversation history</small>
            </div>

            {/* Conversation Instructions */}
            <div className="config-section" style={{ marginBottom: '32px' }}>
              <h3>💭 Conversation Instructions</h3>
              <textarea
                value={tenantConfig?.conversation_instructions || `========================================
💬 INTERPRETING NEGATIVE RESPONSES
========================================

When the user responds with "No", "Nope", "Never", or similar negative responses:
1. Interpret it as a response to YOUR PREVIOUS QUESTION ONLY
2. Do NOT switch topics or start a new conversation thread
3. Continue with the next logical question on the SAME topic

Examples of CORRECT behavior:
- Stay on the same topic when user responds negatively
- Ask the next relevant question about the same subject
- Maintain conversation context

Examples of WRONG behavior to AVOID:
- Switching to a different topic when user says "No"
- Changing the subject or relationship context
- Deferring questions instead of asking the next logical question

REMEMBER: "No" means "the answer to your specific question is no" - NOT "start a new topic".
========================================

========================================
🎯 AVOID REPETITIVE PHRASES
========================================

Vary your response patterns across messages. Avoid repeating the same phrases or sentence structures.

AVOID repeating:
- Filler phrases that sound robotic
- Template openings and closings
- Repetitive transition words

INSTEAD, vary your openings:
- "Thank you for that information."
- "Thanks for letting me know."
- "Got it, thank you."
- "Noted, thanks."
- Or go directly to your question without a filler phrase

Each response should feel natural and unique, not robotic or templated.
========================================`}
                onChange={(e) => updateTenantConfig({ conversation_instructions: e.target.value })}
                disabled={configSaving}
                rows={20}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  fontFamily: 'monospace'
                }}
              />
              <small style={{ color: '#6b7280' }}>Instructions for conversation behavior patterns</small>
            </div>

            {/* Domain Classification */}
            <div className="config-section" style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>🏷️ Domain Classifications</h3>
                <button
                  className="btn btn-primary"
                  onClick={openAddDomain}
                  disabled={configSaving}
                >
                  ➕ Add Domain
                </button>
              </div>

              {tenantDomains.length === 0 ? (
                <div className="empty-state" style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px', marginTop: '16px' }}>
                  <p>No domains configured. Add domains to enable classification.</p>
                </div>
              ) : (
                <div style={{ marginTop: '16px' }}>
                  {tenantDomains.map((domain) => (
                    <div key={domain.domain_key} style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '12px',
                      backgroundColor: domain.is_active !== false ? 'white' : '#f9fafb'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <strong>{domain.domain_name}</strong>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              backgroundColor: '#e0f2fe',
                              color: '#0369a1'
                            }}>
                              {domain.domain_key}
                            </span>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              backgroundColor: domain.is_active !== false ? '#d1fae5' : '#f3f4f6',
                              color: domain.is_active !== false ? '#065f46' : '#6b7280'
                            }}>
                              {domain.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              backgroundColor: '#fef3c7',
                              color: '#92400e'
                            }}>
                              Priority: {domain.priority || 0}
                            </span>
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            {domain.description}
                          </div>
                          <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>
                            Confidence: {(domain.confidence_threshold || 0.70).toFixed(2)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                            onClick={() => openEditDomain(domain)}
                            disabled={configSaving}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ padding: '6px 12px', fontSize: '13px', backgroundColor: '#ef4444', color: 'white', border: 'none' }}
                            onClick={() => handleDeleteDomain(domain.domain_key)}
                            disabled={configSaving}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Domain Test Section */}
              <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <h4 style={{ marginTop: 0 }}>🔍 Test Domain Classification</h4>
                <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                  <input
                    type="text"
                    placeholder="Enter a query to test..."
                    value={domainTestQuery}
                    onChange={(e) => setDomainTestQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleTestDomain()}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleTestDomain}
                    disabled={!domainTestQuery.trim() || configSaving}
                  >
                    Test
                  </button>
                </div>
                {domainTestResult && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    border: '1px solid #f59e0b'
                  }}>
                    <strong>Matched Domain:</strong> {domainTestResult.domain_name} ({domainTestResult.domain_key})<br />
                    <strong>Confidence Threshold:</strong> {(domainTestResult.confidence_threshold || 0.70).toFixed(2)}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Domain Modal */}
        {showDomainModal && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ marginTop: 0 }}>{editingDomain ? 'Edit Domain' : 'Add New Domain'}</h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Domain Key *
                </label>
                <input
                  type="text"
                  value={domainForm.domain_key}
                  onChange={(e) => setDomainForm({ ...domainForm, domain_key: e.target.value })}
                  placeholder="e.g., immigration_workpass, real_estate, healthcare"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  disabled={!!editingDomain}
                />
                <small style={{ color: '#6b7280' }}>Unique identifier (cannot be changed after creation)</small>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Domain Name *
                </label>
                <input
                  type="text"
                  value={domainForm.domain_name}
                  onChange={(e) => setDomainForm({ ...domainForm, domain_name: e.target.value })}
                  placeholder="e.g., Work Pass, Real Estate, Healthcare"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <small style={{ color: '#6b7280' }}>Display name for this domain</small>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Description *
                </label>
                <textarea
                  value={domainForm.description}
                  onChange={(e) => setDomainForm({ ...domainForm, description: e.target.value })}
                  placeholder="Describe the types of queries in this domain (used for keyword matching)"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <small style={{ color: '#6b7280' }}>Keywords from description are used for matching</small>
              </div>

              <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Confidence Threshold
                  </label>
                  <input
                    type="number"
                    value={domainForm.confidence_threshold}
                    onChange={(e) => setDomainForm({ ...domainForm, confidence_threshold: parseFloat(e.target.value) || 0.70 })}
                    min="0"
                    max="1"
                    step="0.05"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <small style={{ color: '#6b7280' }}>Required confidence (0-1)</small>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Priority
                  </label>
                  <input
                    type="number"
                    value={domainForm.priority}
                    onChange={(e) => setDomainForm({ ...domainForm, priority: parseInt(e.target.value) || 0 })}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <small style={{ color: '#6b7280' }}>Higher = checked first</small>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  System Prompt Addition (Optional)
                </label>
                <textarea
                  value={domainForm.system_prompt_addition}
                  onChange={(e) => setDomainForm({ ...domainForm, system_prompt_addition: e.target.value })}
                  placeholder="Additional prompt instructions for this domain"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box'
                  }}
                />
                <small style={{ color: '#6b7280' }}>Extra instructions added to system prompt for this domain</small>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={domainForm.is_active}
                    onChange={(e) => setDomainForm({ ...domainForm, is_active: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
                <small style={{ color: '#6b7280' }}>Only active domains are used for classification</small>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDomainModal(false);
                    setDomainForm({
                      domain_key: '',
                      domain_name: '',
                      description: '',
                      confidence_threshold: 0.70,
                      priority: 0,
                      system_prompt_addition: '',
                      is_active: true
                    });
                    setEditingDomain(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveDomain}
                  disabled={!domainForm.domain_key || !domainForm.domain_name || !domainForm.description}
                >
                  {editingDomain ? 'Update' : 'Create'} Domain
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderChatRoutingRules = () => {
    const chatRoutingRules = getChatRoutingRules();

    return (
      <div className="tenant-config-section">
        <div className="section-header">
          <h2>🧭 Chat Routing Rules</h2>
          <p>Configure tenant-specific routing for non-client outreach and job-search chats</p>
        </div>

        {configLoading ? (
          <div className="loading">Loading configuration...</div>
        ) : (
          <div className="tenant-config-content">
            <div className="config-section" style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                <p style={{ color: '#6b7280', margin: 0 }}>
                  Rules run from lowest priority number to highest. The first enabled rule whose trigger phrases match the recent conversation will be used.
                </p>
                <button className="btn btn-primary" onClick={openCreateChatRoutingRule} disabled={configSaving}>
                  ➕ Add Rule
                </button>
              </div>

              {chatRoutingRules.length === 0 ? (
                <div className="empty-state" style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <p>No chat routing rules configured yet.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '16px' }}>
                  {[...chatRoutingRules]
                    .sort((a: any, b: any) => a.priority - b.priority)
                    .map((rule: any, index: number, orderedRules: any[]) => {
                      const typeDefinition = getChatRoutingRuleTypeDefinition(rule.actionType);
                      return (
                        <div
                          key={rule.id}
                          style={{
                            border: '1px solid #d1d5db',
                            borderRadius: '10px',
                            padding: '20px',
                            backgroundColor: rule.enabled ? 'white' : '#f9fafb'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                <h4 style={{ margin: 0 }}>{rule.name}</h4>
                                <span style={{ padding: '4px 8px', borderRadius: '999px', fontSize: '12px', backgroundColor: '#e0f2fe', color: '#075985' }}>
                                  {typeDefinition?.label || rule.actionType}
                                </span>
                                <span style={{ padding: '4px 8px', borderRadius: '999px', fontSize: '12px', backgroundColor: '#fef3c7', color: '#92400e' }}>
                                  Priority {rule.priority}
                                </span>
                                <span style={{ padding: '4px 8px', borderRadius: '999px', fontSize: '12px', backgroundColor: rule.enabled ? '#d1fae5' : '#f3f4f6', color: rule.enabled ? '#065f46' : '#6b7280' }}>
                                  {rule.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                              </div>

                              <div style={{ color: '#4b5563', fontSize: '14px', marginBottom: '10px' }}>
                                <strong>Reply:</strong> {rule.responseMessage}
                              </div>
                              {typeDefinition?.requiresManagementGroupName && (
                                <div style={{ color: '#4b5563', fontSize: '14px', marginBottom: '10px' }}>
                                  <strong>Group Chat:</strong> {rule.managementGroupName || 'Not set'}
                                </div>
                              )}
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {(rule.triggerPhrases || []).map((trigger: string) => (
                                  <span key={`${rule.id}-${trigger}`} style={{ padding: '4px 8px', borderRadius: '999px', backgroundColor: '#f3f4f6', fontSize: '12px', color: '#374151' }}>
                                    {trigger}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '130px' }}>
                              <button className="btn btn-secondary" onClick={() => openEditChatRoutingRule(rule)} disabled={configSaving}>
                                Edit
                              </button>
                              <button className="btn btn-secondary" onClick={() => handleMoveChatRoutingRule(rule.id, 'up')} disabled={configSaving || index === 0}>
                                Move Up
                              </button>
                              <button className="btn btn-secondary" onClick={() => handleMoveChatRoutingRule(rule.id, 'down')} disabled={configSaving || index === orderedRules.length - 1}>
                                Move Down
                              </button>
                              <button
                                className="btn btn-danger"
                                style={{ backgroundColor: '#ef4444', color: 'white', border: 'none' }}
                                onClick={() => handleDeleteChatRoutingRule(rule.id)}
                                disabled={configSaving}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )}

        {showChatRoutingRuleModal && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '90%',
              maxWidth: '720px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ marginTop: 0 }}>{editingChatRoutingRuleId ? 'Edit Chat Routing Rule' : 'Add Chat Routing Rule'}</h3>

              <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Rule Name *
                  </label>
                  <input
                    type="text"
                    value={chatRoutingRuleForm.name}
                    onChange={(e) => setChatRoutingRuleForm({ ...chatRoutingRuleForm, name: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Action *
                  </label>
                  <select
                    value={chatRoutingRuleForm.actionType}
                    onChange={(e) => {
                      const actionType = e.target.value as 'management_message' | 'job_search_redirect';
                      const template = createEmptyChatRoutingRule(actionType);
                      const selectedTypeDefinition = getChatRoutingRuleTypeDefinition(actionType);
                      setChatRoutingRuleForm({
                        ...chatRoutingRuleForm,
                        actionType,
                        responseMessage: chatRoutingRuleForm.responseMessage || template.responseMessage,
                        managementGroupName: selectedTypeDefinition?.requiresManagementGroupName
                          ? (chatRoutingRuleForm.managementGroupName || template.managementGroupName)
                          : ''
                      });
                    }}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                  >
                    {getAvailableChatRoutingRuleTypes().map((ruleType) => (
                      <option key={ruleType.actionType} value={ruleType.actionType}>
                        {ruleType.label}
                      </option>
                    ))}
                  </select>
                  {getChatRoutingRuleTypeDefinition(chatRoutingRuleForm.actionType)?.description && (
                    <small style={{ color: '#6b7280' }}>
                      {getChatRoutingRuleTypeDefinition(chatRoutingRuleForm.actionType)?.description}
                    </small>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Priority *
                  </label>
                  <input
                    type="number"
                    value={chatRoutingRuleForm.priority}
                    onChange={(e) => setChatRoutingRuleForm({ ...chatRoutingRuleForm, priority: parseInt(e.target.value, 10) || 10 })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                  <small style={{ color: '#6b7280' }}>Lower numbers run first.</small>
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '32px' }}>
                    <input
                      type="checkbox"
                      checked={chatRoutingRuleForm.enabled}
                      onChange={(e) => setChatRoutingRuleForm({ ...chatRoutingRuleForm, enabled: e.target.checked })}
                    />
                    <span>Rule enabled</span>
                  </label>
                </div>
              </div>

              {getChatRoutingRuleTypeDefinition(chatRoutingRuleForm.actionType)?.requiresManagementGroupName && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Internal Group Chat Name *
                  </label>
                  <input
                    type="text"
                    value={chatRoutingRuleForm.managementGroupName}
                    onChange={(e) => setChatRoutingRuleForm({ ...chatRoutingRuleForm, managementGroupName: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Customer Reply *
                </label>
                <textarea
                  value={chatRoutingRuleForm.responseMessage}
                  onChange={(e) => setChatRoutingRuleForm({ ...chatRoutingRuleForm, responseMessage: e.target.value })}
                  rows={4}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  Trigger Phrases *
                </label>
                <textarea
                  value={chatRoutingRuleForm.triggerText}
                  onChange={(e) => setChatRoutingRuleForm({ ...chatRoutingRuleForm, triggerText: e.target.value })}
                  rows={8}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', fontFamily: 'monospace', boxSizing: 'border-box' }}
                />
                <small style={{ color: '#6b7280' }}>One phrase per line.</small>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button className="btn btn-secondary" onClick={closeChatRoutingRuleModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveChatRoutingRule} disabled={configSaving}>
                  {editingChatRoutingRuleId ? 'Save Rule' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCuratedQALibrary = () => {
    // Filter Q&A pairs based on search and category
    const filteredQAPairs = curatedQAPairs.filter(qa => {
      const matchesSearch = !curatedQASearchQuery ||
        qa.question.toLowerCase().includes(curatedQASearchQuery.toLowerCase()) ||
        qa.answer.toLowerCase().includes(curatedQASearchQuery.toLowerCase());
      const matchesCategory = !curatedQACategoryFilter || qa.category === curatedQACategoryFilter;
      return matchesSearch && matchesCategory;
    });

    return (
      <div className="curated-qa-section">
        <div className="section-header">
          <h2>❓ Curated Q&A Library</h2>
          <p>Manually curated, high-quality Q&A pairs for common immigration questions</p>
        </div>

        {curatedQALoading ? (
          <div className="loading">Loading Curated Q&A Library...</div>
        ) : (
          <div className="curated-qa-content">
            {/* Statistics Section */}
            <div className="statistics-section">
              <h3>📊 Library Statistics</h3>
              {curatedQAStats ? (
                <div className="stats-grid">
                  <div className="stat-card">
                    <h4>{curatedQAStats.total_qas || 0}</h4>
                    <p>Total Q&A Pairs</p>
                  </div>
                  <div className="stat-card">
                    <h4>{curatedQAStats.active_qas || 0}</h4>
                    <p>Active Pairs</p>
                  </div>
                  <div className="stat-card">
                    <h4>{curatedQAStats.total_usage || 0}</h4>
                    <p>Total Usage</p>
                  </div>
                  <div className="stat-card">
                    <h4>{curatedQAStats.avg_rating?.toFixed(1) || 'N/A'}</h4>
                    <p>Average Rating</p>
                  </div>
                </div>
              ) : (
                <div className="stats-grid">
                  <div className="stat-card">
                    <h4>{curatedQAPairs.length}</h4>
                    <p>Total Q&A Pairs</p>
                  </div>
                  <div className="stat-card">
                    <h4>{curatedQAPairs.filter(qa => qa.is_active).length}</h4>
                    <p>Active Pairs</p>
                  </div>
                  <div className="stat-card">
                    <h4>{curatedQAPairs.reduce((sum, qa) => sum + (qa.usage_count || 0), 0)}</h4>
                    <p>Total Usage</p>
                  </div>
                  <div className="stat-card">
                    <h4>{curatedQACategories.length}</h4>
                    <p>Categories</p>
                  </div>
                </div>
              )}
            </div>

            {/* Category Breakdown */}
            {curatedQAStats?.by_category && curatedQAStats.by_category.length > 0 && (
              <div className="category-breakdown-section">
                <h3>📁 Q&A by Category</h3>
                <div className="category-list">
                  {curatedQAStats.by_category.map((cat: any) => (
                    <div key={cat.category} className="category-item">
                      <span className="category-name">{cat.category}</span>
                      <span className="category-count">{cat.count} Q&As</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search Test Section */}
            <div className="search-test-section">
              <h3>🔍 Test Semantic Search</h3>
              <p>Test the AI-powered semantic search to find relevant Q&A pairs</p>
              <div className="search-controls">
                <input
                  type="text"
                  placeholder="Ask a question (e.g., 'What is the minimum salary for EP?')"
                  className="search-input"
                  value={curatedQASearchQuery}
                  onChange={(e) => setCuratedQASearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchCuratedQA()}
                />
                <select
                  className="filter-select"
                  value={curatedQACategoryFilter}
                  onChange={(e) => setCuratedQACategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {curatedQACategories.map(cat => (
                    <option key={cat.category_key} value={cat.category_key}>
                      {cat.icon_emoji} {cat.display_name}
                    </option>
                  ))}
                </select>
                <button className="btn btn-primary" onClick={handleSearchCuratedQA}>
                  🔍 Search
                </button>
              </div>

              {curatedQASearchResults && (
                <div className="search-results-section">
                  <h4>Results ({curatedQASearchResults.total_results} found, {curatedQASearchResults.search_time_ms}ms)</h4>
                  <div className="search-method-badge">
                    {curatedQASearchResults.search_type === 'hybrid' && '🧩 Hybrid Search (Semantic + Keyword)'}
                    {curatedQASearchResults.search_type === 'semantic' && '🧠 Semantic Search'}
                    {curatedQASearchResults.search_type === 'keyword' && '🔤 Keyword Search'}
                  </div>
                  {curatedQASearchResults.results.map((qa: any) => (
                    <div key={qa.id} className="qa-result-card">
                      <div className="qa-header">
                        <span className="qa-category">{qa.category}</span>
                        <span className="qa-similarity">
                          {(qa.similarity_score * 100).toFixed(0)}% match
                        </span>
                      </div>
                      <div className="qa-question">{qa.question}</div>
                      <div className="qa-answer">{qa.answer}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Q&A Pairs List */}
            <div className="qa-pairs-section">
              <div className="qa-pairs-header">
                <h3>📋 Q&A Pairs ({filteredQAPairs.length})</h3>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setEditingQAPair(null);
                    setCuratedQAForm({
                      question: '',
                      answer: '',
                      category: 'General',
                      subcategory: '',
                      tags: [],
                      keywords: [],
                      priority: 50,
                      quality_score: 0.8,
                      language: 'english'
                    });
                    setShowCreateQAModal(true);
                  }}
                >
                  ➕ Add Q&A Pair
                </button>
              </div>

              <div className="qa-pairs-controls">
                <input
                  type="text"
                  placeholder="🔍 Filter Q&A pairs..."
                  className="search-input"
                  value={curatedQASearchQuery}
                  onChange={(e) => setCuratedQASearchQuery(e.target.value)}
                />
                <select
                  className="filter-select"
                  value={curatedQACategoryFilter}
                  onChange={(e) => setCuratedQACategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {curatedQACategories.map(cat => (
                    <option key={cat.category_key} value={cat.category_key}>
                      {cat.icon_emoji} {cat.display_name}
                    </option>
                  ))}
                </select>
              </div>

              {filteredQAPairs.length === 0 ? (
                <div className="empty-state">
                  <p>No Q&A pairs found. Create your first Q&A pair to get started.</p>
                </div>
              ) : (
                <div className="qa-pairs-grid">
                  {filteredQAPairs.map((qa) => (
                    <div key={qa.id} className={`qa-pair-card ${!qa.is_active ? 'inactive' : ''}`}>
                      <div className="qa-pair-header">
                        <div className="qa-category-badge">{qa.category}</div>
                        <div className="qa-priority" title={`Priority: ${qa.priority}`}>
                          {'★'.repeat(Math.ceil((qa.priority || 50) / 20))}
                        </div>
                      </div>

                      <div className="qa-pair-content">
                        <h4>Q: {qa.question}</h4>
                        <p>A: {qa.answer}</p>

                        {qa.tags && qa.tags.length > 0 && (
                          <div className="qa-tags">
                            {qa.tags.map((tag: string) => (
                              <span key={tag} className="tag">{tag}</span>
                            ))}
                          </div>
                        )}

                        {qa.keywords && qa.keywords.length > 0 && (
                          <div className="qa-keywords">
                            {qa.keywords.map((kw: string) => (
                              <span key={kw} className="keyword">{kw}</span>
                            ))}
                          </div>
                        )}

                        <div className="qa-meta">
                          <span>👁 {qa.usage_count || 0} uses</span>
                          <span>⭐ {qa.success_rate?.toFixed(1) || 'N/A'} success</span>
                          {qa.quality_score && (
                            <span>🎯 {(qa.quality_score * 100).toFixed(0)}% quality</span>
                          )}
                        </div>
                      </div>

                      <div className="qa-pair-actions">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleEditQAPair(qa)}
                        >
                          ✏️ Edit
                        </button>
                        {qa.is_active ? (
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleDeactivateQAPair(qa.id, qa.question)}
                          >
                            📴 Deactivate
                          </button>
                        ) : (
                          <span className="status-badge inactive">Inactive</span>
                        )}
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteQAPair(qa.id, qa.question)}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create/Edit Q&A Modal */}
        {showCreateQAModal && (
          <div className="modal-overlay" onClick={() => setShowCreateQAModal(false)}>
            <div className="modal-content qa-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingQAPair ? '✏️ Edit Q&A Pair' : '➕ Create Q&A Pair'}</h3>
                <button className="close-btn" onClick={() => setShowCreateQAModal(false)}>❌</button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label>Question *</label>
                  <textarea
                    value={curatedQAForm.question}
                    onChange={(e) => setCuratedQAForm({ ...curatedQAForm, question: e.target.value })}
                    placeholder="Enter the question..."
                    rows={2}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Answer *</label>
                  <textarea
                    value={curatedQAForm.answer}
                    onChange={(e) => setCuratedQAForm({ ...curatedQAForm, answer: e.target.value })}
                    placeholder="Enter the answer..."
                    rows={4}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      value={curatedQAForm.category}
                      onChange={(e) => setCuratedQAForm({ ...curatedQAForm, category: e.target.value })}
                      required
                    >
                      {curatedQACategories.map(cat => (
                        <option key={cat.category_key} value={cat.category_key}>
                          {cat.icon_emoji} {cat.display_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Subcategory</label>
                    <input
                      type="text"
                      value={curatedQAForm.subcategory}
                      onChange={(e) => setCuratedQAForm({ ...curatedQAForm, subcategory: e.target.value })}
                      placeholder="e.g., Eligibility, Documents"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Priority (1-100)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={curatedQAForm.priority}
                      onChange={(e) => setCuratedQAForm({ ...curatedQAForm, priority: parseInt(e.target.value) || 50 })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Quality Score (0-1)</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={curatedQAForm.quality_score}
                      onChange={(e) => setCuratedQAForm({ ...curatedQAForm, quality_score: parseFloat(e.target.value) || 0.8 })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Language</label>
                    <select
                      value={curatedQAForm.language}
                      onChange={(e) => setCuratedQAForm({ ...curatedQAForm, language: e.target.value })}
                    >
                      <option value="english">English</option>
                      <option value="chinese">中文</option>
                      <option value="malay">Bahasa Melayu</option>
                      <option value="tamil">தமிழ்</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <div className="tag-input-group">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      placeholder="Add tag and press Enter"
                    />
                    <button className="btn btn-sm" onClick={handleAddTag}>Add</button>
                  </div>
                  <div className="tag-list">
                    {curatedQAForm.tags.map(tag => (
                      <span key={tag} className="tag">
                        {tag}
                        <button onClick={() => handleRemoveTag(tag)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Keywords</label>
                  <div className="keyword-input-group">
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                      placeholder="Add keyword and press Enter"
                    />
                    <button className="btn btn-sm" onClick={handleAddKeyword}>Add</button>
                  </div>
                  <div className="keyword-list">
                    {curatedQAForm.keywords.map(keyword => (
                      <span key={keyword} className="keyword">
                        {keyword}
                        <button onClick={() => handleRemoveKeyword(keyword)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCreateQAModal(false)}
                  disabled={curatedQASaving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={editingQAPair ? handleUpdateQAPair : handleCreateQAPair}
                  disabled={curatedQASaving}
                >
                  {curatedQASaving ? 'Saving...' : (editingQAPair ? 'Update Q&A Pair' : 'Create Q&A Pair')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderUrlManagement = () => {
    return (
      <div style={urlManagementStyles} className="url-management-section">
        <div className="section-header">
          <h2>🌐 URL Management & RAG Sources</h2>
          <p>Manage scraped URLs for the knowledge base, view last updated times, and manually trigger updates</p>
        </div>

        <div className="url-management-content">
          {/* Stats Overview */}
          <div className="stats-overview">
            <h3>📊 Scraping Statistics</h3>
            {scrapingStats ? (
              <div className="stats-grid">
                <div className="stat-card">
                  <span className="stat-number">{scrapingStats.totalUrls}</span>
                  <span className="stat-label">Total URLs</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">{scrapingStats.activeUrls}</span>
                  <span className="stat-label">Active URLs</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">{scrapingStats.failedUrls}</span>
                  <span className="stat-label">Failed URLs</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">{scrapingStats.changesDetected}</span>
                  <span className="stat-label">Recent Changes</span>
                </div>
              </div>
            ) : (
              <div className="loading-state">Loading statistics...</div>
            )}
          </div>

          {/* Automatic Update Schedule */}
          <div className="schedule-settings-section">
            <h3>📅 Automatic Update Schedule</h3>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Configure when to automatically check ICA/MOM websites for content updates
            </p>

            <div className="schedule-form" style={{ maxWidth: '800px' }}>
              {/* Frequency Selector */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label><strong>Update Frequency:</strong></label>
                <div className="radio-group" style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="frequency"
                      value="daily"
                      checked={scheduleSettings.frequency === 'daily'}
                      onChange={(e) => setScheduleSettings({ ...scheduleSettings, frequency: e.target.value })}
                      style={{ marginRight: '6px' }}
                    />
                    Daily
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="frequency"
                      value="weekly"
                      checked={scheduleSettings.frequency === 'weekly'}
                      onChange={(e) => setScheduleSettings({ ...scheduleSettings, frequency: e.target.value })}
                      style={{ marginRight: '6px' }}
                    />
                    Weekly
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="frequency"
                      value="monthly"
                      checked={scheduleSettings.frequency === 'monthly'}
                      onChange={(e) => setScheduleSettings({ ...scheduleSettings, frequency: e.target.value })}
                      style={{ marginRight: '6px' }}
                    />
                    Monthly
                  </label>
                </div>
              </div>

              {/* Day of Week (for weekly) */}
              {scheduleSettings.frequency === 'weekly' && (
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label><strong>Day of Week:</strong></label>
                  <select
                    value={scheduleSettings.day_of_week}
                    onChange={(e) => setScheduleSettings({ ...scheduleSettings, day_of_week: parseInt(e.target.value) })}
                    style={{ marginTop: '8px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '200px' }}
                  >
                    <option value={0}>Sunday</option>
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                  </select>
                </div>
              )}

              {/* Day of Month (for monthly) */}
              {scheduleSettings.frequency === 'monthly' && (
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label><strong>Day of Month:</strong></label>
                  <select
                    value={scheduleSettings.day_of_month}
                    onChange={(e) => setScheduleSettings({ ...scheduleSettings, day_of_month: parseInt(e.target.value) })}
                    style={{ marginTop: '8px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '200px' }}
                  >
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Time of Day */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label><strong>Time of Day:</strong></label>
                <input
                  type="time"
                  value={scheduleSettings.time_of_day}
                  onChange={(e) => setScheduleSettings({ ...scheduleSettings, time_of_day: e.target.value })}
                  style={{ marginTop: '8px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '150px' }}
                />
                <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>Singapore time (UTC+8)</small>
              </div>

              {/* Notification Email */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label><strong>Notification Email:</strong></label>
                <input
                  type="email"
                  value={scheduleSettings.notification_email}
                  onChange={(e) => setScheduleSettings({ ...scheduleSettings, notification_email: e.target.value })}
                  placeholder="your-email@example.com"
                  style={{ marginTop: '8px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '100%', maxWidth: '400px' }}
                />
              </div>

              {/* Notification Preferences */}
              <div className="form-group" style={{ marginBottom: '20px', maxWidth: '400px' }}>
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={scheduleSettings.notify_on_changes}
                    onChange={(e) => setScheduleSettings({ ...scheduleSettings, notify_on_changes: e.target.checked })}
                    style={{ marginRight: '8px', flexShrink: 0, width: '16px', height: '16px' }}
                  />
                  <span style={{ flex: 1 }}>Email me when content changes are detected</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={scheduleSettings.notify_on_failures}
                    onChange={(e) => setScheduleSettings({ ...scheduleSettings, notify_on_failures: e.target.checked })}
                    style={{ marginRight: '8px', flexShrink: 0, width: '16px', height: '16px' }}
                  />
                  <span style={{ flex: 1 }}>Email me if scraping fails</span>
                </label>
              </div>

              {/* Next Run Info */}
              {scheduleSettings.next_run_at && (
                <div className="next-run-info" style={{
                  padding: '12px',
                  background: '#f0f9ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '6px',
                  marginBottom: '20px'
                }}>
                  <strong>Next Scheduled Run:</strong>{' '}
                  {new Date(scheduleSettings.next_run_at).toLocaleString('en-SG', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  })}
                </div>
              )}

              {/* Save Button */}
              <div className="form-actions">
                <button
                  onClick={saveScheduleSettings}
                  className="btn btn-primary"
                  style={{ padding: '10px 24px', fontSize: '14px' }}
                >
                  💾 Save Schedule
                </button>
              </div>
            </div>
          </div>

          {/* Manual Update Controls */}
          <div className="manual-controls">
            <h3>🔄 Manual Updates</h3>
            <div className="control-buttons">
              <button
                onClick={handleManualScrapingUpdate}
                disabled={manualScrapingRunning}
                className="btn btn-primary"
                title="Triggers the ICA-MOM scraping agent to check all URLs for content changes, update the knowledge base, and refresh RAG vectors"
              >
                {manualScrapingRunning ? '⏳ Updating Sources...' : '🚀 Manual Update All RAG Sources'}
              </button>
              <button
                onClick={handleUrlOnlyScraping}
                disabled={urlOnlyScrapingRunning}
                className="btn btn-primary"
                title="Scrapes all URLs for content only, without processing into RAG chunks"
              >
                {urlOnlyScrapingRunning ? '⏳ Scraping URLs...' : '🔍 Scrape URLs Only'}
              </button>
              <button
                onClick={loadUrlsData}
                disabled={urlsLoading}
                className="btn btn-secondary"
                title="Refreshes this page's data by reloading the URL list and statistics from the database"
              >
                {urlsLoading ? '⏳ Refreshing...' : '🔄 Refresh URL List'}
              </button>
            </div>
            <div className="button-explanations">
              <div className="button-explanation">
                <strong>🚀 Manual Update All RAG Sources:</strong>
                <p>Runs the ICA-MOM scraping agent immediately. This will:</p>
                <ul>
                  <li>• Scrape all 25+ ICA/MOM URLs for the latest content</li>
                  <li>• Detect changes using SHA-256 content fingerprinting</li>
                  <li>• Update knowledge base documents with new/changed content</li>
                  <li>• Generate new embeddings for changed content using Gemini API</li>
                  <li>• Update the RAG (Retrieval-Augmented Generation) system for the lead qualifying agent</li>
                </ul>
                <small><em>Note: This process may take several minutes to complete.</em></small>
              </div>
              <div className="button-explanation">
                <strong>🔍 Scrape URLs Only:</strong>
                <p>Scrapes content from all URLs without processing into RAG chunks. This will:</p>
                <ul>
                  <li>• Download raw HTML content from all 25+ ICA/MOM URLs</li>
                  <li>• Convert HTML to clean markdown format</li>
                  <li>• Store content in database without creating embeddings</li>
                  <li>• Allow you to view and analyze raw content before processing</li>
                  <li>• Faster than full RAG update - just content fetching</li>
                </ul>
                <small><em>Use this to get fresh content quickly, then process chunks separately.</em></small>
              </div>
              <div className="button-explanation">
                <strong>🔄 Refresh URL List:</strong>
                <p>Simply reloads the current page data without triggering any scraping. This will:</p>
                <ul>
                  <li>• Refresh the URL list and statistics from the database</li>
                  <li>• Update last scrape times and status indicators</li>
                  <li>• Show the latest scraping results and success/failure counts</li>
                </ul>
                <small><em>Use this to see updated information after a manual update completes.</em></small>
              </div>
            </div>
          </div>

          {/* Add New URL */}
          <div className="add-url-section">
            <h3>➕ Add New URL</h3>

            {/* Priority Explanation */}
            <div style={{
              padding: '12px 16px',
              marginBottom: '16px',
              background: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '6px',
              fontSize: '0.85rem',
              lineHeight: '1.5'
            }}>
              <strong style={{ color: '#0369a1', display: 'block', marginBottom: '6px' }}>
                🎯 How Priority Works During Scheduled Scraping:
              </strong>
              <ul style={{ margin: '6px 0', paddingLeft: '20px', color: '#0c4a6e' }}>
                <li><strong>High Priority (1):</strong> Scraped FIRST - use for frequently changing pages (news, announcements)</li>
                <li><strong>Medium Priority (2):</strong> Scraped second - use for moderately updated pages</li>
                <li><strong>Low Priority (3):</strong> Scraped LAST - use for rarely changing pages (general info, FAQs)</li>
              </ul>
              <div style={{ marginTop: '8px', padding: '8px', background: 'white', borderRadius: '4px', fontSize: '0.8rem' }}>
                <strong>Scraping Process:</strong> URLs are processed in <strong>batches of 3</strong> (parallel within batch, sequential between batches).
                Within same priority level, older URLs (not scraped recently) are prioritized first.
              </div>
            </div>

            <div className="add-url-form">
              <div className="form-row">
                <input
                  type="url"
                  value={newUrl.url}
                  onChange={(e) => setNewUrl({ ...newUrl, url: e.target.value })}
                  placeholder="https://www.ica.gov.sg/..."
                  className="url-input"
                />
                <input
                  type="text"
                  value={newUrl.title}
                  onChange={(e) => setNewUrl({ ...newUrl, title: e.target.value })}
                  placeholder="Title (optional)"
                  className="title-input"
                />
              </div>
              <div className="form-row">
                <select
                  value={newUrl.category}
                  onChange={(e) => setNewUrl({ ...newUrl, category: e.target.value })}
                  className="category-select"
                >
                  <option value="citizenship">Citizenship</option>
                  <option value="permanent_residence">Permanent Residence</option>
                  <option value="work_permits">Work Permits</option>
                  <option value="family_immigration">Family Immigration</option>
                  <option value="business_immigration">Business Immigration</option>
                  <option value="employment_authorization">Employment Authorization</option>
                  <option value="visitor_passes">Visitor Passes</option>
                </select>
                <select
                  value={newUrl.source}
                  onChange={(e) => setNewUrl({ ...newUrl, source: e.target.value })}
                  className="source-select"
                >
                  <option value="ica">ICA</option>
                  <option value="mom">MOM</option>
                  <option value="edb">EDB</option>
                  <option value="custom">Custom</option>
                </select>
                <select
                  value={newUrl.priority}
                  onChange={(e) => setNewUrl({ ...newUrl, priority: parseInt(e.target.value) })}
                  className="priority-select"
                  title="Priority determines scraping order: 1=First (frequently changing), 2=Second (moderate updates), 3=Last (rarely changing)"
                >
                  <option value={1}>High Priority (1)</option>
                  <option value={2}>Medium Priority (2)</option>
                  <option value={3}>Low Priority (3)</option>
                </select>
              </div>
              <div className="form-row">
                <input
                  type="text"
                  value={newUrl.description}
                  onChange={(e) => setNewUrl({ ...newUrl, description: e.target.value })}
                  placeholder="Description (optional)"
                  className="description-input"
                />
                <button
                  onClick={handleAddUrl}
                  disabled={!newUrl.url.trim() || urlsLoading}
                  className="btn btn-success"
                >
                  ➕ Add URL
                </button>
              </div>
            </div>
          </div>

          {/* URL List */}
          <div className="urls-list-section">
            <h3>📋 Scraped URLs ({scrapedUrls.length})</h3>
            {urlsLoading ? (
              <div className="loading-state">Loading URLs...</div>
            ) : scrapedUrls.length > 0 ? (
              <div className="urls-table">
                <div className="table-header">
                  <span className="col-url">URL</span>
                  <span className="col-category">Category</span>
                  <span className="col-status">Status</span>
                  <span className="col-last-updated">Last Updated</span>
                  <span className="col-stats" title="Scraping statistics: ✅ = successful scrapes, ❌ = failed attempts, 🎯 = priority level">Stats</span>
                  <span className="col-actions">Actions</span>
                </div>
                {scrapedUrls.map((url) => (
                  <div key={url.id} className="table-row">
                    <div className="col-url">
                      <a href={url.url} target="_blank" rel="noopener noreferrer" className="url-link">
                        {url.title || url.url}
                      </a>
                      <small className="url-source">{url.source.toUpperCase()}</small>
                    </div>
                    <div className="col-category">
                      <span className="category-badge">{url.category.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="col-status">
                      <span
                        className={`status-badge status-${url.status || 'pending'}`}
                        title={
                          url.status === 'active' ? 'URL is actively being scraped' :
                            url.status === 'failed' ? 'URL scraping has failed' :
                              'URL is pending/paused - not yet scraped or temporarily disabled'
                        }
                      >
                        {url.status === 'active' ? '✅' : url.status === 'failed' ? '❌' : '⏸️'} {url.status || 'pending'}
                      </span>
                    </div>
                    <div className="col-last-updated">
                      <div className="date-info">
                        <strong>Last Success:</strong> {formatDateTime(url.last_successful_scrape)}
                      </div>
                      {url.last_attempt && (
                        <div className="date-info">
                          <strong>Last Attempt:</strong> {formatDateTime(url.last_attempt)}
                        </div>
                      )}
                    </div>
                    <div className="col-stats">
                      <div className="stats-mini">
                        <span title="Successful scrapes" className="stat-item">
                          ✅ {url.scrape_count || 0}
                        </span>
                        <span title="Failed scrape attempts" className="stat-item">
                          ❌ {url.failure_count || 0}
                        </span>
                        <span title={`Priority level: ${url.priority} (1=High, 2=Medium, 3=Low)`} className="stat-item">
                          🎯 P{url.priority}
                        </span>
                      </div>
                    </div>
                    <div className="col-actions">
                      <div className="action-buttons">
                        <button
                          onClick={() => handleViewContent(url.id)}
                          className="btn btn-info btn-small"
                          title="View raw scraped content and processed chunks"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => handleProcessChunks(url.id)}
                          className="btn btn-success btn-small"
                          title="Process content into chunks using Gemini model"
                        >
                          ⚙️
                        </button>
                        <button
                          onClick={() => handleDeleteUrl(url.id)}
                          className="btn btn-danger btn-small"
                          title="Delete URL"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">No URLs found. Add some URLs to get started.</div>
            )}
          </div>
        </div>

        {/* Content Viewing Modal */}
        {showContentModal && selectedUrlData && (
          <div className="content-modal-overlay" onClick={() => setShowContentModal(false)}>
            <div className="content-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📄 Content Analysis: {selectedUrlData.url}</h2>
                <button
                  className="modal-close-btn"
                  onClick={() => setShowContentModal(false)}
                >
                  ✕
                </button>
              </div>

              <div className="modal-content">
                <div className="content-metadata">
                  <div className="metadata-item">
                    <strong>Category:</strong> {selectedUrlData.category}
                  </div>
                  <div className="metadata-item">
                    <strong>Source:</strong> {selectedUrlData.source.toUpperCase()}
                  </div>
                  <div className="metadata-item">
                    <strong>Last Scraped:</strong> {formatDateTime(selectedUrlData.last_scraped)}
                  </div>
                  <div className="metadata-item">
                    <strong>Chunks:</strong> {selectedUrlData.chunks_count}
                  </div>
                </div>

                <div className="content-tabs">
                  <div className="content-section">
                    <h3>🔍 Raw Scraped Content</h3>
                    <div className="content-display">
                      {selectedUrlData.raw_content ? (
                        <pre className="raw-content">{selectedUrlData.raw_content}</pre>
                      ) : (
                        <div className="no-content">No raw content available. Try scraping this URL first.</div>
                      )}
                    </div>
                  </div>

                  <div className="content-section">
                    <h3>📝 Markdown Content</h3>
                    <div className="content-display">
                      {selectedUrlData.markdown_content ? (
                        <pre className="markdown-content">{selectedUrlData.markdown_content}</pre>
                      ) : (
                        <div className="no-content">No markdown content available.</div>
                      )}
                    </div>
                  </div>

                  <div className="content-section">
                    <h3>🧩 Processed Chunks ({selectedUrlData.chunks_count})</h3>
                    <div className="chunks-container">
                      {selectedUrlData.processed_chunks && selectedUrlData.processed_chunks.length > 0 ? (
                        selectedUrlData.processed_chunks.map((chunk: any) => (
                          <div key={chunk.id} className="chunk-item">
                            <div className="chunk-header">
                              <span className="chunk-index">Chunk {chunk.chunk_index + 1}</span>
                              <span className="chunk-type">{chunk.chunk_type}</span>
                              <span className="chunk-tokens">{chunk.token_count} tokens</span>
                            </div>
                            <div className="chunk-content">
                              {chunk.content}
                            </div>
                            {chunk.metadata && (
                              <div className="chunk-metadata">
                                <small>{JSON.stringify(chunk.metadata, null, 2)}</small>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="no-content">
                          No processed chunks available.
                          <button
                            onClick={() => handleProcessChunks(selectedUrlData.url_id)}
                            className="btn btn-primary btn-small"
                            style={{ marginLeft: '10px' }}
                          >
                            ⚙️ Process Chunks Now
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Guardrails state
  const [guardrails, setGuardrails] = useState<any>(null);
  const [flows, setFlows] = useState<any[]>([]);
  const [editingFlow, setEditingFlow] = useState<any>(null);
  const [showFlowModal, setShowFlowModal] = useState(false);
  const [guardrailsLoading, setGuardrailsLoading] = useState(false);

  // Load guardrails and flows when tab is active
  useEffect(() => {
    if (activeTab === 'guardrails') {
      loadGuardrailsData();
    }
  }, [activeTab]);

  const loadGuardrailsData = async () => {
    setGuardrailsLoading(true);
    try {
      const tenantId = authTenantId;
      if (!tenantId) {
        console.error('[Guardrails] No tenant in session; skipping load');
        return;
      }
      const [guardrailsResult, flowsResult] = await Promise.all([
        orchestratorApi.getTenantGuardrails(tenantId),
        orchestratorApi.getTenantFlows(tenantId)
      ]);

      if (guardrailsResult.ok) {
        setGuardrails(guardrailsResult.guardrails);
      }
      if (flowsResult.ok) {
        setFlows(flowsResult.flows || []);
      }
    } catch (error) {
      console.error('Failed to load guardrails data:', error);
    } finally {
      setGuardrailsLoading(false);
    }
  };

  const handleSaveGuardrails = async (updates: any) => {
    try {
      const tenantId = authTenantId;
      if (!tenantId) {
        alert('No tenant found in your session. Please log out and log back in.');
        return;
      }
      const result = await orchestratorApi.updateTenantGuardrails(tenantId, updates);
      if (result.ok) {
        setGuardrails(result.guardrails);
        alert('Guardrails updated successfully!');
      } else {
        alert('Failed to update guardrails: ' + result.error);
      }
    } catch (error) {
      console.error('Failed to save guardrails:', error);
      alert('Failed to update guardrails');
    }
  };

  const handleSaveFlow = async (flowData: any) => {
    try {
      const tenantId = authTenantId;
      if (!tenantId) {
        alert('No tenant found in your session. Please log out and log back in.');
        return;
      }
      let result;
      if (editingFlow) {
        result = await orchestratorApi.updateTenantFlow(tenantId, editingFlow.id, flowData);
      } else {
        result = await orchestratorApi.createTenantFlow(tenantId, flowData);
      }

      if (result.ok) {
        await loadGuardrailsData();
        setShowFlowModal(false);
        setEditingFlow(null);
        alert(editingFlow ? 'Flow updated!' : 'Flow created!');
      } else {
        alert('Failed to save flow: ' + result.error);
      }
    } catch (error) {
      console.error('Failed to save flow:', error);
      alert('Failed to save flow');
    }
  };

  const handleDeleteFlow = async (flowId: number) => {
    if (!confirm('Are you sure you want to delete this flow?')) return;

    try {
      const tenantId = authTenantId;
      if (!tenantId) {
        alert('No tenant found in your session. Please log out and log back in.');
        return;
      }
      const result = await orchestratorApi.deleteTenantFlow(tenantId, flowId);
      if (result.ok) {
        await loadGuardrailsData();
        alert('Flow deleted!');
      } else {
        alert('Failed to delete flow: ' + result.error);
      }
    } catch (error) {
      console.error('Failed to delete flow:', error);
      alert('Failed to delete flow');
    }
  };

  const renderGuardrailsTab = () => {
    if (guardrailsLoading) {
      return (
        <div className="guardrails-section" style={{ padding: '20px', textAlign: 'center' }}>
          <p>Loading guardrails configuration...</p>
        </div>
      );
    }

    return (
      <div className="guardrails-section">
        <h2>🛡️ Tenant Guardrails</h2>
        <p className="section-description">
          Configure safety settings to prevent AI hallucinations and inappropriate responses.
          These settings work across all business types - immigration, medical, fitness, etc.
        </p>

        {/* Entity Gate Settings */}
        <div className="feature-flags-section" style={{ marginBottom: '30px' }}>
          <h3>Entity Gate Settings</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            Block the assistant from introducing unconfirmed people/relationships/events.
          </p>

          <div className="feature-flag-card">
            <div className="flag-header">
              <h4>Block Invented Entities</h4>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={guardrails?.entity_gate_enabled ?? true}
                  onChange={(e) => handleSaveGuardrails({ entity_gate_enabled: e.target.checked })}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '13px', color: '#666' }}>
              Prevents the assistant from mentioning relationships (mother, father, spouse, etc.)
              unless explicitly confirmed by the user.
            </p>
          </div>

          <div style={{ marginTop: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Blocked Entity Terms (comma-separated):
            </label>
            <textarea
              value={(guardrails?.entity_gate_terms || []).join(', ')}
              onChange={(e) => {
                const terms = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                setGuardrails({ ...guardrails, entity_gate_terms: terms });
              }}
              onBlur={(e) => {
                const terms = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                handleSaveGuardrails({ entity_gate_terms: terms });
              }}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '13px'
              }}
              placeholder="mother, father, spouse, partner, son, daughter..."
            />
          </div>

          <div style={{ marginTop: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Custom Blocked Terms (business-specific):
            </label>
            <textarea
              value={(guardrails?.entity_gate_custom_terms || []).join(', ')}
              onChange={(e) => {
                const terms = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                setGuardrails({ ...guardrails, entity_gate_custom_terms: terms });
              }}
              onBlur={(e) => {
                const terms = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                handleSaveGuardrails({ entity_gate_custom_terms: terms });
              }}
              style={{
                width: '100%',
                minHeight: '60px',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '13px'
              }}
              placeholder="diagnosis, prescription, case number..."
            />
          </div>
        </div>

        {/* Assumption Blocking */}
        <div className="feature-flags-section" style={{ marginBottom: '30px' }}>
          <h3>Assumption Phrase Blocking</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            Block phrases that indicate the assistant is making assumptions.
          </p>

          <div style={{ marginTop: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Blocked Phrases (comma-separated):
            </label>
            <textarea
              value={(guardrails?.no_assumption_phrases || []).join(', ')}
              onChange={(e) => {
                const phrases = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                setGuardrails({ ...guardrails, no_assumption_phrases: phrases });
              }}
              onBlur={(e) => {
                const phrases = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                handleSaveGuardrails({ no_assumption_phrases: phrases });
              }}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '13px'
              }}
              placeholder="i understand you're, i see your, it sounds like..."
            />
          </div>
        </div>

        {/* Verifier Pass */}
        <div className="feature-flags-section" style={{ marginBottom: '30px' }}>
          <h3>Verifier Pass</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            Second-pass check for assumptive paraphrases that slip through the Entity Gate.
          </p>

          <div className="feature-flag-card">
            <div className="flag-header">
              <h4>Enable Verifier Pass</h4>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={guardrails?.verifier_pass_enabled ?? true}
                  onChange={(e) => handleSaveGuardrails({ verifier_pass_enabled: e.target.checked })}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <p style={{ fontSize: '13px', color: '#666' }}>
              Catches patterns like "I understand you're helping your mother..." even if
              relationship terms slip through the Entity Gate.
            </p>
          </div>
        </div>

        {/* Flow Configurations */}
        <div className="feature-flags-section">
          <h3>Flow Configurations</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            Intent-to-mode mappings that trigger specific behaviors (intake, QA, booking, etc.)
          </p>

          <button
            onClick={() => {
              setEditingFlow(null);
              setShowFlowModal(true);
            }}
            className="btn btn-primary"
            style={{ marginBottom: '15px' }}
          >
            ➕ Add New Flow
          </button>

          <div className="chats-table-wrapper">
            <table className="chats-table">
              <thead>
                <tr>
                  <th>Flow Name</th>
                  <th>Mode</th>
                  <th>Priority</th>
                  <th>Enabled</th>
                  <th>Intent Patterns</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {flows.map((flow) => (
                  <tr key={flow.id}>
                    <td>{flow.flow_name}</td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: flow.mode === 'intake_mode' ? '#fef3c7' : flow.mode === 'qa_mode' ? '#dbeafe' : '#f3e8ff',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {flow.mode}
                      </span>
                    </td>
                    <td>{flow.priority}</td>
                    <td>
                      <label className="toggle-switch" style={{ transform: 'scale(0.7)' }}>
                        <input
                          type="checkbox"
                          checked={flow.enabled}
                          onChange={async (e) => {
                            await handleSaveFlow({ enabled: e.target.checked });
                          }}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </td>
                    <td>
                      <div style={{ fontSize: '12px' }}>
                        {flow.intent_patterns?.map((p: string, i: number) => (
                          <span key={i} style={{
                            display: 'inline-block',
                            backgroundColor: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            margin: '2px'
                          }}>
                            {p}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => {
                          setEditingFlow(flow);
                          setShowFlowModal(true);
                        }}
                        className="btn-icon btn-edit"
                        title="Edit flow"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteFlow(flow.id)}
                        className="btn-icon btn-delete"
                        title="Delete flow"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Flow Modal */}
        {showFlowModal && (
          <FlowModal
            flow={editingFlow}
            onSave={handleSaveFlow}
            onClose={() => {
              setShowFlowModal(false);
              setEditingFlow(null);
            }}
          />
        )}
      </div>
    );
  };

  // Flow Modal Component
  const FlowModal = ({ flow, onSave, onClose }: { flow: any; onSave: (data: any) => void; onClose: () => void }) => {
    const [formData, setFormData] = useState({
      flow_name: flow?.flow_name || '',
      intent_patterns: flow?.intent_patterns || [],
      mode: flow?.mode || 'intake_mode',
      required_fields: flow?.required_fields || {},
      first_question_template: flow?.first_question_template || '',
      question_order: flow?.question_order || [],
      rag_doc_type: flow?.rag_doc_type || 'intake_playbook',
      allow_answering: flow?.allow_answering ?? false,
      priority: flow?.priority || 0,
      enabled: flow?.enabled ?? true
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
          <div className="modal-header">
            <h3>{flow ? 'Edit Flow' : 'Create New Flow'}</h3>
            <button onClick={onClose} className="modal-close">×</button>
          </div>
          <form onSubmit={handleSubmit}>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Flow Name *
                </label>
                <input
                  type="text"
                  value={formData.flow_name}
                  onChange={(e) => setFormData({ ...formData, flow_name: e.target.value })}
                  required
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  placeholder="e.g., denial_of_entry_intake"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Intent Patterns * (comma-separated)
                </label>
                <textarea
                  value={formData.intent_patterns.join(', ')}
                  onChange={(e) => setFormData({ ...formData, intent_patterns: e.target.value.split(',').map(t => t.trim()).filter(t => t) })}
                  required
                  style={{ width: '100%', minHeight: '80px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  placeholder="denied entry, refused entry, appeal, rejection"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Mode *
                </label>
                <select
                  value={formData.mode}
                  onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                  required
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                >
                  <option value="intake_mode">Intake Mode (ask questions)</option>
                  <option value="qa_mode">QA Mode (answer questions)</option>
                  <option value="booking_mode">Booking Mode (schedule appointments)</option>
                  <option value="escalation_mode">Escalation Mode (human handoff)</option>
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  Priority (higher = checked first)
                </label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  First Question Template (for intake mode)
                </label>
                <textarea
                  value={formData.first_question_template}
                  onChange={(e) => setFormData({ ...formData, first_question_template: e.target.value })}
                  style={{ width: '100%', minHeight: '60px', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  placeholder="Sure, I can help. Were you denied entry yourself, or are you assisting someone else?"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                  RAG Doc Type
                </label>
                <select
                  value={formData.rag_doc_type}
                  onChange={(e) => setFormData({ ...formData, rag_doc_type: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                >
                  <option value="intake_playbook">Intake Playbook (questions only)</option>
                  <option value="faq">FAQ (answers allowed)</option>
                  <option value="policy">Policy Document (answers allowed)</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    style={{ marginRight: '5px' }}
                  />
                  Enabled
                </label>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #e5e7eb' }}>
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {flow ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Load service configs when service-config tab is active
  useEffect(() => {
    if (activeTab === 'service-config') {
      loadServiceConfigs();
    }
  }, [activeTab]);

  const loadServiceConfigs = async () => {
    setLoadingServices(true);
    try {
      const result = await orchestratorApi.getServiceConfigs();
      if (result.ok && result.configs) {
        setServiceConfigs(result.configs);
        // Initialize editing state with current required_keys
        const initialEditState: { [key: string]: string[] } = {};
        result.configs.forEach(config => {
          initialEditState[config.service_code] = config.required_keys || [];
        });
        setEditingServiceKeys(initialEditState);
      } else {
        alert(`Failed to load service configs: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`Error loading service configs: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleSaveRequiredKeys = async (serviceCode: string) => {
    const requiredKeys = editingServiceKeys[serviceCode] || [];
    try {
      const result = await orchestratorApi.updateServiceConfig(serviceCode, {
        required_keys: requiredKeys
      });

      if (result.ok) {
        alert(`✅ Required keys updated for ${serviceCode}`);
        await loadServiceConfigs(); // Reload to get fresh data
      } else {
        alert(`❌ Failed to update required keys: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`❌ Error updating required keys: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleAddService = async () => {
    const serviceName = prompt('Enter service name (e.g., "Fitness Consultation", "Medical Checkup", "Legal Consultation"):');
    if (!serviceName || !serviceName.trim()) return;

    const serviceCode = prompt('Enter service code (lowercase, no spaces, e.g., "fitness_consult", "medical_checkup"):');
    if (!serviceCode || !serviceCode.trim()) return;

    const keywords = prompt('Enter keywords (comma-separated) to identify this service:\n\nExample: consultation, checkup, assessment', '');
    if (keywords === null) return;

    const description = prompt('Enter optional description:', '') || '';

    try {
      const result = await orchestratorApi.createServiceConfig({
        service_name: serviceName.trim(),
        service_code: serviceCode.trim().toLowerCase().replace(/\s+/g, '_'),
        keywords: keywords.split(',').map(k => k.trim()).filter(k => k),
        description: description.trim(),
        enabled: true
      });

      if (result.ok) {
        alert(`✅ Service "${serviceName}" created successfully!`);
        await loadServiceConfigs();
      } else {
        alert(`❌ Failed to create service: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`❌ Error creating service: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteService = async (serviceCode: string) => {
    if (!confirm(`⚠️ Are you sure you want to delete this service?\n\nService code: ${serviceCode}\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const result = await orchestratorApi.deleteServiceConfig(serviceCode);

      if (result.ok) {
        alert(`✅ Service deleted successfully!`);
        await loadServiceConfigs();
      } else {
        alert(`❌ Failed to delete service: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`❌ Error deleting service: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const renderServiceConfigTab = () => {
    return (
      <div style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ marginTop: 0 }}>✅ Service Completion Rules</h2>
            <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0', lineHeight: '1.6' }}>
              Define services and their required fields for your business. Works for any industry - immigration, medical, fitness, consulting, etc.
            </p>
          </div>
          <button
            onClick={() => handleAddService()}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
              whiteSpace: 'nowrap'
            }}
          >
            ✨ Add new product/service
          </button>
        </div>

        {loadingServices ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
            <div>Loading service configurations...</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {serviceConfigs.map(service => (
              <div
                key={service.service_code}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  background: service.enabled ? '#fff' : '#f9fafb'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                      {service.service_name}
                      {!service.enabled && <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#9ca3af' }}>(Disabled)</span>}
                    </h3>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
                      Code: <code style={{ background: '#f3f4f6', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>{service.service_code}</code>
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleSaveRequiredKeys(service.service_code)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#0891b2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}
                    >
                      💾 Save
                    </button>
                    <button
                      onClick={() => handleDeleteService(service.service_code)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}
                      title="Delete this service"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                    Required Fields ({(editingServiceKeys[service.service_code] || []).length})
                  </label>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.75rem 0' }}>
                    Enter field names (comma-separated) that must be collected before booking:
                  </p>
                  <textarea
                    value={(editingServiceKeys[service.service_code] || []).join(', ')}
                    onChange={(e) => {
                      const keys = e.target.value.split(',').map(k => k.trim()).filter(k => k);
                      setEditingServiceKeys(prev => ({ ...prev, [service.service_code]: keys }));
                    }}
                    placeholder="e.g., customer_name, email, phone_number, service_type, preferred_date"
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontFamily: 'monospace',
                      resize: 'vertical'
                    }}
                  />
                  <p style={{ fontSize: '0.7rem', color: '#9ca3af', margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>
                    💡 Examples: applicant_name, date_of_birth, nationality, service_type, urgency, budget, contact_email
                  </p>
                </div>

                {service.description && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px' }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#075985' }}>
                      💡 {service.description}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: '#fef3c7',
          border: '1px solid #fde68a',
          borderRadius: '8px'
        }}>
          <h4 style={{ marginTop: 0, fontSize: '0.95rem', color: '#92400e' }}>ℹ️ How It Works</h4>
          <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: '0.85rem', color: '#92400e', lineHeight: '1.7' }}>
            <li>Selected fields are checked against extracted facts in the <code style={{ background: '#fde68a', padding: '0.1rem 0.3rem', borderRadius: '3px' }}>client_memory_facts</code> table</li>
            <li>Booking is only allowed when ALL required fields have been collected for the active service</li>
            <li>Fields are extracted using the memory extraction configuration in Agent Factory</li>
            <li>Use structured LLM extraction type for reliable fact capture from natural language</li>
          </ul>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-state">Loading admin panel...</div>
      </div>
    );
  }

  const renderEntitySelector = () => {
    // Get booking timezone from agent settings
    const calendarSettings = agentSettings.find(s => s.agent_name === 'calendar_webhook_agent');
    const bookingTimezone = calendarSettings?.calendar_timezone || 'Asia/Singapore';

    return (
      <div className="admin-entity-selector">
        <div className="entity-selector-left">
          <label htmlFor="entity-select" className="entity-select-label">
            🏢 WhatsApp Business Session:
          </label>
          <select
            id="entity-select"
            value={selectedEntityId || ''}
            onChange={(e) => setSelectedEntityId(e.target.value)}
            className="entity-select-dropdown"
          >
            {entities.length === 0 && (
              <option value="">No sessions configured</option>
            )}
            {entities.map((entity) => {
              const statusIcon =
                entity.status === "connected" ? "🟢" :
                  entity.status === "disconnected" ? "🔴" :
                    entity.status === "authenticating" ? "🟡" :
                      entity.status === "needs_qr" ? "🟠" : "⚪";

              return (
                <option key={entity.id} value={entity.id}>
                  {statusIcon} {entity.name} ({entity.phone})
                </option>
              );
            })}
          </select>
        </div>

        <div className="entity-selector-center">
          <div className="timezone-display" title="Booking timezone for calendar appointments">
            🌏 <strong>{bookingTimezone}</strong>
          </div>
        </div>

        <div className="entity-selector-right">
          {selectedEntity && (
            <>
              <button
                className="btn-icon btn-edit"
                onClick={() => handleEditEntity(selectedEntity)}
                title="Edit session"
              >
                ✏️
              </button>
              <button
                className="btn-icon btn-delete"
                onClick={() => handleDeleteEntity(selectedEntity)}
                title="Delete session"
              >
                🗑️
              </button>
            </>
          )}
          <button
            className="btn btn-primary add-session-btn"
            onClick={() => setShowAddEntityModal(true)}
            title="Add new WhatsApp Business session"
          >
            ➕ Add Session
          </button>
        </div>
      </div>
    );
  };

  const renderMainContent = () => {
    // System-level tabs that don't require a selected entity
    const systemLevelTabs = ["archived-sessions", "settings", "monitoring", "llm-config", "ai-models", "create-agent", "tenant-topics", "entity-management"];

    if (!selectedEntity && !systemLevelTabs.includes(activeTab)) {
      return (
        <div className="admin-main-content">
          {renderEntitySelector()}
          <div className="no-entity-selected">
            <h2>Select a WhatsApp Business Session</h2>
            <p>
              Choose a session from the dropdown above to manage its settings and
              connection.
            </p>
            <p style={{ marginTop: '20px', color: '#666' }}>
              Or access system-wide settings:
            </p>
            <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setActiveTab("create-agent")}
                style={{ padding: '10px 20px' }}
              >
                ➕ Create Chat Agent
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setActiveTab("archived-sessions")}
                style={{ padding: '10px 20px' }}
              >
                📦 Archived Sessions
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setActiveTab("settings")}
                style={{ padding: '10px 20px' }}
              >
                ⚙️ System Settings
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setActiveTab("monitoring")}
                style={{ padding: '10px 20px' }}
              >
                📊 Monitoring
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="admin-main-content">
        {renderEntitySelector()}

        <div className="admin-layout-with-sidebar">
          <div className="admin-tabs-sidebar">
            <button
              className={`tab-button ${activeTab === "whatsapp" ? "active" : ""}`}
              onClick={() => setActiveTab("whatsapp")}
            >
              WhatsApp Connection
            </button>

            {/* AI Agents Collapsible Section */}
            <div className="nav-section">
              <button
                className={`tab-button section-header ${["ai-models", "agents", "entity-assignments", "system-prompt", "knowledge", "urls", "rag", "learning", "curated-qa"].includes(activeTab) ? "active-section" : ""
                  }`}
                onClick={() => setExpandedSections({
                  ...expandedSections,
                  aiChatAgent: !expandedSections.aiChatAgent
                })}
              >
                <span className="section-title">🤖 AI Agents</span>
                <span className="expand-icon">{expandedSections.aiChatAgent ? "▼" : "▶"}</span>
              </button>

              {expandedSections.aiChatAgent && (
                <div className="nav-subsection">
                  <button
                    className={`tab-button nested ${activeTab === "ai-models" ? "active" : ""}`}
                    onClick={() => setActiveTab("ai-models")}
                  >
                    ⚙️ AI Model Selection
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "create-agent" ? "active" : ""}`}
                    onClick={() => setActiveTab("create-agent")}
                  >
                    ➕ Create Chat Agent
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "agents" ? "active" : ""}`}
                    onClick={() => setActiveTab("agents")}
                  >
                    🤖 Agent Controls
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "entity-assignments" ? "active" : ""}`}
                    onClick={() => setActiveTab("entity-assignments")}
                  >
                    🔗 Entity Assignments
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "system-prompt" ? "active" : ""}`}
                    onClick={() => setActiveTab("system-prompt")}
                  >
                    📝 System Prompt
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "knowledge" ? "active" : ""}`}
                    onClick={() => setActiveTab("knowledge")}
                  >
                    📚 Knowledge Base
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "urls" ? "active" : ""}`}
                    onClick={() => setActiveTab("urls")}
                  >
                    🌐 URL Management
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "rag" ? "active" : ""}`}
                    onClick={() => setActiveTab("rag")}
                  >
                    🧩 RAG Management
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "learning" ? "active" : ""}`}
                    onClick={() => setActiveTab("learning")}
                  >
                    🧠 Continuous Learning
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "curated-qa" ? "active" : ""}`}
                    onClick={() => setActiveTab("curated-qa")}
                  >
                    ❓ Curated QA Library
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "tenant-topics" ? "active" : ""}`}
                    onClick={() => setActiveTab("tenant-topics")}
                  >
                    🏷️ Tenant Topics
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "tenant-config" ? "active" : ""}`}
                    onClick={() => setActiveTab("tenant-config")}
                  >
                    ⚙️ Tenant Config
                  </button>
                  <button
                    className={`tab-button nested ${activeTab === "chat-routing-rules" ? "active" : ""}`}
                    onClick={() => setActiveTab("chat-routing-rules")}
                  >
                    🧭 Chat Routing Rules
                  </button>
                </div>
              )}
            </div>

            <button
              className={`tab-button ${activeTab === "tokens" ? "active" : ""}`}
              onClick={() => setActiveTab("tokens")}
            >
              Token Usage
            </button>
            <button
              className={`tab-button ${activeTab === "translation" ? "active" : ""}`}
              onClick={() => setActiveTab("translation")}
            >
              Translation
            </button>
            <button
              className={`tab-button ${activeTab === "llm-config" ? "active" : ""}`}
              onClick={() => setActiveTab("llm-config")}
            >
              AI Models
            </button>
            <button
              className={`tab-button ${activeTab === "monitoring" ? "active" : ""}`}
              onClick={() => setActiveTab("monitoring")}
            >
              Monitoring
            </button>
            <Link
              to="/analytics/response-times"
              className="tab-button external-link"
              style={{ textDecoration: 'none', display: 'block', textAlign: 'left' }}
            >
              📊 Response Times
            </Link>
            <button
              className={`tab-button ${activeTab === "settings" ? "active" : ""}`}
              onClick={() => setActiveTab("settings")}
            >
              ⚙️ System Settings
            </button>
            <button
              className={`tab-button ${activeTab === "service-config" ? "active" : ""}`}
              onClick={() => setActiveTab("service-config")}
            >
              ✅ Service Completion Rules
            </button>
            <button
              className={`tab-button ${activeTab === "archived-sessions" ? "active" : ""}`}
              onClick={() => setActiveTab("archived-sessions")}
            >
              📦 Archived Sessions
            </button>
            <button
              className={`tab-button ${activeTab === "entity-management" ? "active" : ""}`}
              onClick={() => setActiveTab("entity-management")}
            >
              🏢 Entity Management
            </button>
            <button
              className={`tab-button ${activeTab === "google-calendar" ? "active" : ""}`}
              onClick={() => setActiveTab("google-calendar")}
            >
              📅 Google Calendar
            </button>
            <button
              className={`tab-button ${activeTab === "zoho-crm" ? "active" : ""}`}
              onClick={() => setActiveTab("zoho-crm")}
            >
              🏢 CRM
            </button>
            <button
              className={`tab-button ${activeTab === "zoho-response-control" ? "active" : ""}`}
              onClick={() => setActiveTab("zoho-response-control")}
            >
              🎯 CRM Response Control
            </button>
            <button
              className={`tab-button ${activeTab === "memory" ? "active" : ""}`}
              onClick={() => setActiveTab("memory")}
            >
              🧠 Memory
            </button>
            <button
              className={`tab-button ${activeTab === "tools" ? "active" : ""}`}
              onClick={() => setActiveTab("tools")}
            >
              🔧 Tools
            </button>
            <button
              className={`tab-button ${activeTab === "handoff" ? "active" : ""}`}
              onClick={() => setActiveTab("handoff")}
            >
              👥 Human Handoff
            </button>
            <button
              className={`tab-button ${activeTab === "management-messages" ? "active" : ""}`}
              onClick={() => setActiveTab("management-messages")}
            >
              📬 Management Messages
            </button>
            <button
              className={`tab-button ${activeTab === "curated-qa" ? "active" : ""}`}
              onClick={() => setActiveTab("curated-qa")}
            >
              📚 Curated QA
            </button>
            <button
              className={`tab-button ${activeTab === "testing" ? "active" : ""}`}
              onClick={() => setActiveTab("testing")}
            >
              ✅ Testing
            </button>
            <button
              className={`tab-button ${activeTab === "guardrails" ? "active" : ""}`}
              onClick={() => setActiveTab("guardrails")}
            >
              🛡️ Guardrails
            </button>
            <button
              className={`tab-button ${activeTab === "dead-letter" ? "active" : ""}`}
              onClick={() => setActiveTab("dead-letter")}
            >
              ⚠️ Dead Letter Queue
            </button>
          </div>

          <div className="admin-content">
            {activeTab === "agents" && (
              <>
                <div className="feature-flags-section">
                  <h2>Agent Controls</h2>
                  <p className="section-description">
                    Toggle agents on/off to control their behavior in real-time
                    for {selectedEntity?.name}.
                  </p>

                  <div className="feature-flags-grid">
                    {flags.map((flag) => (
                      <div key={flag.id} className="feature-flag-card">
                        <div className="flag-header">
                          <h3 className="flag-name">
                            {getFlagDisplayName(flag.name)}
                          </h3>
                          <div className="flag-toggle">
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={flag.enabled}
                                onChange={() =>
                                  handleToggleFlag(flag.name, flag.enabled)
                                }
                                disabled={updating === flag.name}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                            {updating === flag.name && (
                              <span className="updating-text">Updating...</span>
                            )}
                          </div>
                        </div>

                        <p className="flag-description">
                          {getFlagDescription(flag.name)}
                        </p>

                        <div className="flag-status">
                          <span
                            className={`status-badge ${flag.enabled ? "enabled" : "disabled"}`}
                          >
                            {flag.enabled ? "Enabled" : "Disabled"}
                          </span>
                          <span className="last-updated">
                            Updated: {new Date(flag.updated_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Lead Agent Configuration */}
                {leadConfig && (
                  <div className="lead-config-section">
                    <h2>Lead Agent Configuration</h2>
                    <p className="section-description">
                      Configure the target WhatsApp chat for lead notifications
                      for {selectedEntity?.name}.
                    </p>

                    <div className="lead-config-card">
                      <div className="config-header">
                        <h3>Target Chat Configuration</h3>
                        <div className="config-status">
                          <span
                            className={`status-badge ${leadConfig.enabled ? "enabled" : "disabled"}`}
                          >
                            {leadConfig.enabled
                              ? "Agent Enabled"
                              : "Agent Disabled"}
                          </span>
                        </div>
                      </div>

                      <div className="config-form">
                        <label htmlFor="targetChat">🎯 Lead Webhook Notification Target:</label>
                        <div className="input-group">
                          <input
                            id="targetChat"
                            type="text"
                            value={leadConfig.config.targetChat}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setLeadConfig((prev) =>
                                prev
                                  ? {
                                    ...prev,
                                    config: {
                                      ...prev.config,
                                      targetChat: newValue,
                                    },
                                  }
                                  : null,
                              );
                            }}
                            placeholder="Group name or phone number (e.g., E&H Immigration Internal or 6596734436)"
                            className="target-chat-input"
                            disabled={updatingLeadConfig}
                          />
                          <button
                            onClick={() =>
                              handleUpdateLeadConfig(leadConfig.config.targetChat)
                            }
                            disabled={
                              updatingLeadConfig ||
                              !leadConfig.config.targetChat.trim()
                            }
                            className="update-config-button"
                          >
                            {updatingLeadConfig ? "Updating..." : "Update"}
                          </button>
                        </div>
                        <small className="input-help">
                          📌 <strong>Purpose:</strong> Receives notifications when external lead webhooks (Google Sheets, CRM integrations) send lead data.<br />
                          📱 <strong>Format:</strong> Group chat name (e.g., "E&H Immigration Internal") or phone number (e.g., "6596734436")<br />
                          🔍 <strong>Note:</strong> This is separate from email agent notifications configured above.
                        </small>
                      </div>

                      {!leadConfig.enabled && (
                        <div className="config-warning">
                          ⚠️ Note: At least one email agent (Typeform or Webform) should be enabled
                          above for lead notifications to work.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Agent Settings Configuration */}
                <div className="agent-settings-section">
                  <h2>Email Agent Settings</h2>
                  <p className="section-description">
                    Configure email agent behavior including client messaging and notification targets
                    for {selectedEntity?.name}.
                  </p>

                  {loadingAgentSettings ? (
                    <div className="loading-state">Loading agent settings...</div>
                  ) : agentSettings.length > 0 ? (
                    <div className="agent-settings-grid">
                      {agentSettings
                        .filter(setting => !setting.agent_name.includes('calendar_') && !setting.agent_name.includes('unanswered_') && setting.agent_name !== 'qualifier_agent')
                        .map((setting) => (
                          <AgentSettingCard
                            key={setting.id}
                            setting={setting}
                            updating={updatingAgentSettings === setting.agent_name}
                            onUpdate={updateAgentSettings}
                          />
                        ))}
                    </div>
                  ) : (
                    <div className="no-settings">
                      No email agent settings found. Settings will be created automatically when agents run.
                    </div>
                  )}
                </div>

                {/* Lead Qualifier Agent Settings */}
                <div className="agent-settings-section">
                  <h2>🤖 Lead Qualifier Agent</h2>
                  <p className="section-description">
                    Configure the AI-powered lead qualification agent with FSM logic, message templates, and scheduling
                    for {selectedEntity?.name}.
                  </p>

                  {/* Qualifier Agent Settings */}
                  {loadingAgentSettings ? (
                    <div className="loading-state">Loading qualifier agent settings...</div>
                  ) : (
                    <div className="agent-settings-grid">
                      {agentSettings
                        .filter(setting => setting.agent_name === 'qualifier_agent')
                        .map((setting) => (
                          <AgentSettingCard
                            key={setting.id}
                            setting={setting}
                            updating={updatingAgentSettings === setting.agent_name}
                            onUpdate={updateAgentSettings}
                          />
                        ))}
                    </div>
                  )}

                  {/* Weekly Schedule */}
                  {loadingQualifierSchedule ? (
                    <div className="loading-state">Loading qualifier agent schedule...</div>
                  ) : (
                    <div className="qualifier-settings-card" style={{ marginTop: '20px' }}>
                      <QualifierScheduleCard
                        schedule={qualifierSchedule}
                        updating={updatingQualifierSchedule}
                        onUpdate={updateQualifierSchedule}
                      />
                    </div>
                  )}
                </div>

                {/* Calendar Agent Settings */}
                <div className="agent-settings-section">
                  <h2>📅 Calendar Reminder Settings</h2>
                  <p className="section-description">
                    Configure when the calendar agent checks for Sales meetings and sends WhatsApp reminders
                    for {selectedEntity?.name}.
                  </p>

                  {loadingCalendarSchedule ? (
                    <div className="loading-state">Loading calendar settings...</div>
                  ) : (
                    <div className="calendar-settings-card">
                      <CalendarSettingsCard
                        schedule={calendarSchedule}
                        updating={updatingCalendarSchedule}
                        onUpdate={updateCalendarSchedule}
                        onTest={testCalendarCheck}
                      />
                    </div>
                  )}
                </div>

                {/* Unanswered Messages Agent Settings */}
                <div className="agent-settings-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h2>🚨 Unanswered Messages Monitor</h2>
                    {selectedEntity && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '14px', color: '#666' }}>Current Entity:</span>
                        <select
                          value={selectedEntityId || ''}
                          onChange={(e) => setSelectedEntityId(e.target.value)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #cbd5e0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            background: 'white',
                            minWidth: '250px'
                          }}
                        >
                          {entities.map((entity) => {
                            const statusIcon =
                              entity.status === "connected" ? "🟢" :
                                entity.status === "disconnected" ? "🔴" :
                                  entity.status === "authenticating" ? "🟡" :
                                    entity.status === "needs_qr" ? "🟠" : "⚪";
                            return (
                              <option key={entity.id} value={entity.id}>
                                {statusIcon} {entity.name}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}
                  </div>
                  <p className="section-description">
                    Configure when the agent scans for unanswered client messages and sends reminder alerts
                    for the selected entity.
                  </p>

                  {loadingUnansweredSchedule ? (
                    <div className="loading-state">Loading unanswered messages settings...</div>
                  ) : (
                    <div className="unanswered-settings-card">
                      <UnansweredMessagesSettingsCard
                        schedule={unansweredSchedule}
                        updating={updatingUnansweredSchedule}
                        onUpdate={updateUnansweredSchedule}
                        onTest={testUnansweredCheck}
                        onToggleEnabled={toggleUnansweredEnabled}
                      />
                    </div>
                  )}

                  {/* Unanswered Scan Archive */}
                  {selectedEntityId && (
                    <div style={{ marginTop: '2rem' }}>
                      <h3 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>📋 Unanswered Scan Archive</h3>
                      <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        History of all Unanswered scans run from the chat board, including which chats received AI responses.
                      </p>
                      <UnansweredArchive entityId={selectedEntityId} />
                    </div>
                  )}
                </div>

                <div className="broadcast-section">
                  <h2>📢 Broadcast Messaging</h2>
                  <p className="section-description">
                    Send messages to multiple contacts sequentially from CSV uploads or CRM leads.
                  </p>

                  <div className="broadcast-controls">
                    <div className="broadcast-header">
                      <button
                        onClick={() => setShowCreateCampaign(true)}
                        className="btn btn-primary"
                        disabled={loadingBroadcast}
                      >
                        ➕ Create New Campaign
                      </button>
                    </div>

                    {showCreateCampaign && (
                      <div className="create-campaign-form">
                        <h3>Create New Broadcast Campaign</h3>
                        <div className="form-group">
                          <label>Campaign Name:</label>
                          <input
                            type="text"
                            value={campaignForm.name}
                            onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                            placeholder="e.g., October Promotion"
                          />
                        </div>
                        <div className="form-group">
                          <label>Message:</label>
                          <textarea
                            value={campaignForm.message}
                            onChange={(e) => setCampaignForm({ ...campaignForm, message: e.target.value })}
                            placeholder="Enter your broadcast message..."
                            rows={4}
                          />
                        </div>
                        <div className="form-group">
                          <label>Data Source:</label>
                          <div className="source-options">
                            <label>
                              <input
                                type="radio"
                                checked={campaignForm.source_type === 'csv'}
                                onChange={() => setCampaignForm({ ...campaignForm, source_type: 'csv' })}
                              />
                              📄 CSV Upload
                            </label>
                            <label>
                              <input
                                type="radio"
                                checked={campaignForm.source_type === 'zoho'}
                                onChange={() => setCampaignForm({ ...campaignForm, source_type: 'zoho' })}
                              />
                              🔗 CRM
                            </label>
                          </div>
                        </div>
                        <div className="form-actions">
                          <button
                            onClick={createCampaign}
                            disabled={!campaignForm.name || !campaignForm.message || loadingBroadcast}
                            className="btn btn-primary"
                          >
                            Create Campaign
                          </button>
                          <button
                            onClick={() => setShowCreateCampaign(false)}
                            className="btn btn-secondary"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {selectedCampaign && (
                      <div className="campaign-details">
                        <h3>📋 Campaign: {selectedCampaign.name}</h3>
                        <p><strong>Status:</strong> <span className={`status-${selectedCampaign.status}`}>{selectedCampaign.status.toUpperCase()}</span></p>

                        {/* Progress Bar */}
                        {selectedCampaign.status === 'running' && campaignProgress[selectedCampaign.id] && (
                          <div className="progress-section">
                            <h4>📊 Progress</h4>
                            <div className="progress-bar-container">
                              <div className="progress-bar">
                                <div
                                  className="progress-fill"
                                  style={{ width: `${campaignProgress[selectedCampaign.id].percentage}%` }}
                                >
                                  {campaignProgress[selectedCampaign.id].percentage}%
                                </div>
                              </div>
                            </div>
                            <div className="progress-stats">
                              <span className="stat-item">
                                📤 Processed: {campaignProgress[selectedCampaign.id].processed} / {campaignProgress[selectedCampaign.id].total}
                              </span>
                              <span className="stat-item">
                                ✅ Success: {campaignProgress[selectedCampaign.id].success}
                              </span>
                              <span className="stat-item">
                                ❌ Failed: {campaignProgress[selectedCampaign.id].failed}
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="campaign-stats">
                          <p><strong>Recipients:</strong> {selectedCampaign.total_recipients}</p>
                          <p><strong>Processed:</strong> {selectedCampaign.processed_count}</p>
                          <p><strong>Success:</strong> {selectedCampaign.success_count}</p>
                          <p><strong>Failed:</strong> {selectedCampaign.failed_count}</p>
                        </div>

                        {selectedCampaign.source_type === 'csv' && (
                          <div className="csv-upload">
                            <h4>📤 Upload Recipients (CSV)</h4>
                            <input
                              type="file"
                              accept=".csv"
                              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                            />
                            <button
                              onClick={uploadCsv}
                              disabled={!csvFile || loadingBroadcast}
                              className="btn btn-primary"
                            >
                              Upload CSV
                            </button>
                            <small>CSV format: name,phone (with header row)</small>
                          </div>
                        )}

                        <div className="campaign-actions">
                          {selectedCampaign.status === 'created' && (
                            <button
                              onClick={() => startCampaign(selectedCampaign.id)}
                              disabled={selectedCampaign.total_recipients === 0}
                              className="btn btn-success"
                            >
                              ▶️ Start Campaign
                            </button>
                          )}
                          {selectedCampaign.status === 'running' && (
                            <button
                              onClick={() => pauseCampaign(selectedCampaign.id)}
                              className="btn btn-warning"
                            >
                              ⏸️ Pause Campaign
                            </button>
                          )}
                          {selectedCampaign.status === 'paused' && (
                            <button
                              onClick={() => resumeCampaign(selectedCampaign.id)}
                              className="btn btn-success"
                            >
                              ▶️ Resume Campaign
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="campaigns-list">
                      <h3>📊 All Campaigns</h3>
                      {loadingBroadcast ? (
                        <div className="loading-state">Loading campaigns...</div>
                      ) : broadcastCampaigns.length > 0 ? (
                        <div className="campaigns-grid">
                          {broadcastCampaigns.map((campaign) => (
                            <div
                              key={campaign.id}
                              className={`campaign-card ${selectedCampaign?.id === campaign.id ? 'selected' : ''}`}
                              onClick={() => setSelectedCampaign(campaign)}
                            >
                              <h4>{campaign.name}</h4>
                              <p>Status: <span className={`status-${campaign.status}`}>{campaign.status}</span></p>
                              <p>Recipients: {campaign.total_recipients}</p>
                              <p>Success: {campaign.success_count}</p>

                              {/* Mini progress bar for running campaigns */}
                              {campaign.status === 'running' && campaign.total_recipients > 0 && (
                                <div className="mini-progress">
                                  <div className="mini-progress-bar">
                                    <div
                                      className="mini-progress-fill"
                                      style={{
                                        width: `${Math.round((campaign.processed_count / campaign.total_recipients) * 100)}%`
                                      }}
                                    ></div>
                                  </div>
                                  <small>{Math.round((campaign.processed_count / campaign.total_recipients) * 100)}% complete</small>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-campaigns">
                          No campaigns created yet. Create your first campaign to get started!
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="admin-info">
                  <h3>ℹ️ How it works</h3>
                  <ul>
                    <li>
                      <strong>Agent 1 (Watcher):</strong> Processes all inbound
                      events and forwards them to the UI with enrichment
                    </li>
                    <li>
                      <strong>Lead Qualifier Agent:</strong> Analyzes message
                      content and automatically qualifies leads
                    </li>
                    <li>
                      <strong>Lead Webhook Agent:</strong> Receives lead data from
                      Google Sheets monitor and sends formatted notifications to
                      configured WhatsApp chat
                    </li>
                    <li>
                      <strong>Webform Email Agent:</strong> Monitors Squarespace form submissions every minute, extracts contact details, and sends personalized WhatsApp messages with business cards using AI-powered responses
                    </li>
                    <li>
                      <strong>Typeform Email Agent:</strong> Monitors Typeform submissions every minute, extracts contact details, and sends personalized WhatsApp messages with business cards plus internal group notifications
                    </li>
                    <li>
                      <strong>Real-time updates:</strong> Changes take effect
                      immediately via WebSocket
                    </li>
                    <li>
                      <strong>Handover protection:</strong> Agent 2 is blocked
                      from sending when status = 'handover'
                    </li>
                  </ul>
                </div>
              </>
            )}

            {activeTab === "whatsapp" && renderWhatsAppConnection()}

            {activeTab === "ai-models" && <ModelPreferences />}

            {activeTab === "create-agent" && <AgentFactory tenantId={authTenantId ?? ""} />}
            {activeTab === "entity-assignments" && (
              <EntityAssignments
                tenantId={authTenantId ?? ""}
                selectedAdminEntityId={selectedEntityId}
              />
            )}
            {activeTab === "system-prompt" && <SystemPrompt />}
            {activeTab === "knowledge" && renderKnowledgeBase()}
            {activeTab === "learning" && renderLearningSystem()}
            {activeTab === "urls" && renderUrlManagement()}
            {activeTab === "rag" && <RAGManagement />}
            {activeTab === "curated-qa" && renderCuratedQALibrary()}
            {activeTab === "tenant-topics" && renderTenantTopics()}
            {activeTab === "tenant-config" && renderTenantConfig()}
            {activeTab === "chat-routing-rules" && renderChatRoutingRules()}
            {activeTab === "tokens" && <TokenUsage />}

            {activeTab === "translation" && (
              <div className="translation-settings-section">
                <h2>Translation Settings</h2>
                <p className="section-description">
                  Configure translation models and run backfill for untranslated messages
                </p>

                {loadingTranslationSettings ? (
                  <div className="loading">Loading translation settings...</div>
                ) : translationSettings ? (
                  <>
                    <div className="settings-card">
                      <h3>Model Configuration</h3>
                      <div className="settings-grid">
                        <div className="setting-row">
                          <label htmlFor="primary-model">Primary Translation Model</label>
                          <select
                            id="primary-model"
                            value={translationSettings.primary_model || 'minimax-m2-free'}
                            onChange={(e) => {
                              const newSettings = { ...translationSettings, primary_model: e.target.value };
                              setTranslationSettings(newSettings);
                            }}
                            disabled={updatingTranslationSettings}
                          >
                            {availableModels.map(model => (
                              <option key={model.value} value={model.value}>
                                {model.label} ({model.cost})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="setting-row">
                          <label htmlFor="backup-model">Backup Translation Model</label>
                          <select
                            id="backup-model"
                            value={translationSettings.backup_model || 'deepseek-v3-1'}
                            onChange={(e) => {
                              const newSettings = { ...translationSettings, backup_model: e.target.value };
                              setTranslationSettings(newSettings);
                            }}
                            disabled={updatingTranslationSettings}
                          >
                            {availableModels.map(model => (
                              <option key={model.value} value={model.value}>
                                {model.label} ({model.cost})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="setting-row">
                          <label htmlFor="auto-translate">
                            <input
                              type="checkbox"
                              id="auto-translate"
                              checked={translationSettings.auto_translate_enabled === 'true'}
                              onChange={(e) => {
                                const newSettings = {
                                  ...translationSettings,
                                  auto_translate_enabled: e.target.checked ? 'true' : 'false'
                                };
                                setTranslationSettings(newSettings);
                              }}
                              disabled={updatingTranslationSettings}
                            />
                            Automatically translate incoming messages
                          </label>
                        </div>

                        <div className="setting-row">
                          <label htmlFor="track-history">
                            <input
                              type="checkbox"
                              id="track-history"
                              checked={translationSettings.track_message_history === 'true'}
                              onChange={(e) => {
                                const newSettings = {
                                  ...translationSettings,
                                  track_message_history: e.target.checked ? 'true' : 'false'
                                };
                                setTranslationSettings(newSettings);
                              }}
                              disabled={updatingTranslationSettings}
                            />
                            Track message edit history and re-translate changes
                          </label>
                        </div>
                      </div>

                      <button
                        onClick={() => updateTranslationSettings(translationSettings)}
                        disabled={updatingTranslationSettings}
                        className="btn btn-primary"
                      >
                        {updatingTranslationSettings ? 'Updating...' : 'Save Translation Settings'}
                      </button>
                    </div>

                    <div className="settings-card">
                      <h3>Translation Backfill</h3>
                      <p>Run translation on all untranslated messages in the database.</p>

                      {backfillProgress && (
                        <div className="backfill-progress">
                          <div className="progress-stats">
                            <div className="stat">
                              <span className="stat-label">Translated:</span>
                              <span className="stat-value">{backfillProgress.translated}</span>
                            </div>
                            <div className="stat">
                              <span className="stat-label">Skipped:</span>
                              <span className="stat-value">{backfillProgress.skipped}</span>
                            </div>
                            <div className="stat">
                              <span className="stat-label">Failed:</span>
                              <span className="stat-value">{backfillProgress.failed}</span>
                            </div>
                            <div className="stat">
                              <span className="stat-label">Remaining:</span>
                              <span className="stat-value">{backfillProgress.remaining}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      <button
                        onClick={runTranslationBackfill}
                        disabled={runningBackfill}
                        className="btn btn-success"
                      >
                        {runningBackfill ? 'Running Backfill...' : 'Run Translation Backfill'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="no-data">No translation settings found</div>
                )}
              </div>
            )}

            {activeTab === "llm-config" && <AgentLLMConfig />}

            {activeTab === "monitoring" && <MonitoringDashboard />}

            {activeTab === "settings" && <SystemSettings />}

            {activeTab === "archived-sessions" && (
              <div className="archived-sessions-section">
                <h2>📦 Archived Sessions</h2>
                <p className="section-description">
                  View and restore archived WhatsApp sessions. All chat history and data is preserved.
                </p>

                <ArchivedSessions
                  onRestore={() => {
                    // Reload active entities after restore
                    loadEntities();
                  }}
                />
              </div>
            )}

            {activeTab === "entity-management" && (
              <EntityManagement
                onEntityUpdated={() => {
                  // Reload entities after any changes
                  loadEntities();
                }}
              />
            )}

            {activeTab === "google-calendar" && <GoogleCalendarAuth selectedEntityId={selectedEntityId} />}

            {activeTab === "zoho-crm" && <CRMSettings />}

            {activeTab === "zoho-response-control" && <ZohoResponseControl />}

            {activeTab === "memory" && <MemoryManagement />}

            {activeTab === "tools" && <ToolConfiguration selectedEntityId={selectedEntityId} />}

            {activeTab === "handoff" && <HumanHandoffDashboard />}

            {activeTab === "management-messages" && <ManagementMessagesDashboard selectedEntityId={selectedEntityId || undefined} />}

            {activeTab === "curated-qa" && <CuratedQA />}
          </div>
          {activeTab === "testing" && <Testing />}

          {activeTab === "guardrails" && renderGuardrailsTab()}

          {activeTab === "dead-letter" && <DeadLetterQueue />}

          {activeTab === "service-config" && renderServiceConfigTab()}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{qrComparisonStyles}</style>
      <style>{agentSettingsStyles}</style>
      <style>{knowledgeBaseStyles}</style>
      <style>{broadcastStyles}</style>
      <style>{learningSystemStyles}</style>
      <style>{urlManagementCSS}</style>
      <div className="admin-page">
        {renderMainContent()}

        <AddEntityModal
          isOpen={showAddEntityModal}
          onClose={() => {
            setShowAddEntityModal(false);
            setEditingEntity(null);
          }}
          onAdd={editingEntity ? handleUpdateEntity : handleAddEntity}
          editingEntity={editingEntity}
        />

        {/* Prompt Editor Modal */}
        {showPromptEditor && editingPrompt && (
          <div className="modal-overlay" onClick={() => setShowPromptEditor(false)}>
            <div className="prompt-editor-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>📝 Prompt Template Editor</h3>
                <button
                  className="close-btn"
                  onClick={() => setShowPromptEditor(false)}
                >
                  ❌
                </button>
              </div>

              <div className="prompt-info">
                <div className="prompt-meta-grid">
                  <div className="meta-item">
                    <label>Type:</label>
                    <span className="prompt-type-badge">{editingPrompt.type?.replace('_', ' ').toUpperCase()}</span>
                  </div>
                  <div className="meta-item">
                    <label>Name:</label>
                    <span>{editingPrompt.prompt_name}</span>
                  </div>
                  <div className="meta-item">
                    <label>Version:</label>
                    <span>v{editingPrompt.version_number}</span>
                  </div>
                  <div className="meta-item">
                    <label>Usage:</label>
                    <span>{editingPrompt.usage_count || 0} times</span>
                  </div>
                  <div className="meta-item">
                    <label>Success Rate:</label>
                    <span>{Math.round((editingPrompt.success_count / Math.max(editingPrompt.usage_count, 1)) * 100) || 0}%</span>
                  </div>
                  <div className="meta-item">
                    <label>Last Used:</label>
                    <span>{editingPrompt.last_used_at ? new Date(editingPrompt.last_used_at).toLocaleDateString() : 'Never'}</span>
                  </div>
                </div>
              </div>

              <div className="prompt-content-section">
                <label className="prompt-label">📋 Current Prompt Content:</label>
                <div className="prompt-content-display">
                  <pre className="prompt-text">{editingPrompt.prompt_content}</pre>
                </div>
              </div>

              <div className="prompt-editor-section">
                <label className="prompt-label">✏️ Edit Prompt (Create New Version):</label>
                <textarea
                  className="prompt-editor-textarea"
                  placeholder="Enter new prompt content to create a new version..."
                  rows={12}
                  value={promptContent}
                  onChange={(e) => setPromptContent(e.target.value)}
                />
                <div className="prompt-editor-controls">
                  <input
                    type="text"
                    placeholder="Description for this version (optional)"
                    className="version-description"
                    value={versionDescription}
                    onChange={(e) => setVersionDescription(e.target.value)}
                  />
                  <button
                    className="btn btn-primary save-prompt-btn"
                    onClick={saveNewPromptVersion}
                    disabled={!promptContent.trim()}
                  >
                    💾 Save New Version
                  </button>
                </div>
              </div>

              <div className="prompt-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPromptEditor(false)}
                >
                  📖 View Only
                </button>
                <button className="btn btn-success">
                  🔄 Load Previous Versions
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Unanswered Test Confirmation Modal */}
      {showUnansweredTestModal && (
        <div className="modal-overlay" onClick={() => setShowUnansweredTestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', margin: '100px auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>💬 Test Unanswered Check</h2>
              <button
                onClick={() => setShowUnansweredTestModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <p style={{ marginBottom: '1.5rem', color: '#6b7280' }}>
              Analyze recent conversations to find unanswered client messages that need responses.
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                How many chats to analyze?
              </label>
              <input
                type="number"
                min="1"
                max="500"
                value={maxChatsInput}
                onChange={(e) => setMaxChatsInput(Math.min(500, Math.max(1, parseInt(e.target.value) || 100)))}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  fontSize: '1rem'
                }}
              />
              <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                More chats = longer processing time (typically 10-20 seconds)
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowUnansweredTestModal(false)}
                className="btn btn-secondary"
                style={{
                  padding: '0.75rem 1.5rem'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => runUnansweredTest(maxChatsInput)}
                className="btn btn-primary"
                style={{
                  padding: '0.75rem 1.5rem'
                }}
              >
                Analyze
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unanswered Test Results Modal */}
      {unanswerTestLoading || unansweredTestResults !== null ? (
        <div className="modal-overlay" onClick={() => { if (!unanswerTestLoading) setUnansweredTestResults(null); }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>💬 Unanswered Messages Test Results</h2>
              <button
                onClick={() => setUnansweredTestResults(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                disabled={unanswerTestLoading}
              >
                ×
              </button>
            </div>

            {unanswerTestLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="spinner" style={{
                  display: 'inline-block',
                  width: '40px',
                  height: '40px',
                  border: '4px solid #e5e7eb',
                  borderTop: '4px solid #10b981',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '1rem'
                }}></div>
                <p style={{ color: '#6b7280' }}>Analyzing conversations with AI...</p>
                <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>This typically takes 10-20 seconds</p>
              </div>
            ) : unansweredTestResults !== null && unansweredTestResults.length > 0 ? (
              <div>
                <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                  Found {unansweredTestResults.length} chat{unansweredTestResults.length !== 1 ? 's' : ''} with unanswered questions
                </p>
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
                  {unansweredTestResults.map((chat: any, index: number) => (
                    <div key={index} style={{
                      padding: '1rem',
                      borderBottom: index < unansweredTestResults.length - 1 ? '1px solid #e5e7eb' : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                          {chat.chatName || chat.chatId.split('@')[0]}
                          {chat.phoneNumber && ` (${chat.phoneNumber})`}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                          {chat.questions?.join(', ') || 'Unanswered message'}
                        </div>
                      </div>
                      {chat.isGroup && (
                        <span style={{ fontSize: '0.75rem', background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px' }}>
                          Group
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p>✅ No unanswered messages found!</p>
                <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                  All messages have been responded to within the threshold period.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Model Settings Modal */}
      <ModelSettings
        isOpen={showModelSettings}
        onClose={() => setShowModelSettings(false)}
        tenantId={authTenantId ?? ""}
        userId={undefined}
      />
    </>
  );
};
